import React, { useMemo, useState, useEffect, useRef, useCallback, memo } from "react";
import { useTooltip } from "@nivo/tooltip";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	FormGroup,
	Typography,
	Slider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Radio,
	RadioGroup,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Collapse,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Popover,
	TextField,
	Tooltip,
	IconButton,
	InputAdornment,
	CircularProgress,
	Switch,
	Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { extent } from 'd3-array';
import moment from "moment";
import { HOSPITAL_CARDS_LABELS, HOSPITAL_CARDS_TYPE } from "../../../types/hospital.type";
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS } from "../../../types/community-transfer.type";
import { DECEASED_CARDS_LABELS, DECEASED_CARDS_TYPE } from "../../../types/deceased.type";
import { ADMISSION_CARDS_LABELS, ADMISSION_CARDS_TYPE } from "../../../types/admission.type";
import { OVERALL_CARDS_LABELS, OVERALL_CARDS_TYPE } from "../../../types/overall.type";
import { CUSTOM_TAB_TYPE, CHART_FILTER_DAY_OF } from "../../../types/common.type";
import { CUSTOM_TAB_PAGE_TYPE, OVERALL_PAGE_SUB_TYPE, PAGE_TYPE } from "../../../types/pages.type";
import { chartsData, chartsOverallData, chartGroupBy } from "../../../utilis/charts-common";
import {
	getTrendMappingGroups,
	createTrendMappingGroup,
	updateTrendMappingGroup,
	deleteTrendMappingGroup,
} from "../../../services/api/trend-mapping-group.api";
import {
	getTrendMappingSelection,
	createTrendMappingSelection,
	updateTrendMappingSelection,
} from "../../../services/api/trend-mapping-selection.api";
import { getAllCardForCustomTab } from "../../../services/hospital.service";
import { getAllCardForCommunity } from "../../../services/community-transfer.service";
import { getAllCardForDeceased } from "../../../services/deceased.service";
import { getAllCardForAdmissions } from "../../../services/admission.service";
import { getAllOverallPatient } from "../../../services/overall.service";
import { DEFAULT_CARD_FILTER } from "../../../store/reducers/hospital.slice";
import { DEFAULT_COMMUNITY_DATA } from "../../../store/reducers/community-transfer.slice";
import { DEFAULT_DECEASED_DATA } from "../../../store/reducers/deceased.slice";
import { DEFAULT_CARD_FILTER as DEFAULT_CARD_FILTER_ADMISSION } from "../../../store/reducers/admission.slice";
import { DEFAULT_OVERALL_DATA } from "../../../store/reducers/overall.slice";
import { updateFilterListData } from "../../../utilis/hospital-common";
import { updateFilterListData as updateFilterListDataCommunity } from "../../../utilis/community-common";
import { updateFilterListData as updateFilterListDataDeceased } from "../../../utilis/deceased-common";
import { updateFilterListDataBoth } from "../../../utilis/admission-common";
import { updateFilterListData as updateFilterListDataOverall } from "../../../utilis/overall-common";
import { getQuestionsData } from "../../../services/dynamic-data-tab.service";
import { ADT_SUB_TYPES } from "../../../types/common.type";
import { selectedTabAccess } from "../../../utilis/common";

// Helper function to format percentage: round to nearest whole number
// 100.0% → 100%, 33.3% → 33%, 33.6% → 34%, 75.5% → 76%
const formatPercentage = (value) => {
	if (value === null || value === undefined || isNaN(value)) {
		return "0%";
	}
	// Round to nearest whole number
	const rounded = Math.round(value);
	return `${rounded}%`;
};

// Mini trend graph component (clickable)
const MiniTrendGraph = ({
	series,
	width = 100,
	height = 40,
	id,
	onClick,
	displayMode = "number",
	baselineSeries = [],
	isIsolationMode = false,
	onBrushSelect = null,
	isolatedRanges = [],
	currentFilterType = null
}) => {
	const isMobile = useMediaQuery('(max-width: 768px)');
	const [isHovered, setIsHovered] = useState(false);
	const svgRef = useRef(null);
	const containerRef = useRef(null);


	// Point click handler for isolation mode - replaces brush selection
	const handlePointClick = useCallback((e, pointIndex) => {
		if (!isIsolationMode || !onBrushSelect || !Array.isArray(series) || series.length === 0) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();

		if (pointIndex < 0 || pointIndex >= series.length) return;

		const point = series[pointIndex];
		if (!point) return;

		const pointDate = point.rawDate || point.x || point.date || point.z;
		if (!pointDate) return;

		// Parse the date
		let parsedDate = null;
		if (typeof pointDate === 'string') {
			parsedDate = moment(pointDate, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD", "MMM DD, YYYY"], true);
			if (!parsedDate.isValid()) parsedDate = moment(pointDate);
		} else if (pointDate instanceof Date) {
			parsedDate = moment(pointDate);
		}

		if (!parsedDate || !parsedDate.isValid()) return;

		// Calculate bucket boundaries based on currentFilterType
		let bucketStart, bucketEnd;
		if (currentFilterType === CHART_FILTER_DAY_OF.MONTH) {
			bucketStart = parsedDate.clone().startOf('month').toDate();
			bucketEnd = parsedDate.clone().endOf('month').toDate();
		} else if (currentFilterType === CHART_FILTER_DAY_OF.WEEK) {
			bucketStart = parsedDate.clone().startOf('week').toDate();
			bucketEnd = parsedDate.clone().endOf('week').toDate();
		} else if (currentFilterType === CHART_FILTER_DAY_OF.SEVEN) {
			bucketEnd = parsedDate.toDate();
			bucketStart = parsedDate.clone().subtract(6, 'days').startOf('day').toDate();
		} else if (currentFilterType === CHART_FILTER_DAY_OF.THIRTY) {
			bucketEnd = parsedDate.toDate();
			bucketStart = parsedDate.clone().subtract(29, 'days').startOf('day').toDate();
		} else {
			bucketStart = parsedDate.clone().startOf('day').toDate();
			bucketEnd = parsedDate.clone().endOf('day').toDate();
		}

		// Call onBrushSelect with the bucket range
		onBrushSelect({ start: bucketStart, end: bucketEnd });
	}, [isIsolationMode, onBrushSelect, series, currentFilterType]);

	// No longer needed - using point click instead of brush selection

	if (!Array.isArray(series) || series.length === 0) {
		return (
			<Box sx={{ width, height, display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Typography variant="caption" color="text.secondary">No data</Typography>
			</Box>
		);
	}

	// Filter series by isolated ranges for display
	// When in isolation mode, show full series for brush selection
	// When isolated ranges are set (not in isolation mode), show filtered series
	const displaySeries = (!isIsolationMode && Array.isArray(isolatedRanges) && isolatedRanges.length > 0)
		? series.filter((point) => {
			// Prefer rawDate/x (canonical bucket date like "MM-DD-YYYY") when available.
			const pointDate = point.rawDate || point.x || point.date || point.z;
			if (!pointDate) return true;

			let parsedDate = null;
			if (typeof pointDate === 'string') {
				parsedDate = moment(pointDate, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD", "MMM DD, YYYY"], true);
				if (!parsedDate.isValid()) {
					parsedDate = moment(pointDate);
				}
			} else if (pointDate instanceof Date) {
				parsedDate = moment(pointDate);
			}

			if (!parsedDate || !parsedDate.isValid()) return true;

			// Check if date is within any of the isolated ranges
			// Use the same logic as filterSeriesByMultipleRanges to handle Monthly and 30 Days correctly
			return isolatedRanges.some(range => {
				if (!range.start || !range.end) return false;

				const rangeStart = moment(range.start);
				const rangeEnd = moment(range.end);

				// Handle different filter types with appropriate comparison logic
				if (currentFilterType === CHART_FILTER_DAY_OF.MONTH) {
					// For Monthly: rawDate is the start of the month
					// Compare by month/year - point's month should overlap with range's months
					const pointMonth = parsedDate.format('YYYY-MM');
					const rangeStartMonth = rangeStart.format('YYYY-MM');
					const rangeEndMonth = rangeEnd.format('YYYY-MM');
					return pointMonth >= rangeStartMonth && pointMonth <= rangeEndMonth;
				} else if (currentFilterType === CHART_FILTER_DAY_OF.THIRTY) {
					// For 30 Days: rawDate is the end date of the 30-day bucket
					// The bucket spans from (rawDate - 29 days) to rawDate
					// Check if the bucket overlaps with the range
					const bucketStart = parsedDate.clone().subtract(29, 'days');
					const bucketEnd = parsedDate.clone();
					// Bucket overlaps if: bucketStart <= rangeEnd AND bucketEnd >= rangeStart
					return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
				} else if (currentFilterType === CHART_FILTER_DAY_OF.SEVEN) {
					// For 7 Days: rawDate is the end date of the 7-day bucket
					// The bucket spans from (rawDate - 6 days) to rawDate
					// Check if the bucket overlaps with the range
					const bucketStart = parsedDate.clone().subtract(6, 'days');
					const bucketEnd = parsedDate.clone();
					// Bucket overlaps if: bucketStart <= rangeEnd AND bucketEnd >= rangeStart
					return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
				} else {
					// For Daily, Weekly, and others: use day-level comparison
					return parsedDate.isSameOrAfter(rangeStart, 'day') &&
						parsedDate.isSameOrBefore(rangeEnd, 'day');
				}
			});
		})
		: series;

	// ALWAYS use actual values for chart visualization (chart stays the same)
	// Only change labels to show percentages when displayMode === "percentage"
	const values = displaySeries.map((point) => point.total || 0);

	const maxValue = Math.max(...values, 1);
	const minValue = Math.min(...values, 0);
	const range = maxValue - minValue || 1;

	// Calculate percentages for labels only (when displayMode === "percentage")
	// This doesn't affect the chart visualization, only the label display
	let percentageLabels = null;
	if (displayMode === "percentage" && Array.isArray(baselineSeries) && baselineSeries.length > 0) {
		// Create a lookup map for baseline series by date
		const baselineMap = new Map();
		baselineSeries.forEach((point) => {
			const dateKey = point.date || point.z || point.x;
			if (dateKey) {
				baselineMap.set(String(dateKey), point.total || 0);
			}
		});

		// Calculate percentages for labels only
		percentageLabels = displaySeries.map((point) => {
			const dateKey = point.date || point.z || point.x;
			const baselineValue = baselineMap.get(String(dateKey)) || 0;
			const resultValue = point.total || 0;

			// Calculate percentage: (result / baseline) * 100
			// If baseline is 0, return 0 to avoid division by zero
			if (baselineValue === 0) {
				return resultValue === 0 ? 0 : 100; // If result exists but baseline is 0, show 100%
			}
			return (resultValue / baselineValue) * 100;
		});
	}

	// Increase padding in percentage mode to accommodate labels
	const padding = displayMode === "percentage" ? 8 : (isMobile ? 6 : 4);
	const topPadding = displayMode === "percentage" ? 28 : (isMobile ? 24 : 22); // Extra space at top for number labels
	const chartWidth = width - padding * 2;
	const chartHeight = height - padding - topPadding;

	const points = values.map((value, index) => {
		const x = padding + (index / (values.length - 1 || 1)) * chartWidth;
		const normalizedValue = range > 0 ? (value - minValue) / range : 0.5;
		const y = topPadding + chartHeight - normalizedValue * chartHeight;
		return { x, y, value };
	});

	const pathData = points
		.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
		.join(" ");

	// Ensure the gradient id is a valid SVG id (no spaces/special chars)
	const rawId = id || Math.random().toString(36).substr(2, 9);
	const safeId = String(rawId).replace(/[^a-zA-Z0-9_-]/g, "_");
	const gradientId = `trendGradient-${safeId}`;

	// Brush selection removed - using point-click instead

	// Calculate isolated range rectangles for all ranges
	// IMPORTANT: Calculate this AFTER points are created so we can use the actual rendered points
	// Use the same comparison logic as filtering to handle Monthly and 30 Days correctly
	const isolatedRects = Array.isArray(isolatedRanges) && isolatedRanges.length > 0 && points.length > 0
		? isolatedRanges.map(range => {
			if (!range.start || !range.end) return null;

			const rangeStart = moment(range.start);
			const rangeEnd = moment(range.end);

			// Find all points that fall within this range by checking their corresponding series data
			const matchingPointIndices = series
				.map((p, idx) => {
					const date = p.rawDate || p.x || p.date || p.z;
					if (!date) return null;
					let parsed = null;
					if (typeof date === 'string') {
						parsed = moment(date, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD", "MMM DD, YYYY"], true);
						if (!parsed.isValid()) parsed = moment(date);
					} else if (date instanceof Date) {
						parsed = moment(date);
					}
					if (!parsed || !parsed.isValid()) return null;

					// Use the same comparison logic as filtering
					let isInRange = false;
					if (currentFilterType === CHART_FILTER_DAY_OF.MONTH) {
						const pointMonth = parsed.format('YYYY-MM');
						const rangeStartMonth = rangeStart.format('YYYY-MM');
						const rangeEndMonth = rangeEnd.format('YYYY-MM');
						isInRange = pointMonth >= rangeStartMonth && pointMonth <= rangeEndMonth;
					} else if (currentFilterType === CHART_FILTER_DAY_OF.THIRTY) {
						// For 30 Days: rawDate is the end date of the bucket
						// Bucket spans from (rawDate - 29 days) to rawDate
						const bucketStart = parsed.clone().subtract(29, 'days').startOf('day');
						const bucketEnd = parsed.clone().endOf('day');
						// Check if bucket overlaps with range
						isInRange = bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
					} else if (currentFilterType === CHART_FILTER_DAY_OF.SEVEN) {
						// For 7 Days: rawDate is the end date of the bucket
						// Bucket spans from (rawDate - 6 days) to rawDate
						const bucketStart = parsed.clone().subtract(6, 'days').startOf('day');
						const bucketEnd = parsed.clone().endOf('day');
						// Check if bucket overlaps with range
						isInRange = bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
					} else {
						// For Daily, Weekly: use day-level comparison
						isInRange = parsed.isSameOrAfter(rangeStart, 'day') && parsed.isSameOrBefore(rangeEnd, 'day');
					}

					return isInRange ? idx : null;
				})
				.map((idx, originalIdx) => idx !== null ? originalIdx : null)
				.filter(idx => idx !== null);

			if (matchingPointIndices.length === 0) return null;

			const startIdx = Math.min(...matchingPointIndices);
			const endIdx = Math.max(...matchingPointIndices);

			if (startIdx >= 0 && endIdx >= 0 && startIdx <= endIdx && startIdx < points.length && endIdx < points.length) {
				const startPoint = points[startIdx];
				const endPoint = points[endIdx];
				const startX = startPoint.x;
				const endX = endPoint.x;
				// Ensure minimum width for visibility
				const rectWidth = Math.max(2, Math.abs(endX - startX));
				return {
					x: Math.min(startX, endX),
					width: rectWidth,
					startIdx,
					endIdx,
				};
			}
			return null;
		}).filter(Boolean)
		: [];

	return (
		<Box
			ref={containerRef}
			sx={{
				width,
				height,
				cursor: isIsolationMode ? "crosshair" : (onClick ? "pointer" : "default"),
				position: "relative",
				userSelect: isIsolationMode ? "none" : "auto",
				overflow: "visible",
				"&:hover": !isIsolationMode && onClick ? {
					opacity: 0.9,
					transition: "opacity 0.2s",
				} : {},
			}}
			onClick={!isIsolationMode ? onClick : undefined}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<svg
				ref={svgRef}
				width={width}
				height={height}
				style={{ overflow: "visible", position: "relative" }}
			>
				<defs>
					<linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
						<stop offset="0%" stopColor="#1976d2" stopOpacity={0.3} />
						<stop offset="100%" stopColor="#1976d2" stopOpacity={0} />
					</linearGradient>
				</defs>
				{/* Area fill */}
				<path
					d={`${pathData} L ${points[points.length - 1].x} ${height - padding} L ${padding} ${height - padding} Z`}
					fill={`url(#${gradientId})`}
				/>
				{/* Line */}
				<path
					d={pathData}
					fill="none"
					stroke="#1976d2"
					strokeWidth="1.5"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				{/* Isolated range overlays - fill area under the line chart within selected range */}
				{isolatedRects.map((rect, index) => {
					if (!rect || rect.width <= 0 || rect.startIdx === undefined || rect.endIdx === undefined) {
						// Fallback: simple rectangle if calculation failed
						if (rect && rect.x !== undefined && rect.width > 0) {
							return (
								<g key={`isolated-fallback-${index}`}>
									<rect
										x={rect.x}
										y={topPadding}
										width={rect.width}
										height={chartHeight}
										fill="#1976d2"
										opacity={0.2}
										pointerEvents="none"
									/>
								</g>
							);
						}
						return null;
					}

					const startIdx = rect.startIdx;
					const endIdx = rect.endIdx;
					const bottomY = topPadding + chartHeight;

					// Get the actual points for this range
					if (startIdx < 0 || endIdx >= points.length || startIdx > endIdx) return null;

					const rangePoints = points.slice(startIdx, endIdx + 1);
					if (rangePoints.length === 0) return null;

					const pathStart = rangePoints[0];
					const pathEnd = rangePoints[rangePoints.length - 1];

					// Create path following the line
					const linePath = rangePoints
						.map((point, idx) => `${idx === 0 ? "M" : "L"} ${point.x} ${point.y}`)
						.join(" ");

					// Complete path: line + bottom edge + close to form filled area
					const fullPath = `${linePath} L ${pathEnd.x} ${bottomY} L ${pathStart.x} ${bottomY} Z`;

					return (
						<g key={`isolated-${index}`}>
							{/* Filled area under the line - like the image */}
							<path
								d={fullPath}
								fill="#1976d2"
								opacity={0.2}
								pointerEvents="none"
							/>
							{/* Subtle vertical boundary lines */}
							<line
								x1={pathStart.x}
								y1={topPadding}
								x2={pathStart.x}
								y2={bottomY}
								stroke="#1976d2"
								strokeWidth={1}
								opacity={0.3}
								pointerEvents="none"
							/>
							<line
								x1={pathEnd.x}
								y1={topPadding}
								x2={pathEnd.x}
								y2={bottomY}
								stroke="#1976d2"
								strokeWidth={1}
								opacity={0.3}
								pointerEvents="none"
							/>
						</g>
					);
				})}
				{/* Brush selection removed - using point-click instead */}
				{/* Points - clickable in isolation mode, with blue highlight when selected */}
				{points.map((point, index) => {
					// Check if this point is in an isolated range
					const isPointSelected = Array.isArray(isolatedRanges) && isolatedRanges.length > 0 && series[index]
						? isolatedRanges.some(range => {
							if (!range.start || !range.end || !series[index]) return false;
							const pointDate = series[index].rawDate || series[index].x || series[index].date || series[index].z;
							if (!pointDate) return false;

							let parsedDate = null;
							if (typeof pointDate === 'string') {
								parsedDate = moment(pointDate, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD", "MMM DD, YYYY"], true);
								if (!parsedDate.isValid()) parsedDate = moment(pointDate);
							} else if (pointDate instanceof Date) {
								parsedDate = moment(pointDate);
							}
							if (!parsedDate || !parsedDate.isValid()) return false;

							const rangeStart = moment(range.start);
							const rangeEnd = moment(range.end);

							if (currentFilterType === CHART_FILTER_DAY_OF.MONTH) {
								const pointMonth = parsedDate.format('YYYY-MM');
								const rangeStartMonth = rangeStart.format('YYYY-MM');
								const rangeEndMonth = rangeEnd.format('YYYY-MM');
								return pointMonth >= rangeStartMonth && pointMonth <= rangeEndMonth;
							} else if (currentFilterType === CHART_FILTER_DAY_OF.THIRTY) {
								const bucketStart = parsedDate.clone().subtract(29, 'days').startOf('day');
								const bucketEnd = parsedDate.clone().endOf('day');
								return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
							} else if (currentFilterType === CHART_FILTER_DAY_OF.SEVEN) {
								const bucketStart = parsedDate.clone().subtract(6, 'days').startOf('day');
								const bucketEnd = parsedDate.clone().endOf('day');
								return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
							} else {
								return parsedDate.isSameOrAfter(rangeStart, 'day') && parsedDate.isSameOrBefore(rangeEnd, 'day');
							}
						})
						: false;

					const pointData = displaySeries[index];
					const isProjected = pointData?.isProjected || false;
					const pointSize = isIsolationMode ? 6 : (isPointSelected ? 5 : (isProjected ? 4 : 0));

					return (
						<g key={`point-${index}`}>
							{/* Clickable circle/square for point - larger in isolation mode, square for projected */}
							{isProjected ? (
								<rect
									x={point.x - pointSize / 2}
									y={point.y - pointSize / 2}
									width={pointSize}
									height={pointSize}
									fill={isPointSelected ? "#1976d2" : "#FF8C00"}
									opacity={isPointSelected ? 1 : (isIsolationMode ? 0.3 : 0.8)}
									stroke={isPointSelected ? "#1976d2" : "#CC5500"}
									strokeWidth={isPointSelected ? 2 : 1}
									transform={`rotate(45 ${point.x} ${point.y})`}
									cursor={isIsolationMode ? "pointer" : "default"}
									pointerEvents={isIsolationMode ? "all" : "none"}
									onClick={(e) => isIsolationMode && handlePointClick(e, index)}
									style={{
										transition: "all 0.2s ease",
									}}
								/>
							) : (
								<circle
									cx={point.x}
									cy={point.y}
									r={pointSize}
									fill={isPointSelected ? "#1976d2" : (isIsolationMode ? "#1976d2" : "transparent")}
									opacity={isPointSelected ? 1 : (isIsolationMode ? 0.3 : 0)}
									stroke={isPointSelected ? "#1976d2" : (isIsolationMode ? "#1976d2" : "transparent")}
									strokeWidth={isPointSelected ? 2 : (isIsolationMode ? 1 : 0)}
									cursor={isIsolationMode ? "pointer" : "default"}
									pointerEvents={isIsolationMode ? "all" : "none"}
									onClick={(e) => isIsolationMode && handlePointClick(e, index)}
									style={{
										transition: "all 0.2s ease",
									}}
								/>
							)}
							{/* Point value label */}
							{point.value !== 0 && (
								<text
									x={point.x}
									y={displayMode === "percentage" ? point.y - 14 : point.y - 10}
									textAnchor="middle"
									fill={isPointSelected ? "#1976d2" : "#1976d2"}
									fontSize={displayMode === "percentage" ? "9" : "10"}
									fontWeight={isPointSelected ? "700" : "600"}
									pointerEvents="none"
									opacity={displayMode === "percentage" ? (isHovered ? 1 : 0.7) : (isHovered ? 1 : 0)}
									style={{
										transition: "opacity 0.3s ease-in-out",
									}}
								>
									{displayMode === "percentage" && percentageLabels && percentageLabels[index] !== undefined
										? formatPercentage(percentageLabels[index])
										: point.value}
								</text>
							)}
						</g>
					);
				})}
			</svg>
		</Box>
	);
};

/** Renders above the voronoi mesh so projected (diamond) points get a larger hover target. */
const ProjectedDiamondHitLayer = memo(function ProjectedDiamondHitLayer({
	points,
	margin,
	TooltipComponent,
	hitRadius,
}) {
	const { showTooltipAt, hideTooltip } = useTooltip();
	const projectedPoints = useMemo(
		() => (points || []).filter((p) => p.data?.isProjected),
		[points]
	);

	const show = useCallback(
		(p) => {
			showTooltipAt(
				React.createElement(TooltipComponent, { point: p }),
				[p.x + margin.left, p.y + margin.top],
				"top"
			);
		},
		[showTooltipAt, TooltipComponent, margin.left, margin.top]
	);

	if (!projectedPoints.length) return null;

	return (
		<g>
			{projectedPoints.map((p) => (
				<circle
					key={`proj-hit-${p.id}`}
					cx={p.x}
					cy={p.y}
					r={hitRadius}
					fill="transparent"
					style={{ cursor: "default" }}
					onMouseEnter={() => show(p)}
					onMouseMove={() => show(p)}
					onMouseLeave={() => hideTooltip()}
				/>
			))}
		</g>
	);
});

// Full trend chart component for popup using ResponsiveLine from @nivo/line
const FullTrendChart = ({ series, displayMode = "number", baselineSeries = [] }) => {
	const isMobileQuery = useMediaQuery('(max-width: 768px)');
	const isShortMobile = useMediaQuery('(max-width: 900px) and (max-height: 500px)');
	const isMobile = isMobileQuery || isShortMobile;
	const [isHovered, setIsHovered] = useState(false);

	const chartMargin = useMemo(
		() => ({
			top: isMobile ? 20 : 30,
			right: isMobile ? 15 : 20,
			bottom: isMobile ? 100 : 140,
			left: isMobile ? 50 : 70,
		}),
		[isMobile]
	);

	if (!Array.isArray(series) || series.length === 0) {
		return (
			<Box sx={{ height: 450, display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Typography variant="body2" color="text.secondary">No data available</Typography>
			</Box>
		);
	}

	// ALWAYS use actual values for chart visualization (chart stays the same)
	// Only change labels to show percentages when displayMode === "percentage"
	const processedSeries = series;

	// Calculate percentages for labels only (when displayMode === "percentage")
	// This doesn't affect the chart visualization, only the label display
	let percentageLabels = null;
	if (displayMode === "percentage" && Array.isArray(baselineSeries) && baselineSeries.length > 0) {
		// Create a lookup map for baseline series by date
		const baselineMap = new Map();
		baselineSeries.forEach((point) => {
			const dateKey = point.date || point.z || point.x;
			if (dateKey) {
				baselineMap.set(String(dateKey), point.total || 0);
			}
		});

		// Calculate percentages for labels only
		percentageLabels = processedSeries.map((point) => {
			const dateKey = point.date || point.z || point.x;
			const baselineValue = baselineMap.get(String(dateKey)) || 0;
			const resultValue = point.total || 0;

			// Calculate percentage: (result / baseline) * 100
			if (baselineValue === 0) {
				return resultValue === 0 ? 0 : 100;
			}
			return (resultValue / baselineValue) * 100;
		});
	}

	// Transform series data to ResponsiveLine format
	// ALWAYS use actual values for chart (y field)
	// Only change displayValue to show percentages in labels
	const chartData = [
		{
			id: "trend",
			color: "hsl(217, 70%, 50%)",
			data: processedSeries.map((point, index) => {
				let xValue = point.date;

				// Try to parse the date if it's a string
				if (typeof point.date === 'string') {
					// Try common date formats
					const parsedDate = moment(point.date, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD"], true);
					if (parsedDate.isValid()) {
						xValue = parsedDate.format("MM-DD-YYYY");
					}
				}

				const value = point.total || 0; // Always use actual value for chart
				// Use percentage for label only if displayMode is percentage
				const displayValue = (displayMode === "percentage" && percentageLabels && percentageLabels[index] !== undefined)
					? formatPercentage(percentageLabels[index])
					: value;

				return {
					x: xValue,
					y: value, // Chart always uses actual value
					date: point.date,
					value: value,
					displayValue: displayValue, // Label shows percentage or actual value
					isProjected: point?.isProjected || false,
					originalY: point?.originalY,
				};
			}),
		},
	];

	// Determine if we should use time scale or point scale
	// Use time scale if dates can be parsed, otherwise use point scale
	const firstDate = series[0]?.date;
	const canUseTimeScale = firstDate && moment(firstDate, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD"], true).isValid();
	const useTimeScale = canUseTimeScale && series.length > 1;

	// Calculate Y domain (matching LineChartDialog.js)
	const calculateYDomain = (data) => {
		if (!data || data.length === 0) {
			return [0, 10]; // Default domain if no data
		}

		const allYValues = data.flatMap((serie) => serie.data.map((point) => point.y));
		const [minY, maxY] = extent(allYValues);

		return [Math.min(0, minY), maxY]; // Ensure 0 is included
	};

	const lineData = chartData[0]?.data ?? [];
	const lastPointIndex = lineData.length - 1;
	const maxYInSeries = lineData.length
		? lineData.reduce((acc, d) => Math.max(acc, Number(d.y) || 0), 0)
		: 0;
	const hasProjectedPoints = lineData.some((d) => d.isProjected);
	const fullChartPointSize = isMobile ? 10 : 8;
	const projectedHitRadius = fullChartPointSize + 12;

	// Nivo mesh tooltips use anchor "top" (above the point, centered on x).
	// Use series index for horizontal clipping; max Y for vertical (tooltip above max points clips).
	const CustomTooltip = ({ point }) => {
		const datum = point.data;
		const isProjected = datum?.isProjected || false;
		const originalY = datum?.originalY;
		const projectedY = datum?.y;

		const idx = point.index;
		const isFirstX = lastPointIndex >= 0 && idx === 0;
		const isLastX = lastPointIndex >= 0 && idx === lastPointIndex;
		const yVal = Number(datum?.y);
		const touchesPlotTop =
			maxYInSeries > 0 && Math.abs(yVal - maxYInSeries) < 1e-6;

		const transforms = [];
		if (isLastX) transforms.push("translateX(calc(-50% - 14px))");
		if (isFirstX) transforms.push("translateX(calc(50% + 14px))");
		if (touchesPlotTop) transforms.push("translateY(calc(100% + 22px))");

		const shellStyle = {
			background: "white",
			padding: "8px 12px",
			border: "1px solid #ccc",
			borderRadius: "4px",
			boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
			fontSize: isMobile ? "12px" : "14px",
			...(transforms.length > 0 && { transform: transforms.join(" ") }),
		};

		if (isProjected && originalY != null && originalY !== undefined) {
			return (
				<div style={shellStyle}>
					<div style={{ color: "#666", marginBottom: "2px" }}>
						Original: <strong>{originalY}</strong>
					</div>
					<div style={{ color: "#FF8C00", fontWeight: "bold" }}>
						Projected: <strong>{projectedY}</strong>
					</div>
					<div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
						{datum?.date || datum?.x}
					</div>
				</div>
			);
		}

		return (
			<div style={shellStyle}>
				<div>{datum?.displayValue || datum?.y}</div>
				<div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
					{datum?.date || datum?.x}
				</div>
			</div>
		);
	};

	return (
		<Box
			sx={{
				height: isMobile ? (isShortMobile ? 220 : 320) : 450,
				width: "100%",
				transition: "all 0.3s ease-in-out",
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<ResponsiveLine
				data={chartData}
				xScale={
					useTimeScale
						? {
							type: "time",
							format: "%m-%d-%Y",
						}
						: {
							type: "point",
						}
				}
				xFormat={useTimeScale ? "time:%m-%d-%Y" : undefined}
				axisBottom={{
					format: useTimeScale ? "%m-%d-%Y" : undefined,
					orient: "bottom",
					...(useTimeScale && series.length > 10 && {
						tickValues: isMobile ? "every 14 days" : "every 7 days",
					}),
					tickPadding: isMobile ? 8 : 5,
					tickRotation: isMobile ? -45 : -20,
					tickSize: isMobile ? 8 : 5,
				}}
				yScale={{
					type: "linear",
					min: 0,
					max: "auto",
					reverse: false,
					tickPadding: isMobile ? 8 : 5,
					domain: calculateYDomain(chartData),
				}}
				axisLeft={{
					format: displayMode === "percentage" ? (value) => `${value.toFixed(0)}%` : undefined,
					orient: "left",
					tickPadding: isMobile ? 8 : 5,
				}}
				margin={chartMargin}
				pointLabel={({ data }) => {
					// Don't show label if value is 0
					return data?.y !== 0 ? (data?.displayValue || data?.y) : "";
				}}
				pointLabelYOffset={-12}
				enableGridX={false}
				animate={false}
				colors={{ scheme: "category10" }}
				axisTop={null}
				axisRight={null}
				pointColor={{ theme: "background" }}
				pointBorderWidth={isMobile ? 3 : 2}
				pointBorderColor={{ from: "serieColor" }}
				pointSymbol={({ datum, size, color, borderWidth, borderColor }) => {
					if (datum?.isProjected) {
						const side = size / Math.SQRT2;
						return (
							<rect
								x={-side / 2}
								y={-side / 2}
								width={side}
								height={side}
								fill="#FF8C00"
								stroke="#CC5500"
								strokeWidth={2}
								transform="rotate(45)"
							/>
						);
					}
					return (
						<circle
							r={size / 2}
							fill={color}
							stroke={borderColor}
							strokeWidth={borderWidth}
						/>
					);
				}}
				useMesh={chartData[0]?.data?.length > 0}
				enablePointLabel={isHovered}
				pointSize={fullChartPointSize}
				tooltip={CustomTooltip}
				layers={
					hasProjectedPoints
						? [
								"grid",
								"markers",
								"axes",
								"areas",
								"crosshair",
								"lines",
								"points",
								"slices",
								"mesh",
								(layerProps) => (
									<ProjectedDiamondHitLayer
										points={layerProps.points}
										margin={layerProps.margin}
										TooltipComponent={CustomTooltip}
										hitRadius={projectedHitRadius}
									/>
								),
								"legends",
						  ]
						: undefined
				}
				theme={{
					labels: {
						text: {
							fontSize: isMobile ? 10 : 12,
							fill: "#1976d2",
							fontWeight: 600,
						},
					},
				}}
				motionConfig={{
					default: {
						duration: 300,
						easing: "easeInOut",
					},
				}}
			/>
		</Box>
	);
};

// Common toggle to enable/disable "Save selection as group" feature
const ENABLE_GROUPING = true;

// Common toggle to enable/disable custom cards in Trend Mapping
const ENABLE_CUSTOM_CARDS = true;

// Toggle to enable/disable search TextField in Analysis Result table.
// Temporarily disabled for Overall page to avoid InputBase update-depth issues.
const ENABLE_RESULT_SEARCH = true;

const DASHBOARD_OPTIONS = [
	{ id: "HOSPITAL", label: "Hospital" },
	{ id: "COMMUNITY_TRANSFER", label: "Community Transfer" },
	{ id: "ADMISSION", label: "Admission" },
	{ id: "DECEASED", label: "Deceased" },
	{ id: CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING, label: "Total Incoming" },
	{ id: CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING, label: "Total Outgoing" },
];

/** Overall chart dialog uses "OVERALL"; some callers use PAGE_TYPE.OVERALL ("overall"). */
const isTrendMappingOverallContext = (pt) =>
	pt === "OVERALL" || pt === PAGE_TYPE.OVERALL;

/** Compare-pool dashboard ids for "View cards from" (excludes current page + Overall split when already on Overall). */
const getOtherDashboardIdsForCompare = (pt) => {
	const hideOverallIncomingOutgoing = isTrendMappingOverallContext(pt);
	return DASHBOARD_OPTIONS.filter((d) => {
		if (d.id === pt) return false;
		if (
			hideOverallIncomingOutgoing &&
			(d.id === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING ||
				d.id === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING)
		) {
			return false;
		}
		return true;
	}).map((d) => d.id);
};

const PAGE_TYPE_TO_ANALYZER = {
	HOSPITAL: "hospital",
	COMMUNITY_TRANSFER: "communityTransfer",
	ADMISSION: "admission",
	DECEASED: "deceased",
	[CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING]: "overall",
	[CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING]: "overall",
};

const DASHBOARD_ID_TO_PAGE_TYPE = {
	HOSPITAL: PAGE_TYPE.HOSPITAL,
	COMMUNITY_TRANSFER: PAGE_TYPE.COMMUNITY_TRANSFER,
	DECEASED: PAGE_TYPE.DECEASED,
	ADMISSION: PAGE_TYPE.ADMISSION,
};

const CARD_CONFIG = {
	[PAGE_TYPE.HOSPITAL]: [
		{ value: HOSPITAL_CARDS_TYPE.TOTAL, label: HOSPITAL_CARDS_LABELS.total },
		{ value: HOSPITAL_CARDS_TYPE.UNPLANNED, label: HOSPITAL_CARDS_LABELS.unplannedHospitalTransfer },
		{ value: HOSPITAL_CARDS_TYPE.PLANNED, label: HOSPITAL_CARDS_LABELS.plannedHospitalTransfer },
	],
	[PAGE_TYPE.COMMUNITY_TRANSFER]: [
		{ value: CO_TRANSFER_CARDS_TYPE.TOTAL, label: COMMUNITY_CARD_LABELS.total },
		{ value: CO_TRANSFER_CARDS_TYPE.AMA, label: COMMUNITY_CARD_LABELS.AMA },
		{ value: CO_TRANSFER_CARDS_TYPE.SNF, label: COMMUNITY_CARD_LABELS.SNF },
		{ value: CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE, label: COMMUNITY_CARD_LABELS.safeDischarge },
	],
	[PAGE_TYPE.DECEASED]: [
		{ value: DECEASED_CARDS_TYPE.TOTAL, label: DECEASED_CARDS_LABELS.total },
	],
	[PAGE_TYPE.ADMISSION]: [
		{ value: ADMISSION_CARDS_TYPE.TOTAL, label: ADMISSION_CARDS_LABELS.total },
		{ value: ADMISSION_CARDS_TYPE.ADMISSION, label: ADMISSION_CARDS_LABELS.admission },
		{ value: ADMISSION_CARDS_TYPE.READMISSION, label: ADMISSION_CARDS_LABELS.readmission },
	],
};

const TOTAL_CARD_VALUES = new Set([
	"total",
	"unplannedHospitalTransfer",
	"plannedHospitalTransfer",
	"AMA",
	"SNF",
	"safeDischarge",
	"admission",
	"readmission"
]);

const COMPARE_PAGE_CONFIG = {
	HOSPITAL: {
		cardFilter: DEFAULT_CARD_FILTER,
		getCards: getAllCardForCustomTab,
		updateFn: updateFilterListData,
		query: { forType: "transfer", forTransferType: ["hospitalTransfer"], isCustom: true },
	},
	COMMUNITY_TRANSFER: {
		cardFilter: DEFAULT_COMMUNITY_DATA,
		getCards: getAllCardForCommunity,
		updateFn: updateFilterListDataCommunity,
		query: {
			forType: "transfer",
			forTransferType: [ADT_SUB_TYPES.AMA, ADT_SUB_TYPES.SAFE_DISCHARGE, ADT_SUB_TYPES.SNF],
			isCustom: true,
		},
	},
	DECEASED: {
		cardFilter: DEFAULT_DECEASED_DATA,
		getCards: getAllCardForDeceased,
		updateFn: updateFilterListDataDeceased,
		query: { forType: "transfer", forTransferType: ["deceased"], isCustom: true },
	},
	ADMISSION: {
		cardFilter: DEFAULT_CARD_FILTER_ADMISSION,
		getCards: getAllCardForAdmissions,
		updateFn: updateFilterListDataBoth,
		query: { isCustom: true },
	},
	[CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING]: {
		cardFilter: DEFAULT_OVERALL_DATA,
		getCards: getAllOverallPatient,
		updateFn: updateFilterListDataOverall,
		query: { isCustom: true },
	},
	[CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING]: {
		cardFilter: DEFAULT_OVERALL_DATA,
		getCards: getAllOverallPatient,
		updateFn: updateFilterListDataOverall,
		query: { isCustom: true },
	},
};

const COMPARE_LABEL_MAP = {
	ADMISSION: ADMISSION_CARDS_LABELS,
	COMMUNITY_TRANSFER: COMMUNITY_CARD_LABELS,
	DECEASED: DECEASED_CARDS_LABELS,
	HOSPITAL: HOSPITAL_CARDS_LABELS,
	[CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING]: OVERALL_CARDS_LABELS,
	[CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING]: OVERALL_CARDS_LABELS,
};

/**
 * Generic Trend Mapping configuration + result dialog.
 * Designed to be reusable across Hospital/Admission/Overall/etc. dialogs.
 */
export default function TrendMappingDialog({
	open,
	onClose,
	initialConfig,
	pageType,
	// Full filter context from the parent dialog (e.g. HospitalCardDetailsDialog filters object)
	dialogFilters,
	// Map of cardType -> items list used for item-level selection
	cardItemsMap,
	// Async analysis handler provided by parent, must return a result object
	onAnalyze,
	analyzing,
	result,
	// Current card being viewed (for preview)
	currentCardType,
	currentSelectedItems,
	// Current card's chart data (for preview trend charts)
	currentCardChartData,
	// Current filter type (Weekly, Monthly, Daily, etc.) for preview chart
	currentFilterType,
}) {
	const [config, setConfig] = useState(initialConfig || {});
	const [previewSeries, setPreviewSeries] = useState([]);
	// Controls expand/collapse of "Cards & Items to Compare"
	const [cardsAccordionOpen, setCardsAccordionOpen] = useState(true);
	// State for "Save Selection as Group" popover
	const [groupPopoverAnchor, setGroupPopoverAnchor] = useState(null);
	const [groupPopoverCardId, setGroupPopoverCardId] = useState(null);
	const [groupName, setGroupName] = useState("");
	// Local selection state for group items (allows selection/deselection in popover)
	const [groupItemsSelection, setGroupItemsSelection] = useState([]);
	// State for editing existing group (null = create new, groupId = edit existing)
	const [editingGroupId, setEditingGroupId] = useState(null);
	// State for search/filter results
	const [searchTerm, setSearchTerm] = useState("");
	// State for filtering results by dashboard (only visible when multiple dashboards are selected)
	const [dashboardFilter, setDashboardFilter] = useState("all");
	// State for searching cards and items in the comparison section
	const [cardItemSearchTerm, setCardItemSearchTerm] = useState("");
	// Active dashboard pool in "Cards & items" (CURRENT vs compare dashboards). Persisted via Save Selection.
	const [compareCardsPoolTab, setCompareCardsPoolTab] = useState("CURRENT");
	// State for full chart popup
	const [chartPopupOpen, setChartPopupOpen] = useState(false);
	const [chartPopupData, setChartPopupData] = useState(null);
	const [chartPopupTitle, setChartPopupTitle] = useState("");
	const [chartPopupDisplayMode, setChartPopupDisplayMode] = useState("number");
	const [chartPopupBaselineSeries, setChartPopupBaselineSeries] = useState([]);
	// State for loading groups from API
	const [loadingGroups, setLoadingGroups] = useState(false);
	// Raw GET /trend-mapping-group payload — one fetch per dialog open; remap when availableCards changes
	const [trendMappingGroupsPayload, setTrendMappingGroupsPayload] = useState(null);
	// State for saving group (to prevent multiple clicks)
	const [savingGroup, setSavingGroup] = useState(false);
	// Media queries for responsive design
	const isMobileQuery = useMediaQuery('(max-width: 768px)');
	const isShortMobile = useMediaQuery('(max-width: 900px) and (max-height: 500px)');
	const isMobile = isMobileQuery || isShortMobile;
	const isTablet = useMediaQuery('(max-width: 960px)');

	// Ref and state for chart container width and height (for responsive chart sizing)
	const chartContainerRef = useRef(null);
	const [chartWidth, setChartWidth] = useState(isMobile ? 320 : 1000);
	const [chartHeight, setChartHeight] = useState(isMobile ? 160 : 350);

	// State for saved selection
	const [savedSelection, setSavedSelection] = useState(null);
	const [loadingSelection, setLoadingSelection] = useState(false);
	const [savingSelection, setSavingSelection] = useState(false);
	const [saveSuccessMessage, setSaveSuccessMessage] = useState(null);
	// State for trend display mode (number or percentage)
	const [trendDisplayMode, setTrendDisplayMode] = useState("number"); // "number" or "percentage"
	// State for date range isolation via brush selection
	const [isIsolationMode, setIsIsolationMode] = useState(false);
	const [isolatedDateRanges, setIsolatedDateRanges] = useState([]); // Array of { start, end } objects
	const [, setShowIsolationPanel] = useState(false); // Toggle for period selection panel
	// State for minimum total count filter (can be empty string for user input)
	const [minimumTotalFilter, setMinimumTotalFilter] = useState("");

	// Every other dashboard type is a compare pool (tabs under "View cards from").
	// On Overall, hide Total Incoming / Total Outgoing — same dashboard family as the current view.
	const otherDashboardIdsForCompare = useMemo(
		() => getOtherDashboardIdsForCompare(pageType),
		[pageType]
	);
	const [compareDashboardData, setCompareDashboardData] = useState({});
	const [compareDashboardLoading, setCompareDashboardLoading] = useState({});
	const [compareDashboardCache, setCompareDashboardCache] = useState({});
	const isLoadingOtherDashboards = useMemo(
		() =>
			otherDashboardIdsForCompare.some((dashboardId) => Boolean(compareDashboardLoading?.[dashboardId])),
		[otherDashboardIdsForCompare, compareDashboardLoading]
	);
	const activeFacilities = useSelector((state) => state.activeFacilities.facilities);
	const selectedTab = selectedTabAccess();

	useEffect(() => {
		if (initialConfig) {
			setConfig(initialConfig);
		}
	}, [initialConfig]);

	// After analysis result is available, collapse the cards accordion to give more space to results
	useEffect(() => {
		if (result && !analyzing) {
			setCardsAccordionOpen(false);
		}
	}, [result, analyzing]);

	// Clear search term and reset isolation states when dialog opens/closes
	useEffect(() => {
		if (open) {
			// Reset isolation states when dialog opens
			setIsIsolationMode(false);
			setIsolatedDateRanges([]);
			setShowIsolationPanel(false);
			setMinimumTotalFilter("");
			// Fresh compare-pool fetches each open; avoids stale/empty cache after aborted requests or filter changes.
			setCompareDashboardCache({});
			setCompareDashboardData({});
			setCompareDashboardLoading({});
		} else {
			// Clear search terms when dialog closes
			setSearchTerm("");
			setCardItemSearchTerm("");
		}
	}, [open]);

	// Clear search term when analysis starts (new results coming)
	useEffect(() => {
		if (analyzing) {
			setSearchTerm("");
		}
	}, [analyzing]);

	// Calculate chart width and height based on container size using ResizeObserver
	useEffect(() => {
		if (!open) return;

		const updateChartSize = (width, height) => {
			const horizontalPadding = isMobile ? 8 : (isTablet ? 24 : 40);
			const verticalPadding = isMobile ? 20 : (isTablet ? 48 : 56);
			// Subtract padding for width - ensure minimum usable width
			const calculatedWidth = Math.max(width - horizontalPadding, isMobile ? 250 : 600);
			// Use fixed height constraint on mobile, dynamic height on desktop
			let calculatedHeight;
			if (isMobile) {
				calculatedHeight = isShortMobile ? 110 : 185;
			} else {
				calculatedHeight = Math.max(height - verticalPadding, 320);
			}
			setChartWidth(calculatedWidth);
			setChartHeight(calculatedHeight);
		};

		// Initial measure
		if (chartContainerRef.current) {
			const rect = chartContainerRef.current.getBoundingClientRect();
			updateChartSize(rect.width, rect.height);
		}

		// Delay measure to account for Dialog opening transition/animations on mobile
		const timer = setTimeout(() => {
			if (chartContainerRef.current) {
				const rect = chartContainerRef.current.getBoundingClientRect();
				updateChartSize(rect.width, rect.height);
			}
		}, 300);

		let resizeObserver;
		if (chartContainerRef.current && typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver((entries) => {
				for (let entry of entries) {
					if (entry.contentRect) {
						const rect = entry.target.getBoundingClientRect();
						updateChartSize(rect.width, rect.height);
					}
				}
			});
			resizeObserver.observe(chartContainerRef.current);
		}

		const handleWindowResize = () => {
			if (chartContainerRef.current) {
				const rect = chartContainerRef.current.getBoundingClientRect();
				updateChartSize(rect.width, rect.height);
			}
		};
		window.addEventListener('resize', handleWindowResize);

		return () => {
			clearTimeout(timer);
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			window.removeEventListener('resize', handleWindowResize);
		};
	}, [open, isMobile, isTablet, isShortMobile]);

	// Create stable hashes for patient data to detect content changes
	const filterPatientsHash = useMemo(() => {
		if (!currentCardChartData?.filterPatients || !Array.isArray(currentCardChartData.filterPatients)) return '';
		const ids = currentCardChartData.filterPatients
			.slice(0, 20) // Use first 20 IDs for hash
			.map(p => p._id || p.id || '')
			.filter(Boolean)
			.sort();
		return ids.length > 0 ? `${ids.length}-${ids[0]}-${ids[ids.length - 1]}-${ids[Math.floor(ids.length / 2)]}` : '';
	}, [currentCardChartData?.filterPatients]);

	const mainDataHash = useMemo(() => {
		if (!currentCardChartData?.mainData || !Array.isArray(currentCardChartData.mainData)) return '';
		const ids = currentCardChartData.mainData
			.slice(0, 20) // Use first 20 IDs for hash
			.map(p => p._id || p.id || '')
			.filter(Boolean)
			.sort();
		return ids.length > 0 ? `${ids.length}-${ids[0]}-${ids[ids.length - 1]}-${ids[Math.floor(ids.length / 2)]}` : '';
	}, [currentCardChartData?.mainData]);

	// Compute dependency values that change when filters or data actually change
	const previewDependencies = useMemo(() => {
		const cardType = currentCardType || dialogFilters?.type;
		const selectedItemsRaw = currentSelectedItems || dialogFilters?.cardFilter?.[cardType] || [];
		const selectedItemIds = Array.isArray(selectedItemsRaw)
			? selectedItemsRaw.map(sel => {
				if (typeof sel === 'object' && sel !== null) {
					return sel._id || sel.id || sel.key || sel.name || sel.label || sel.value;
				}
				return sel;
			}).filter(Boolean)
			: [];

		const filter = dialogFilters?.filter || {};
		const filterPatientsLength = currentCardChartData?.filterPatients?.length || 0;
		const mainDataLength = currentCardChartData?.mainData?.length || 0;
		const selectedItemsKey = JSON.stringify(selectedItemIds.sort());

		// Use the appropriate hash based on data source
		const dataHash = selectedItemIds.length > 0 ? filterPatientsHash : mainDataHash;


		return {
			cardType,
			selectedItemsKey,
			filterPatientsLength,
			mainDataLength,
			filterPatientsHash, // Hash for filterPatients
			mainDataHash, // Hash for mainData
			dataHash, // Hash for the data source being used
			filterType: currentFilterType,
			startDate: filter.startDate,
			endDate: filter.endDate,
		};
	}, [
		currentCardType,
		currentSelectedItems,
		currentCardChartData?.filterPatients?.length,
		currentCardChartData?.mainData?.length,
		filterPatientsHash, // Use the stable hash
		mainDataHash, // Use the stable hash
		currentFilterType,
		dialogFilters?.filter,
		dialogFilters?.cardFilter,
		dialogFilters?.type,
	]);

	// Load preview series when dependencies change
	useEffect(() => {
		if (!open || !currentCardChartData) {
			setPreviewSeries([]);
			return;
		}

		const cardType = previewDependencies.cardType;
		if (!cardType) {
			setPreviewSeries([]);
			return;
		}

		const filter = dialogFilters?.filter || {};
		if (!filter.startDate || !filter.endDate) {
			setPreviewSeries([]);
			return;
		}

		// Parse selected items from the dependency key
		const selectedItemIds = JSON.parse(previewDependencies.selectedItemsKey || '[]');

		let isMounted = true;

		const loadPreviewSeries = async () => {
			// Check if result has the series already, but ONLY use it if:
			// 1. The result exists
			// 2. The filter type matches (don't use cached data from different filter type)
			// 3. The data hasn't changed (check hash)
			const baseCardResult = result?.matchesByCard?.find(
				(r) => r.cardId === cardType && !r.itemId
			);

			// Only use cached result if filter type matches and data hasn't changed
			if (baseCardResult?.series?.length > 0) {
				// Check if the cached result is for the same filter type
				// We can't easily check this from the result, so we'll recalculate to be safe
				// when filter type changes
				const shouldUseCache = false; // Always recalculate to ensure accuracy

				if (shouldUseCache && isMounted) {
					setPreviewSeries(baseCardResult.series);
					return;
				}
			}

			// Get raw patient data (same source as main chart)
			// Use EXACT same logic as parent chart: filterPatients when items selected, mainData otherwise
			// This matches the parent chart's logic in handleClickFilter and filterOptions
			let patientsData = null;

			// Match parent chart logic EXACTLY:
			// - When items are selected: use filterPatients (parent uses chartData.filterPatients)
			// - When no items selected: use mainData (parent uses chartData.mainData)
			if (selectedItemIds.length > 0) {
				// Items are selected - use filterPatients (same as parent chart's handleClickFilter)
				// Parent chart: if (selectedItem.length > 0) { uses chartData.filterPatients }
				if (currentCardChartData.filterPatients && Array.isArray(currentCardChartData.filterPatients) && currentCardChartData.filterPatients.length > 0) {
					patientsData = currentCardChartData.filterPatients;
				} else {
					// Fallback to mainData if filterPatients not available or empty
					// This can happen if filter hasn't been applied yet
					if (currentCardChartData.mainData && Array.isArray(currentCardChartData.mainData)) {
						patientsData = currentCardChartData.mainData;
					}
				}
			} else {
				// No items selected - use mainData (same as parent chart)
				// Parent chart: else { uses chartData.mainData }
				if (currentCardChartData.mainData && Array.isArray(currentCardChartData.mainData)) {
					patientsData = currentCardChartData.mainData;
				}
			}

			// Additional fallbacks for edge cases
			if (!patientsData) {
				if (currentCardChartData.data && Array.isArray(currentCardChartData.data)) {
					patientsData = currentCardChartData.data;
				} else if (Array.isArray(currentCardChartData)) {
					patientsData = currentCardChartData;
				}
			}

			if (!patientsData || !Array.isArray(patientsData) || patientsData.length === 0) {
				if (isMounted) {
					setPreviewSeries([]);
				}
				return;
			}

			try {
				// For Overall page with TOTAL transfer type, use chartsOverallData to get +/- values
				// This matches how the main chart displays data (totalIN - totalOut)
				// For other cases, use chartsData (SAME as main chart)
				const transferType = dialogFilters?.transferType;
				const isOverallTotal = pageType === "OVERALL" && transferType === OVERALL_PAGE_SUB_TYPE.TOTAL;

				let rawChartData;
				if (isOverallTotal) {
					rawChartData = await chartsOverallData(patientsData, filter);
				} else {
					rawChartData = await chartsData(patientsData, filter, cardType);
				}

				if (!Array.isArray(rawChartData) || rawChartData.length === 0) {
					if (isMounted) {
						setPreviewSeries([]);
					}
					return;
				}

				// Get the filter type - use currentFilterType prop directly (same as parent chart's buttonFilterType)
				const filterTypeToUse = currentFilterType || previewDependencies.filterType || CHART_FILTER_DAY_OF.DAY;

				// Use chartGroupBy to group by filter type (SAME as main chart)
				// Extract census data from dialogFilters if available (for accurate monthly calculations)
				// This ensures the preview matches the main chart exactly
				const censusAvg = dialogFilters?.censusAverage ? parseFloat(dialogFilters.censusAverage) || 0 : 0;
				const censusPeriod = dialogFilters?.censusByPeriod || [];
				const grouped = await chartGroupBy(
					rawChartData,
					filterTypeToUse,
					censusAvg,
					censusPeriod,
					filter
				);

				if (!Array.isArray(grouped) || grouped.length === 0) {
					if (isMounted) {
						setPreviewSeries([]);
					}
					return;
				}

				// Convert to series format (matching the format expected by MiniTrendGraph)
				// For Overall page with TOTAL transfer type, total can be negative (totalIN - totalOut)
				// For other cases, the y field is a binary string representation, not the actual count
				// We need to count the actual patients from facilityWiseData or facilityIds
				// This matches how the parent chart (LineChartDialog) calculates the count
				const series = grouped.map((item) => {
					let totalValue = 0;

					// If this is a projected data point, use the pre-calculated projected y value directly
					// The projection was already calculated in chartGroupBy, so we should use it as-is
					// This matches the logic in LineChartDialog.js
					if (item?.isProjected && item?.y != null && typeof item.y === 'number') {
						// Use the projected y value directly from chartGroupBy
						totalValue = item.y;
					} else if (isOverallTotal) {
						// For TOTAL transfer type, total is totalIN - totalOut (can be negative)
						// Use item.total or item.y directly, both represent the net value
						if (typeof item.total === 'number') {
							totalValue = item.total;
						} else if (typeof item.total === 'string') {
							const parsed = parseFloat(item.total);
							if (!isNaN(parsed)) {
								totalValue = parsed;
							}
						} else if (typeof item.y === 'number') {
							// For chartsOverallData, y is also totalIN - totalOut
							totalValue = item.y;
						}
					} else {
						// For non-TOTAL transfer types, use the original logic
						// If total exists and is a number, use it
						if (typeof item.total === 'number') {
							totalValue = item.total;
						} else if (typeof item.total === 'string') {
							const parsed = parseFloat(item.total);
							if (!isNaN(parsed)) {
								totalValue = parsed;
							}
						}

						// If total is not available or is 0, count patients from facilityWiseData
						// This is how the parent chart calculates the count when facilityWiseData exists
						if (totalValue === 0 && item.facilityWiseData && typeof item.facilityWiseData === 'object') {
							// Count all patients across all facilities
							const facilityWiseData = item.facilityWiseData;
							totalValue = Object.values(facilityWiseData).reduce((sum, patients) => {
								return sum + (Array.isArray(patients) ? patients.length : 0);
							}, 0);
						}

						// Fallback: use facilityIds length if facilityWiseData not available
						// (facilityIds can have duplicates, so this is less accurate)
						if (totalValue === 0 && Array.isArray(item.facilityIds)) {
							totalValue = item.facilityIds.length;
						}

						// Last resort: try to parse y as number (but y is usually a binary string)
						if (totalValue === 0 && typeof item.y === 'number') {
							totalValue = item.y;
						}
					}

					// IMPORTANT for isolation / bucket-based operations in preview:
					// - date: human label for display (item.z or item.x)
					// - rawDate: canonical bucket date from chartGroupBy (item.x, e.g. "MM-DD-YYYY")
					return {
						date: item.z || item.x,
						rawDate: item.x,
						total: totalValue,
						isProjected: item?.isProjected || false,
						originalY: item?.originalY,
					};
				});

				if (isMounted) {
					setPreviewSeries(series);
				}
			} catch (error) {
				if (isMounted) {
					setPreviewSeries([]);
				}
			}
		};

		loadPreviewSeries();

		return () => {
			isMounted = false;
		};
	}, [open, previewDependencies, currentCardChartData, dialogFilters, result, currentFilterType, pageType]);

	// Get dynamicCards from Redux if not in dialogFilters (page-specific)
	const dynamicCardsFromRedux = useSelector((state) => {
		if (pageType === "COMMUNITY_TRANSFER") {
			return state.communityTransfer?.dynamicCards || [];
		}
		if (pageType === "DECEASED") {
			return state.deceased?.dynamicCards || [];
		}
		if (pageType === "ADMISSION") {
			return state.admission?.dynamicCards || [];
		}
		if (pageType === "OVERALL") {
			return state.overall?.dynamicCards || [];
		}
		return state.hospital?.dynamicCards || [];
	});
	// Get patientList from Redux to access customTabs (page-specific)
	const patientListFromRedux = useSelector((state) => {
		if (pageType === "COMMUNITY_TRANSFER") {
			return state.communityTransfer?.patientList || null;
		}
		if (pageType === "DECEASED") {
			return state.deceased?.patientList || null;
		}
		if (pageType === "ADMISSION") {
			return state.admission?.patientList || null;
		}
		if (pageType === "OVERALL") {
			return state.overall?.patientList || null;
		}
		return state.hospital?.patientList || null;
	});

	const currentDashboardLabel = useMemo(() => {
		if (pageType === "COMMUNITY_TRANSFER") return "Community Transfer";
		if (pageType === "DECEASED") return "Deceased";
		if (pageType === "ADMISSION") return "Admission";
		if (pageType === "OVERALL") return "Overall";
		return "Hospital";
	}, [pageType]);

	// Available cards to show in Trend Mapping for Hospital.
	// This can be extended or customized per pageType later.
	const availableCards = useMemo(() => {
		const list = [];

		// Helper to safely get title value (handles string trimming, returns null for empty strings)
		const getTitleValue = (val) => {
			if (!val) return null;
			if (typeof val === 'string') {
				const trimmed = val.trim();
				return trimmed.length > 0 ? trimmed : null;
			}
			return val;
		};

		const pushCard = (type, labelOverride = null, cardDef = null) => {
			if (!type) return;
			// Allow caller to override labels/items via cardItemsMap if needed
			const itemsFromMap = cardItemsMap?.[type];
			const labelFromMap = itemsFromMap?.label;

			// Get items from filterListData
			let items = (itemsFromMap && itemsFromMap.items) || dialogFilters?.filterListData?.[type] || [];

			// Get label based on page type
			const getCardLabel = () => {
				if (labelOverride) return labelOverride;
				if (labelFromMap) return labelFromMap;
				if (pageType === "COMMUNITY_TRANSFER") {
					return COMMUNITY_CARD_LABELS?.[type] || type;
				}
				if (pageType === "DECEASED") {
					return DECEASED_CARDS_LABELS?.[type] || type;
				}
				if (pageType === "ADMISSION") {
					return ADMISSION_CARDS_LABELS?.[type] || type;
				}
				if (pageType === "OVERALL") {
					return OVERALL_CARDS_LABELS?.[type] || type;
				}
				return HOSPITAL_CARDS_LABELS?.[type] || type;
			};

			// For combineTab cards, filter items based on the same criteria as DynamicDataCardList
			// Only show items if isCustomDateAnalysis with date or time type
			if (cardDef) {
				// Check question structure - it might be nested or at root level
				// Also check if type is directly on cardDef (for customTabs structure)
				const question = cardDef?.question || cardDef;
				const questionType = question?.type || cardDef?.type;
				const isCombineTab = questionType === CUSTOM_TAB_TYPE.combineTab;

				if (isCombineTab) {
					// Use same logic as DynamicDataCardList: question.isCustomDateAnalysis && question.customAnalysisType
					// Check both question and cardDef root level
					const isCustomDateAnalysis = question?.isCustomDateAnalysis || cardDef?.isCustomDateAnalysis;
					const customAnalysisType = question?.customAnalysisType || cardDef?.customAnalysisType;
					const isValidCombineTab = isCustomDateAnalysis &&
						(customAnalysisType === "date" || customAnalysisType === "time");

					// If combineTab doesn't meet criteria, don't show any items
					if (!isValidCombineTab) {
						items = [];
					}
				}
			}

			list.push({
				id: type,
				label: getCardLabel(),
				items: items,
				dashboard: "CURRENT",
				dashboardLabel: currentDashboardLabel,
			});
		};

		if (pageType === "HOSPITAL") {
			// Standard hospital cards - include all cards that are rendered in Hospital.js
			pushCard(HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS);
			pushCard(HOSPITAL_CARDS_TYPE.DCER_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.INSURANCE_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.RETURNS_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.FLOORS_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.DOCTOR_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.DAYS_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.DX_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.SHIFT_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.NURSE_DATA);
			pushCard(HOSPITAL_CARDS_TYPE.HOSPITAL_DATA);

			// Add custom tabs (excluding combineTab type)
			// Custom tabs are stored in filterListData with their accessor as the key
			// We can identify them by checking dynamicCards or by checking filterListData keys
			// that aren't standard HOSPITAL_CARDS_TYPE values
			const filterListData = dialogFilters?.filterListData || {};
			// Get dynamicCards from dialogFilters first, fallback to Redux
			const dynamicCards = dialogFilters?.dynamicCards || dynamicCardsFromRedux || [];
			// Get patientList from dialogFilters first, fallback to Redux (for customTabs)
			const patientList = dialogFilters?.patientList || patientListFromRedux;
			// Get customTabs from patientList (similar to Hospital.js usage)
			const customTabs = patientList?.customTabs || [];

			// Add custom tabs (excluding combineTab type) - only if ENABLE_CUSTOM_CARDS is true
			if (ENABLE_CUSTOM_CARDS) {
				// Standard card types to exclude from custom tab detection
				const standardCardTypes = new Set([
					HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
					HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
					HOSPITAL_CARDS_TYPE.DX_DATA,
					HOSPITAL_CARDS_TYPE.DAYS_DATA,
					HOSPITAL_CARDS_TYPE.SHIFT_DATA,
					HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
					HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
					HOSPITAL_CARDS_TYPE.DCER_DATA,
					HOSPITAL_CARDS_TYPE.RETURNS_DATA,
					HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
					HOSPITAL_CARDS_TYPE.FLOORS_DATA,
					HOSPITAL_CARDS_TYPE.NURSE_DATA,
					HOSPITAL_CARDS_TYPE.TOTAL,
					HOSPITAL_CARDS_TYPE.PLANNED,
					HOSPITAL_CARDS_TYPE.UNPLANNED,
				]);

				// Process dynamicCards to find custom tabs
				// For combineTab: only include if isCustomDateAnalysis with date or time type
				// For other custom tabs: include all
				// Merge dynamicCards and customTabs the same way Hospital.js does (line 2982)
				const allDynamicCards = [...(dynamicCards || []), ...(customTabs || [])];

				allDynamicCards.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const accessor = card?.accessor;

					// Check if this is a combineTab card
					// Check type at root level (for customTabs from HOSPITAL_CUSTOM_CARD_DATA)
					// or in question object (for dynamicCards)
					const cardType = card?.type;
					const questionType = card?.question?.type;
					const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;

					if (isCustomTab && accessor) {
						// For combineTab cards: ONLY include if they meet the criteria from DynamicDataCardList.js
						// (isCustomDateAnalysis && customAnalysisType === "date" OR "time")
						if (isCombineTab) {
							// Check isCustomDateAnalysis and customAnalysisType at root level or in question
							const isCustomDateAnalysis = card?.isCustomDateAnalysis || card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.customAnalysisType || card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");

							// Skip combineTab cards that don't meet criteria - don't add them to the list at all
							if (!isValidCombineTab) {
								return;
							}
						}

						// Check if this custom tab has data in filterListData
						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							// Use same title priority as DynamicCardItem: title || label || tableLabel
							// Also check question.label and question.title as fallbacks for custom cards
							// Check all possible locations where title might be stored
							const titleValue = getTitleValue(card?.title);
							const labelValue = getTitleValue(card?.label);
							const tableLabelValue = getTitleValue(card?.tableLabel);
							const questionLabelValue = getTitleValue(card?.question?.label);
							const questionTitleValue = getTitleValue(card?.question?.title);
							const questionQuestionValue = getTitleValue(card?.question?.question);
							const descriptionValue = getTitleValue(card?.description);

							const cardLabel = titleValue ||
								labelValue ||
								tableLabelValue ||
								questionLabelValue ||
								questionTitleValue ||
								questionQuestionValue ||
								descriptionValue ||
								accessor;

							pushCard(accessor, cardLabel, card);
						}
					}
				});

				// Also process customTabs from patientList (similar to Hospital.js)
				// These are custom tabs that might not be in dynamicCards
				customTabs.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const questionType = card?.question?.type;
					const isCombineTab = questionType === CUSTOM_TAB_TYPE.combineTab;
					const accessor = card?.accessor;

					if (isCustomTab && accessor) {
						// For combineTab, check if it meets the criteria
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");

							// Skip combineTab items that don't meet criteria
							if (!isValidCombineTab) {
								return;
							}
						}

						// Skip if already added from dynamicCards
						if (list.some((c) => c.id === accessor)) {
							return;
						}

						// Check if this custom tab has data in filterListData
						const customTabData = filterListData[accessor];

						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							// Use same title priority as DynamicCardItem: title || label || tableLabel
							// Also check question.label and question.title as fallbacks for custom cards
							// Check all possible locations where title might be stored
							const titleValue = getTitleValue(card?.title);
							const labelValue = getTitleValue(card?.label);
							const tableLabelValue = getTitleValue(card?.tableLabel);
							const questionLabelValue = getTitleValue(card?.question?.label);
							const questionTitleValue = getTitleValue(card?.question?.title);
							const questionQuestionValue = getTitleValue(card?.question?.question);
							const descriptionValue = getTitleValue(card?.description);

							const cardLabel = titleValue ||
								labelValue ||
								tableLabelValue ||
								questionLabelValue ||
								questionTitleValue ||
								questionQuestionValue ||
								descriptionValue ||
								accessor;

							pushCard(accessor, cardLabel, card);
						}
					}
				});

				// Also check filterListData for any custom tabs that might not be in dynamicCards
				// but are present in filterListData (fallback approach)
				Object.keys(filterListData).forEach((key) => {
					// Skip if it's a standard card type
					if (standardCardTypes.has(key)) {
						return;
					}

					// Skip if already added from dynamicCards
					if (list.some((card) => card.id === key)) {
						return;
					}

					// Check if it's a custom tab by looking at the data structure
					const data = filterListData[key];

					if (Array.isArray(data) && data.length > 0) {
						// Check if items have structure similar to custom tabs (have _id, name, label, etc.)
						const firstItem = data[0];
						if (firstItem && (firstItem._id || firstItem.id || firstItem.name || firstItem.label)) {
							// Try to find the card definition in allDynamicCards (merged from dynamicCards and customTabs)
							const keyWithoutSuffix = key.replace(/Customtab$/i, '');
							const cardDef = allDynamicCards.find((c) =>
								c.accessor === key ||
								c.accessor === keyWithoutSuffix ||
								c.key === key ||
								c.key === keyWithoutSuffix
							);

							if (cardDef) {
								// Check if this is a combineTab card
								const cardType = cardDef?.type;
								const questionType = cardDef?.question?.type;
								const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;

								// For combineTab cards: ONLY include if they meet the criteria from DynamicDataCardList.js
								// (isCustomDateAnalysis && customAnalysisType === "date" OR "time")
								if (isCombineTab) {
									// Check isCustomDateAnalysis and customAnalysisType at root level or in question
									const isCustomDateAnalysis = cardDef?.isCustomDateAnalysis || cardDef?.question?.isCustomDateAnalysis;
									const customAnalysisType = cardDef?.customAnalysisType || cardDef?.question?.customAnalysisType;
									const isValidCombineTab = isCustomDateAnalysis &&
										(customAnalysisType === "date" || customAnalysisType === "time");

									// Skip combineTab cards that don't meet criteria - don't add them to the list at all
									if (!isValidCombineTab) {
										return;
									}
								}

								// Use same title priority as DynamicCardItem: title || label || tableLabel
								// Also check question.label and question.title as fallbacks for custom cards
								// Check all possible locations where title might be stored
								const titleValue = getTitleValue(cardDef?.title);
								const labelValue = getTitleValue(cardDef?.label);
								const tableLabelValue = getTitleValue(cardDef?.tableLabel);
								const questionLabelValue = getTitleValue(cardDef?.question?.label);
								const questionTitleValue = getTitleValue(cardDef?.question?.title);
								const questionQuestionValue = getTitleValue(cardDef?.question?.question);
								const descriptionValue = getTitleValue(cardDef?.description);

								const cardLabel = titleValue ||
									labelValue ||
									tableLabelValue ||
									questionLabelValue ||
									questionTitleValue ||
									questionQuestionValue ||
									descriptionValue ||
									key;

								pushCard(key, cardLabel, cardDef);
							} else {
								// If not found, try alternative matching strategies in allDynamicCards
								const keyWithoutSuffixAlt = key.replace(/Customtab$/i, '');
								const cardDefByKey = allDynamicCards.find((c) => c.key === key);
								const cardDefById = allDynamicCards.find((c) => String(c.id || '') === String(key));
								const cardDefBy_Id = allDynamicCards.find((c) => String(c._id || '') === String(key));

								// Also try case-insensitive matching
								const cardDefCaseInsensitive = allDynamicCards.find((c) =>
									c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()
								);

								// Try partial matching (e.g., if key is "test8Customtab" and accessor is "test8")
								const cardDefPartial = allDynamicCards.find((c) =>
									c.accessor && (String(key).includes(String(c.accessor)) || String(c.accessor).includes(String(key)))
								);

								// Try matching by removing "Customtab" suffix
								const cardDefWithoutSuffix = allDynamicCards.find((c) =>
									c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixAlt).toLowerCase()
								);

								const alternativeMatch = cardDefByKey || cardDefById || cardDefBy_Id || cardDefCaseInsensitive || cardDefPartial || cardDefWithoutSuffix;

								if (alternativeMatch) {
									// Found by alternative method - use it
									// Check if this is a combineTab card
									const altCardType = alternativeMatch?.type;
									const altQuestionType = alternativeMatch?.question?.type;
									const isCombineTab = altCardType === CUSTOM_TAB_TYPE.combineTab || altQuestionType === CUSTOM_TAB_TYPE.combineTab;

									// For combineTab cards: ONLY include if they meet the criteria from DynamicDataCardList.js
									if (isCombineTab) {
										// Check isCustomDateAnalysis and customAnalysisType at root level or in question
										const isCustomDateAnalysis = alternativeMatch?.isCustomDateAnalysis || alternativeMatch?.question?.isCustomDateAnalysis;
										const customAnalysisType = alternativeMatch?.customAnalysisType || alternativeMatch?.question?.customAnalysisType;
										const isValidCombineTab = isCustomDateAnalysis &&
											(customAnalysisType === "date" || customAnalysisType === "time");

										// Skip combineTab cards that don't meet criteria - don't add them to the list at all
										if (!isValidCombineTab) {
											return;
										}
									}

									const titleValue = getTitleValue(alternativeMatch?.title);
									const labelValue = getTitleValue(alternativeMatch?.label);
									const tableLabelValue = getTitleValue(alternativeMatch?.tableLabel);
									const questionLabelValue = getTitleValue(alternativeMatch?.question?.label);
									const questionTitleValue = getTitleValue(alternativeMatch?.question?.title);
									const questionQuestionValue = getTitleValue(alternativeMatch?.question?.question);
									const descriptionValue = getTitleValue(alternativeMatch?.description);

									const cardLabel = titleValue ||
										labelValue ||
										tableLabelValue ||
										questionLabelValue ||
										questionTitleValue ||
										questionQuestionValue ||
										descriptionValue ||
										key;

									pushCard(key, cardLabel, alternativeMatch);
								} else {
									// Still not found - before adding, try one more time to find card definition
									// to check if it's a combineTab that should be excluded
									// Try allDynamicCards to see if any match by checking all properties
									const keyWithoutSuffixForAnyCard = key.replace(/Customtab$/i, '');
									const anyCardDef = allDynamicCards.find((c) => {
										return c.accessor === key ||
											c.accessor === keyWithoutSuffixForAnyCard ||
											c.key === key ||
											c.key === keyWithoutSuffixForAnyCard ||
											String(c.id || '') === String(key) ||
											String(c._id || '') === String(key) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixForAnyCard).toLowerCase());
									});

									if (anyCardDef) {
										// Check if this is a combineTab card
										const anyCardType = anyCardDef?.type;
										const anyQuestionType = anyCardDef?.question?.type;
										const isCombineTab = anyCardType === CUSTOM_TAB_TYPE.combineTab || anyQuestionType === CUSTOM_TAB_TYPE.combineTab;

										if (isCombineTab) {
											// For combineTab cards: ONLY include if they meet the criteria from DynamicDataCardList.js
											const isCustomDateAnalysis = anyCardDef?.isCustomDateAnalysis || anyCardDef?.question?.isCustomDateAnalysis;
											const customAnalysisType = anyCardDef?.customAnalysisType || anyCardDef?.question?.customAnalysisType;
											const isValidCombineTab = isCustomDateAnalysis &&
												(customAnalysisType === "date" || customAnalysisType === "time");

											// Skip combineTab cards that don't meet criteria - don't add them to the list at all
											if (!isValidCombineTab) {
												return;
											}
										}
									}

									// Try to extract label from data items or use a formatted version of the key
									// Check if first item has card-related metadata
									const itemCardLabel = firstItem?.cardLabel || firstItem?.cardTitle || firstItem?.cardName;

									// Try to format the key to be more readable (remove "Customtab" suffix, capitalize)
									const formattedKey = key
										.replace(/Customtab$/i, '')
										.replace(/([A-Z])/g, ' $1')
										.replace(/^./, str => str.toUpperCase())
										.trim();

									const finalLabel = itemCardLabel || formattedKey || key;

									// Try to find cardDef one more time for filtering
									// First try allDynamicCards, then try Redux state directly
									const keyWithoutSuffixForFinal = key.replace(/Customtab$/i, '');
									let finalCardDef = allDynamicCards.find((c) => {
										return c.accessor === key ||
											c.accessor === keyWithoutSuffixForFinal ||
											c.key === key ||
											c.key === keyWithoutSuffixForFinal ||
											String(c.id || '') === String(key) ||
											String(c._id || '') === String(key) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixForFinal).toLowerCase());
									});

									// If not found, try Redux state's patientList.customTabs directly
									if (!finalCardDef && patientListFromRedux?.customTabs) {
										finalCardDef = patientListFromRedux.customTabs.find((c) => {
											return c.accessor === key ||
												c.accessor === keyWithoutSuffixForFinal ||
												c.key === key ||
												c.key === keyWithoutSuffixForFinal ||
												String(c.id || '') === String(key) ||
												String(c._id || '') === String(key) ||
												(c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) ||
												(c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixForFinal).toLowerCase());
										});
									}

									// If we found a cardDef, check if it's a combineTab that should be filtered
									if (finalCardDef) {
										const finalCardType = finalCardDef?.type;
										const finalQuestionType = finalCardDef?.question?.type;
										const isFinalCombineTab = finalCardType === CUSTOM_TAB_TYPE.combineTab || finalQuestionType === CUSTOM_TAB_TYPE.combineTab;

										if (isFinalCombineTab) {
											const isCustomDateAnalysis = finalCardDef?.isCustomDateAnalysis || finalCardDef?.question?.isCustomDateAnalysis;
											const customAnalysisType = finalCardDef?.customAnalysisType || finalCardDef?.question?.customAnalysisType;
											const isValidCombineTab = isCustomDateAnalysis &&
												(customAnalysisType === "date" || customAnalysisType === "time");

											if (!isValidCombineTab) {
												return; // Skip this card
											}
										}
									}

									// If we still can't find the cardDef and the key ends with "Customtab",
									// it might be a combineTab card that we can't verify. Skip it to be safe.
									if (!finalCardDef && /Customtab$/i.test(key)) {
										return; // Skip to avoid adding combineTab cards without proper filtering
									}

									pushCard(key, finalLabel, finalCardDef);
								}
							}
						}
					}
				});
			}
		} else if (pageType === "COMMUNITY_TRANSFER") {
			// Standard Community Transfer cards - include all cards that are rendered in CommunityTransfer.js
			// Order matches the renderCard function in CommunityTransfer.js
			pushCard(CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA);
			pushCard(CO_TRANSFER_CARDS_TYPE.RETURNS_DATA);
			pushCard(CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA);
			pushCard(CO_TRANSFER_CARDS_TYPE.DAYS_DATA);
			pushCard(CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA);
			pushCard(CO_TRANSFER_CARDS_TYPE.FLOORS_DATA);
			pushCard(CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA);
			pushCard(CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA);

			// Add custom tabs (excluding combineTab type)
			const filterListData = dialogFilters?.filterListData || {};
			// Get dynamicCards from dialogFilters first, fallback to Redux
			const dynamicCards = dialogFilters?.dynamicCards || dynamicCardsFromRedux || [];
			// Get patientList from dialogFilters first, fallback to Redux (for customTabs)
			const patientList = dialogFilters?.patientList || patientListFromRedux;
			// Get customTabs from patientList (similar to CommunityTransfer.js usage)
			const customTabs = patientList?.customTabs || [];

			// Add custom tabs (excluding combineTab type) - only if ENABLE_CUSTOM_CARDS is true
			if (ENABLE_CUSTOM_CARDS) {
				// Standard card types to exclude from custom tab detection
				const standardCardTypes = new Set([
					CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA,
					CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA,
					CO_TRANSFER_CARDS_TYPE.FLOORS_DATA,
					CO_TRANSFER_CARDS_TYPE.DAYS_DATA,
					CO_TRANSFER_CARDS_TYPE.RETURNS_DATA,
					CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
					CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA,
					CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA,
					CO_TRANSFER_CARDS_TYPE.TOTAL,
					CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
					CO_TRANSFER_CARDS_TYPE.SNF,
					CO_TRANSFER_CARDS_TYPE.AMA,
				]);

				// Process dynamicCards to find custom tabs
				// For combineTab: only include if isCustomDateAnalysis with date or time type
				// For other custom tabs: include all
				// Merge dynamicCards and customTabs the same way CommunityTransfer.js does
				const allDynamicCards = [...(dynamicCards || []), ...(customTabs || [])];

				allDynamicCards.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const accessor = card?.accessor;

					// Check if this is a combineTab card
					const cardType = card?.type;
					const questionType = card?.question?.type;
					const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;

					if (isCustomTab && accessor) {
						// For combineTab cards: ONLY include if they meet the criteria
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.isCustomDateAnalysis || card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.customAnalysisType || card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");

							if (!isValidCombineTab) {
								return;
							}
						}

						// Check if this custom tab has data in filterListData
						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const titleValue = getTitleValue(card?.title);
							const labelValue = getTitleValue(card?.label);
							const tableLabelValue = getTitleValue(card?.tableLabel);
							const questionLabelValue = getTitleValue(card?.question?.label);
							const questionTitleValue = getTitleValue(card?.question?.title);
							const questionQuestionValue = getTitleValue(card?.question?.question);
							const descriptionValue = getTitleValue(card?.description);

							const cardLabel = titleValue ||
								labelValue ||
								tableLabelValue ||
								questionLabelValue ||
								questionTitleValue ||
								questionQuestionValue ||
								descriptionValue ||
								accessor;

							pushCard(accessor, cardLabel, card);
						}
					}
				});

				// Also process customTabs from patientList
				customTabs.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const questionType = card?.question?.type;
					const isCombineTab = questionType === CUSTOM_TAB_TYPE.combineTab;
					const accessor = card?.accessor;

					if (isCustomTab && accessor) {
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");

							if (!isValidCombineTab) {
								return;
							}
						}

						// Skip if already added from dynamicCards
						if (list.some((c) => c.id === accessor)) {
							return;
						}

						const customTabData = filterListData[accessor];

						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const titleValue = getTitleValue(card?.title);
							const labelValue = getTitleValue(card?.label);
							const tableLabelValue = getTitleValue(card?.tableLabel);
							const questionLabelValue = getTitleValue(card?.question?.label);
							const questionTitleValue = getTitleValue(card?.question?.title);
							const questionQuestionValue = getTitleValue(card?.question?.question);
							const descriptionValue = getTitleValue(card?.description);

							const cardLabel = titleValue ||
								labelValue ||
								tableLabelValue ||
								questionLabelValue ||
								questionTitleValue ||
								questionQuestionValue ||
								descriptionValue ||
								accessor;

							pushCard(accessor, cardLabel, card);
						}
					}
				});

				// Also check filterListData for any custom tabs that might not be in dynamicCards
				Object.keys(filterListData).forEach((key) => {
					if (standardCardTypes.has(key)) {
						return;
					}

					if (list.some((card) => card.id === key)) {
						return;
					}

					const data = filterListData[key];

					if (Array.isArray(data) && data.length > 0) {
						const firstItem = data[0];
						if (firstItem && (firstItem._id || firstItem.id || firstItem.name || firstItem.label)) {
							const keyWithoutSuffix = key.replace(/Customtab$/i, '');
							const cardDef = allDynamicCards.find((c) =>
								c.accessor === key ||
								c.accessor === keyWithoutSuffix ||
								c.key === key ||
								c.key === keyWithoutSuffix
							);

							if (cardDef) {
								const cardType = cardDef?.type;
								const questionType = cardDef?.question?.type;
								const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;

								if (isCombineTab) {
									const isCustomDateAnalysis = cardDef?.isCustomDateAnalysis || cardDef?.question?.isCustomDateAnalysis;
									const customAnalysisType = cardDef?.customAnalysisType || cardDef?.question?.customAnalysisType;
									const isValidCombineTab = isCustomDateAnalysis &&
										(customAnalysisType === "date" || customAnalysisType === "time");

									if (!isValidCombineTab) {
										return;
									}
								}

								const titleValue = getTitleValue(cardDef?.title);
								const labelValue = getTitleValue(cardDef?.label);
								const tableLabelValue = getTitleValue(cardDef?.tableLabel);
								const questionLabelValue = getTitleValue(cardDef?.question?.label);
								const questionTitleValue = getTitleValue(cardDef?.question?.title);
								const questionQuestionValue = getTitleValue(cardDef?.question?.question);
								const descriptionValue = getTitleValue(cardDef?.description);

								const cardLabel = titleValue ||
									labelValue ||
									tableLabelValue ||
									questionLabelValue ||
									questionTitleValue ||
									questionQuestionValue ||
									descriptionValue ||
									key;

								pushCard(key, cardLabel, cardDef);
							} else {
								// Try alternative matching strategies
								const keyWithoutSuffixAlt = key.replace(/Customtab$/i, '');
								const cardDefByKey = allDynamicCards.find((c) => c.key === key);
								const cardDefById = allDynamicCards.find((c) => String(c.id || '') === String(key));
								const cardDefBy_Id = allDynamicCards.find((c) => String(c._id || '') === String(key));
								const cardDefCaseInsensitive = allDynamicCards.find((c) =>
									c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()
								);
								const cardDefPartial = allDynamicCards.find((c) =>
									c.accessor && (String(key).includes(String(c.accessor)) || String(c.accessor).includes(String(key)))
								);
								const cardDefWithoutSuffix = allDynamicCards.find((c) =>
									c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixAlt).toLowerCase()
								);

								const alternativeMatch = cardDefByKey || cardDefById || cardDefBy_Id || cardDefCaseInsensitive || cardDefPartial || cardDefWithoutSuffix;

								if (alternativeMatch) {
									const altCardType = alternativeMatch?.type;
									const altQuestionType = alternativeMatch?.question?.type;
									const isCombineTab = altCardType === CUSTOM_TAB_TYPE.combineTab || altQuestionType === CUSTOM_TAB_TYPE.combineTab;

									if (isCombineTab) {
										const isCustomDateAnalysis = alternativeMatch?.isCustomDateAnalysis || alternativeMatch?.question?.isCustomDateAnalysis;
										const customAnalysisType = alternativeMatch?.customAnalysisType || alternativeMatch?.question?.customAnalysisType;
										const isValidCombineTab = isCustomDateAnalysis &&
											(customAnalysisType === "date" || customAnalysisType === "time");

										if (!isValidCombineTab) {
											return;
										}
									}

									const titleValue = getTitleValue(alternativeMatch?.title);
									const labelValue = getTitleValue(alternativeMatch?.label);
									const tableLabelValue = getTitleValue(alternativeMatch?.tableLabel);
									const questionLabelValue = getTitleValue(alternativeMatch?.question?.label);
									const questionTitleValue = getTitleValue(alternativeMatch?.question?.title);
									const questionQuestionValue = getTitleValue(alternativeMatch?.question?.question);
									const descriptionValue = getTitleValue(alternativeMatch?.description);

									const cardLabel = titleValue ||
										labelValue ||
										tableLabelValue ||
										questionLabelValue ||
										questionTitleValue ||
										questionQuestionValue ||
										descriptionValue ||
										key;

									pushCard(key, cardLabel, alternativeMatch);
								} else {
									// Try one more time to find card definition
									const keyWithoutSuffixForAnyCard = key.replace(/Customtab$/i, '');
									const anyCardDef = allDynamicCards.find((c) => {
										return c.accessor === key ||
											c.accessor === keyWithoutSuffixForAnyCard ||
											c.key === key ||
											c.key === keyWithoutSuffixForAnyCard ||
											String(c.id || '') === String(key) ||
											String(c._id || '') === String(key) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixForAnyCard).toLowerCase());
									});

									if (anyCardDef) {
										const anyCardType = anyCardDef?.type;
										const anyQuestionType = anyCardDef?.question?.type;
										const isCombineTab = anyCardType === CUSTOM_TAB_TYPE.combineTab || anyQuestionType === CUSTOM_TAB_TYPE.combineTab;

										if (isCombineTab) {
											const isCustomDateAnalysis = anyCardDef?.isCustomDateAnalysis || anyCardDef?.question?.isCustomDateAnalysis;
											const customAnalysisType = anyCardDef?.customAnalysisType || anyCardDef?.question?.customAnalysisType;
											const isValidCombineTab = isCustomDateAnalysis &&
												(customAnalysisType === "date" || customAnalysisType === "time");

											if (!isValidCombineTab) {
												return;
											}
										}
									}

									const itemCardLabel = firstItem?.cardLabel || firstItem?.cardTitle || firstItem?.cardName;
									const formattedKey = key
										.replace(/Customtab$/i, '')
										.replace(/([A-Z])/g, ' $1')
										.replace(/^./, str => str.toUpperCase())
										.trim();

									const finalLabel = itemCardLabel || formattedKey || key;

									const keyWithoutSuffixForFinal = key.replace(/Customtab$/i, '');
									let finalCardDef = allDynamicCards.find((c) => {
										return c.accessor === key ||
											c.accessor === keyWithoutSuffixForFinal ||
											c.key === key ||
											c.key === keyWithoutSuffixForFinal ||
											String(c.id || '') === String(key) ||
											String(c._id || '') === String(key) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) ||
											(c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixForFinal).toLowerCase());
									});

									if (!finalCardDef && patientListFromRedux?.customTabs) {
										finalCardDef = patientListFromRedux.customTabs.find((c) => {
											return c.accessor === key ||
												c.accessor === keyWithoutSuffixForFinal ||
												c.key === key ||
												c.key === keyWithoutSuffixForFinal ||
												String(c.id || '') === String(key) ||
												String(c._id || '') === String(key) ||
												(c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) ||
												(c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixForFinal).toLowerCase());
										});
									}

									if (finalCardDef) {
										const finalCardType = finalCardDef?.type;
										const finalQuestionType = finalCardDef?.question?.type;
										const isFinalCombineTab = finalCardType === CUSTOM_TAB_TYPE.combineTab || finalQuestionType === CUSTOM_TAB_TYPE.combineTab;

										if (isFinalCombineTab) {
											const isCustomDateAnalysis = finalCardDef?.isCustomDateAnalysis || finalCardDef?.question?.isCustomDateAnalysis;
											const customAnalysisType = finalCardDef?.customAnalysisType || finalCardDef?.question?.customAnalysisType;
											const isValidCombineTab = isCustomDateAnalysis &&
												(customAnalysisType === "date" || customAnalysisType === "time");

											if (!isValidCombineTab) {
												return;
											}
										}
									}

									if (!finalCardDef && /Customtab$/i.test(key)) {
										return;
									}

									pushCard(key, finalLabel, finalCardDef);
								}
							}
						}
					}
				});
			}
		} else if (pageType === "DECEASED") {
			// Standard Deceased cards - include all cards that are rendered in Deceased.js
			// Order matches the renderCard function in Deceased.js
			pushCard(DECEASED_CARDS_TYPE.INSURANCE_DATA);
			pushCard(DECEASED_CARDS_TYPE.DOCTOR_DATA);
			pushCard(DECEASED_CARDS_TYPE.DAYS_DATA);
			pushCard(DECEASED_CARDS_TYPE.FLOORS_DATA);
			pushCard(DECEASED_CARDS_TYPE.NINETY_DAYS_DATA);

			// Add custom tabs (excluding combineTab type)
			const filterListData = dialogFilters?.filterListData || {};
			// Get dynamicCards from dialogFilters first, fallback to Redux
			const dynamicCards = dialogFilters?.dynamicCards || dynamicCardsFromRedux || [];
			// Get patientList from dialogFilters first, fallback to Redux (for customTabs)
			const patientList = dialogFilters?.patientList || patientListFromRedux;
			// Get customTabs from patientList (similar to Deceased.js usage)
			const customTabs = patientList?.customTabs || [];

			if (ENABLE_CUSTOM_CARDS) {
				// Standard card types to exclude from custom tab detection
				const standardCardTypes = new Set([
					DECEASED_CARDS_TYPE.INSURANCE_DATA,
					DECEASED_CARDS_TYPE.DOCTOR_DATA,
					DECEASED_CARDS_TYPE.FLOORS_DATA,
					DECEASED_CARDS_TYPE.DAYS_DATA,
					DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
					DECEASED_CARDS_TYPE.TOTAL,
				]);

				// Merge dynamicCards and customTabs (similar to Hospital/Community)
				const allDynamicCards = [...(dynamicCards || []), ...(customTabs || [])];

				allDynamicCards.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const accessor = card?.accessor;

					// Check if this is a combineTab card
					const cardType = card?.type;
					const questionType = card?.question?.type;
					const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;

					if (isCustomTab && accessor) {
						// For combineTab cards: ONLY include if they meet the criteria
						// (isCustomDateAnalysis && customAnalysisType === "date" OR "time")
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.isCustomDateAnalysis || card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.customAnalysisType || card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");

							if (!isValidCombineTab) {
								return;
							}
						}

						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const titleValue = getTitleValue(card?.title);
							const labelValue = getTitleValue(card?.label);
							const tableLabelValue = getTitleValue(card?.tableLabel);
							const questionLabelValue = getTitleValue(card?.question?.label);
							const questionTitleValue = getTitleValue(card?.question?.title);
							const questionQuestionValue = getTitleValue(card?.question?.question);
							const descriptionValue = getTitleValue(card?.description);

							const cardLabel = titleValue ||
								labelValue ||
								tableLabelValue ||
								questionLabelValue ||
								questionTitleValue ||
								questionQuestionValue ||
								descriptionValue ||
								accessor;

							pushCard(accessor, cardLabel, card);
						}
					}
				});

				// Also process customTabs from patientList that might not be in dynamicCards
				customTabs.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const questionType = card?.question?.type;
					const isCombineTab = questionType === CUSTOM_TAB_TYPE.combineTab;
					const accessor = card?.accessor;

					if (isCustomTab && accessor) {
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");

							if (!isValidCombineTab) {
								return;
							}
						}

						if (list.some((c) => c.id === accessor)) {
							return;
						}

						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const titleValue = getTitleValue(card?.title);
							const labelValue = getTitleValue(card?.label);
							const tableLabelValue = getTitleValue(card?.tableLabel);
							const questionLabelValue = getTitleValue(card?.question?.label);
							const questionTitleValue = getTitleValue(card?.question?.title);
							const questionQuestionValue = getTitleValue(card?.question?.question);
							const descriptionValue = getTitleValue(card?.description);

							const cardLabel = titleValue ||
								labelValue ||
								tableLabelValue ||
								questionLabelValue ||
								questionTitleValue ||
								questionQuestionValue ||
								descriptionValue ||
								accessor;

							pushCard(accessor, cardLabel, card);
						}
					}
				});

				// Fallback: check filterListData for any additional custom tabs
				Object.keys(filterListData).forEach((key) => {
					if (standardCardTypes.has(key)) {
						return;
					}
					if (list.some((card) => card.id === key)) {
						return;
					}

					const data = filterListData[key];
					if (Array.isArray(data) && data.length > 0) {
						const firstItem = data[0];
						if (firstItem && (firstItem._id || firstItem.id || firstItem.name || firstItem.label)) {
							const keyWithoutSuffix = key.replace(/Customtab$/i, '');
							const allCards = [...allDynamicCards];
							const cardDef = allCards.find((c) =>
								c.accessor === key ||
								c.accessor === keyWithoutSuffix ||
								c.key === key ||
								c.key === keyWithoutSuffix
							);

							if (cardDef) {
								const cardTypeDef = cardDef?.type;
								const questionTypeDef = cardDef?.question?.type;
								const isCombineTabDef = cardTypeDef === CUSTOM_TAB_TYPE.combineTab || questionTypeDef === CUSTOM_TAB_TYPE.combineTab;

								if (isCombineTabDef) {
									const isCustomDateAnalysis = cardDef?.isCustomDateAnalysis || cardDef?.question?.isCustomDateAnalysis;
									const customAnalysisType = cardDef?.customAnalysisType || cardDef?.question?.customAnalysisType;
									const isValidCombineTab = isCustomDateAnalysis &&
										(customAnalysisType === "date" || customAnalysisType === "time");

									if (!isValidCombineTab) {
										return;
									}
								}

								const titleValue = getTitleValue(cardDef?.title);
								const labelValue = getTitleValue(cardDef?.label);
								const tableLabelValue = getTitleValue(cardDef?.tableLabel);
								const questionLabelValue = getTitleValue(cardDef?.question?.label);
								const questionTitleValue = getTitleValue(cardDef?.question?.title);
								const questionQuestionValue = getTitleValue(cardDef?.question?.question);
								const descriptionValue = getTitleValue(cardDef?.description);

								const cardLabel = titleValue ||
									labelValue ||
									tableLabelValue ||
									questionLabelValue ||
									questionTitleValue ||
									questionQuestionValue ||
									descriptionValue ||
									key;

								pushCard(key, cardLabel, cardDef);
							}
						}
					}
				});
			}
		} else if (pageType === "ADMISSION") {
			// Standard Admission cards - include all cards that are rendered in Admission.js
			// Order matches the renderCard function in Admission.js
			pushCard(ADMISSION_CARDS_TYPE.AVERAGE_STAY_DAYS_DATA);
			pushCard(ADMISSION_CARDS_TYPE.DOCTOR_DATA);
			pushCard(ADMISSION_CARDS_TYPE.DAYS_DATA);
			pushCard(ADMISSION_CARDS_TYPE.DX_DATA);
			pushCard(ADMISSION_CARDS_TYPE.INSURANCE_DATA);
			pushCard(ADMISSION_CARDS_TYPE.FLOORS_DATA);
			pushCard(ADMISSION_CARDS_TYPE.HOSPITAL_DATA);

			// Add custom tabs (excluding combineTab type)
			const filterListData = dialogFilters?.filterListData || {};
			// Get dynamicCards from dialogFilters first, fallback to Redux
			const dynamicCards = dialogFilters?.dynamicCards || dynamicCardsFromRedux || [];
			// Get patientList from dialogFilters first, fallback to Redux (for customTabs)
			const patientList = dialogFilters?.patientList || patientListFromRedux;
			// Get customTabs from patientList (similar to Admission.js usage)
			const customTabs = patientList?.customTabs || [];

			if (ENABLE_CUSTOM_CARDS) {
				// Standard card types to exclude from custom tab detection
				const standardCardTypes = new Set([
					ADMISSION_CARDS_TYPE.INSURANCE_DATA,
					ADMISSION_CARDS_TYPE.DOCTOR_DATA,
					ADMISSION_CARDS_TYPE.DX_DATA,
					ADMISSION_CARDS_TYPE.DAYS_DATA,
					ADMISSION_CARDS_TYPE.FLOORS_DATA,
					ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
					ADMISSION_CARDS_TYPE.AVERAGE_STAY_DAYS_DATA,
					ADMISSION_CARDS_TYPE.TOTAL,
					ADMISSION_CARDS_TYPE.ADMISSION,
					ADMISSION_CARDS_TYPE.READMISSION,
				]);

				// Merge dynamicCards and customTabs (similar to Hospital/Community/Deceased)
				const allDynamicCards = [...(dynamicCards || []), ...(customTabs || [])];

				allDynamicCards.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const accessor = card?.accessor;
					const cardType = card?.type;
					const questionType = card?.question?.type;
					const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;

					if (isCustomTab && accessor) {
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.isCustomDateAnalysis || card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.customAnalysisType || card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");
							if (!isValidCombineTab) {
								return;
							}
						}
						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const cardLabel = getTitleValue(card?.title) || getTitleValue(card?.label) || getTitleValue(card?.tableLabel) || getTitleValue(card?.question?.label) || getTitleValue(card?.question?.title) || getTitleValue(card?.question?.question) || getTitleValue(card?.description) || accessor;
							pushCard(accessor, cardLabel, card);
						}
					}
				});
				customTabs.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const questionType = card?.question?.type;
					const isCombineTab = questionType === CUSTOM_TAB_TYPE.combineTab;
					const accessor = card?.accessor;
					if (isCustomTab && accessor) {
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");
							if (!isValidCombineTab) {
								return;
							}
						}
						if (list.some((c) => c.id === accessor)) {
							return;
						}
						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const cardLabel = getTitleValue(card?.title) || getTitleValue(card?.label) || getTitleValue(card?.tableLabel) || getTitleValue(card?.question?.label) || getTitleValue(card?.question?.title) || getTitleValue(card?.question?.question) || getTitleValue(card?.description) || accessor;
							pushCard(accessor, cardLabel, card);
						}
					}
				});
				Object.keys(filterListData).forEach((key) => {
					if (standardCardTypes.has(key)) {
						return;
					}
					if (list.some((card) => card.id === key)) {
						return;
					}
					const data = filterListData[key];
					if (Array.isArray(data) && data.length > 0) {
						const firstItem = data[0];
						if (firstItem && (firstItem._id || firstItem.id || firstItem.name || firstItem.label)) {
							const keyWithoutSuffix = key.replace(/Customtab$/i, '');
							const cardDef = allDynamicCards.find((c) =>
								c.accessor === key ||
								c.accessor === keyWithoutSuffix ||
								c.key === key ||
								c.key === keyWithoutSuffix
							);
							if (cardDef) {
								const cardType = cardDef?.type;
								const questionType = cardDef?.question?.type;
								const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;
								if (isCombineTab) {
									const isCustomDateAnalysis = cardDef?.isCustomDateAnalysis || cardDef?.question?.isCustomDateAnalysis;
									const customAnalysisType = cardDef?.customAnalysisType || cardDef?.question?.customAnalysisType;
									const isValidCombineTab = isCustomDateAnalysis &&
										(customAnalysisType === "date" || customAnalysisType === "time");
									if (!isValidCombineTab) {
										return;
									}
								}
								const cardLabel = getTitleValue(cardDef?.title) || getTitleValue(cardDef?.label) || getTitleValue(cardDef?.tableLabel) || getTitleValue(cardDef?.question?.label) || getTitleValue(cardDef?.question?.title) || getTitleValue(cardDef?.question?.question) || getTitleValue(cardDef?.description) || key;
								pushCard(key, cardLabel, cardDef);
							} else {
								const keyWithoutSuffixAlt = key.replace(/Customtab$/i, '');
								const alternativeMatch = allDynamicCards.find((c) => c.key === key) || allDynamicCards.find((c) => String(c.id || '') === String(key)) || allDynamicCards.find((c) => String(c._id || '') === String(key)) || allDynamicCards.find((c) => c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) || allDynamicCards.find((c) => c.accessor && (String(key).includes(String(c.accessor)) || String(c.accessor).includes(String(key)))) || allDynamicCards.find((c) => c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixAlt).toLowerCase());
								if (alternativeMatch) {
									const altCardType = alternativeMatch?.type;
									const altQuestionType = alternativeMatch?.question?.type;
									const isCombineTabAlt = altCardType === CUSTOM_TAB_TYPE.combineTab || altQuestionType === CUSTOM_TAB_TYPE.combineTab;
									if (isCombineTabAlt) {
										const isCustomDateAnalysisAlt = alternativeMatch?.isCustomDateAnalysis || alternativeMatch?.question?.isCustomDateAnalysis;
										const customAnalysisTypeAlt = alternativeMatch?.customAnalysisType || alternativeMatch?.question?.customAnalysisType;
										const isValidCombineTabAlt = isCustomDateAnalysisAlt &&
											(customAnalysisTypeAlt === "date" || customAnalysisTypeAlt === "time");
										if (!isValidCombineTabAlt) {
											return;
										}
									}
									const finalLabel = getTitleValue(alternativeMatch?.title) || getTitleValue(alternativeMatch?.label) || getTitleValue(alternativeMatch?.tableLabel) || getTitleValue(alternativeMatch?.question?.label) || getTitleValue(alternativeMatch?.question?.title) || getTitleValue(alternativeMatch?.question?.question) || getTitleValue(alternativeMatch?.description) || key;
									pushCard(key, finalLabel, alternativeMatch);
								}
							}
						}
					}
				});
			}
		} else if (pageType === "OVERALL") {
			// Standard Overall cards - include all cards that are rendered in Overall.js
			// Order matches the renderCard function in Overall.js
			pushCard(OVERALL_CARDS_TYPE.DOCTOR_DATA);
			pushCard(OVERALL_CARDS_TYPE.FLOORS_DATA);
			pushCard(OVERALL_CARDS_TYPE.INSURANCE_DATA);
			pushCard(OVERALL_CARDS_TYPE.DAYS_DATA);
			pushCard(OVERALL_CARDS_TYPE.NINETY_DAYS_DATA);
			pushCard(OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA);

			// Add custom tabs (excluding combineTab type)
			const filterListData = dialogFilters?.filterListData || {};
			// Get dynamicCards from dialogFilters first, fallback to Redux
			const dynamicCards = dialogFilters?.dynamicCards || dynamicCardsFromRedux || [];
			// Get patientList from dialogFilters first, fallback to Redux (for customTabs)
			const patientList = dialogFilters?.patientList || patientListFromRedux;
			// Get customTabs from patientList (similar to Overall.js usage)
			const customTabs = patientList?.customTabs || [];

			if (ENABLE_CUSTOM_CARDS) {
				// Standard card types to exclude from custom tab detection
				const standardCardTypes = new Set([
					OVERALL_CARDS_TYPE.INSURANCE_DATA,
					OVERALL_CARDS_TYPE.DOCTOR_DATA,
					OVERALL_CARDS_TYPE.DAYS_DATA,
					OVERALL_CARDS_TYPE.FLOORS_DATA,
					OVERALL_CARDS_TYPE.NINETY_DAYS_DATA,
					OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA,
					OVERALL_CARDS_TYPE.TOTAL,
					OVERALL_CARDS_TYPE.TOTAL_INCOMING,
					OVERALL_CARDS_TYPE.TOTAL_OUTGOING,
				]);

				// Merge dynamicCards and customTabs (similar to Hospital/Community/Deceased/Admission)
				const allDynamicCards = [...(dynamicCards || []), ...(customTabs || [])];

				allDynamicCards.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const accessor = card?.accessor;
					const cardType = card?.type;
					const questionType = card?.question?.type;
					const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;

					if (isCustomTab && accessor) {
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.isCustomDateAnalysis || card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.customAnalysisType || card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");
							if (!isValidCombineTab) {
								return;
							}
						}
						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const cardLabel = getTitleValue(card?.title) || getTitleValue(card?.label) || getTitleValue(card?.tableLabel) || getTitleValue(card?.question?.label) || getTitleValue(card?.question?.title) || getTitleValue(card?.question?.question) || getTitleValue(card?.description) || accessor;
							pushCard(accessor, cardLabel, card);
						}
					}
				});
				customTabs.forEach((card) => {
					const isCustomTab = card?.isCustomTab === true;
					const questionType = card?.question?.type;
					const isCombineTab = questionType === CUSTOM_TAB_TYPE.combineTab;
					const accessor = card?.accessor;
					if (isCustomTab && accessor) {
						if (isCombineTab) {
							const isCustomDateAnalysis = card?.question?.isCustomDateAnalysis;
							const customAnalysisType = card?.question?.customAnalysisType;
							const isValidCombineTab = isCustomDateAnalysis &&
								(customAnalysisType === "date" || customAnalysisType === "time");
							if (!isValidCombineTab) {
								return;
							}
						}
						if (list.some((c) => c.id === accessor)) {
							return;
						}
						const customTabData = filterListData[accessor];
						if (customTabData && Array.isArray(customTabData) && customTabData.length > 0) {
							const cardLabel = getTitleValue(card?.title) || getTitleValue(card?.label) || getTitleValue(card?.tableLabel) || getTitleValue(card?.question?.label) || getTitleValue(card?.question?.title) || getTitleValue(card?.question?.question) || getTitleValue(card?.description) || accessor;
							pushCard(accessor, cardLabel, card);
						}
					}
				});
				Object.keys(filterListData).forEach((key) => {
					if (standardCardTypes.has(key)) {
						return;
					}
					if (list.some((card) => card.id === key)) {
						return;
					}
					const data = filterListData[key];
					if (Array.isArray(data) && data.length > 0) {
						const firstItem = data[0];
						if (firstItem && (firstItem._id || firstItem.id || firstItem.name || firstItem.label)) {
							const keyWithoutSuffix = key.replace(/Customtab$/i, '');
							const cardDef = allDynamicCards.find((c) =>
								c.accessor === key ||
								c.accessor === keyWithoutSuffix ||
								c.key === key ||
								c.key === keyWithoutSuffix
							);
							if (cardDef) {
								const cardType = cardDef?.type;
								const questionType = cardDef?.question?.type;
								const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;
								if (isCombineTab) {
									const isCustomDateAnalysis = cardDef?.isCustomDateAnalysis || cardDef?.question?.isCustomDateAnalysis;
									const customAnalysisType = cardDef?.customAnalysisType || cardDef?.question?.customAnalysisType;
									const isValidCombineTab = isCustomDateAnalysis &&
										(customAnalysisType === "date" || customAnalysisType === "time");
									if (!isValidCombineTab) {
										return;
									}
								}
								const cardLabel = getTitleValue(cardDef?.title) || getTitleValue(cardDef?.label) || getTitleValue(cardDef?.tableLabel) || getTitleValue(cardDef?.question?.label) || getTitleValue(cardDef?.question?.title) || getTitleValue(cardDef?.question?.question) || getTitleValue(cardDef?.description) || key;
								pushCard(key, cardLabel, cardDef);
							} else {
								const keyWithoutSuffixAlt = key.replace(/Customtab$/i, '');
								const alternativeMatch = allDynamicCards.find((c) => c.key === key) || allDynamicCards.find((c) => String(c.id || '') === String(key)) || allDynamicCards.find((c) => String(c._id || '') === String(key)) || allDynamicCards.find((c) => c.accessor && String(c.accessor).toLowerCase() === String(key).toLowerCase()) || allDynamicCards.find((c) => c.accessor && (String(key).includes(String(c.accessor)) || String(c.accessor).includes(String(key)))) || allDynamicCards.find((c) => c.accessor && String(c.accessor).toLowerCase() === String(keyWithoutSuffixAlt).toLowerCase());
								if (alternativeMatch) {
									const altCardType = alternativeMatch?.type;
									const altQuestionType = alternativeMatch?.question?.type;
									const isCombineTabAlt = altCardType === CUSTOM_TAB_TYPE.combineTab || altQuestionType === CUSTOM_TAB_TYPE.combineTab;
									if (isCombineTabAlt) {
										const isCustomDateAnalysisAlt = alternativeMatch?.isCustomDateAnalysis || alternativeMatch?.question?.isCustomDateAnalysis;
										const customAnalysisTypeAlt = alternativeMatch?.customAnalysisType || alternativeMatch?.question?.customAnalysisType;
										const isValidCombineTabAlt = isCustomDateAnalysisAlt &&
											(customAnalysisTypeAlt === "date" || customAnalysisTypeAlt === "time");
										if (!isValidCombineTabAlt) {
											return;
										}
									}
									const finalLabel = getTitleValue(alternativeMatch?.title) || getTitleValue(alternativeMatch?.label) || getTitleValue(alternativeMatch?.tableLabel) || getTitleValue(alternativeMatch?.question?.label) || getTitleValue(alternativeMatch?.question?.title) || getTitleValue(alternativeMatch?.question?.question) || getTitleValue(alternativeMatch?.description) || key;
									pushCard(key, finalLabel, alternativeMatch);
								}
							}
						}
					}
				});
			}
		}

		// Append compare dashboard cards/items for all other page types.
		otherDashboardIdsForCompare.forEach((dashboardId) => {
			const compareResult = compareDashboardData?.[dashboardId];
			if (!compareResult?.cards?.length) return;
			const dashboardLabel = DASHBOARD_OPTIONS.find((d) => d.id === dashboardId)?.label || dashboardId;
			const compareItems = compareResult.items || [];

			const sortedCards = [...compareResult.cards].sort((a, b) => {
				const aIsTotal = TOTAL_CARD_VALUES.has(a.value);
				const bIsTotal = TOTAL_CARD_VALUES.has(b.value);
				if (aIsTotal && !bIsTotal) return -1;
				if (!aIsTotal && bIsTotal) return 1;
				if (aIsTotal && bIsTotal) {
					const order = [
						"total",
						"unplannedHospitalTransfer",
						"plannedHospitalTransfer",
						"AMA",
						"SNF",
						"safeDischarge",
						"admission",
						"readmission",
					];
					return order.indexOf(a.value) - order.indexOf(b.value);
				}
				return 0;
			});

			sortedCards.forEach((card) => {
				const cardId = `compare__${dashboardId}__${card.value}`;
				const cardItems = compareItems
					.filter((item) => String(item.parentId) === String(card.value))
					.map((item) => ({
						_id: item.value,
						label: item.label,
						name: item.label,
						parentId: cardId,
					}));

				list.push({
					id: cardId,
					label: card.label,
					items: cardItems,
					isCompareDashboard: true,
					dashboard: dashboardId,
					dashboardLabel: dashboardLabel,
					originalCardId: card.value,
				});
			});
		});

		// Filter out cards that are being used as filters based on filterMode
		const filterMode = config.filterMode || "keepAll";
		const cardFilter = dialogFilters?.cardFilter || {};

		// Get current card type - always include it even if used as filter
		const currentCardTypeValue = currentCardType || dialogFilters?.type;

		// Helper to check if a card type should be excluded from the list
		// CRITICAL: For display logic, only exclude:
		// 1. Current card itself (it's shown in preview)
		// Cards BEFORE and AFTER current card in priorityData should be visible (they're filters, but shown for reference)
		// This is different from analysis logic which excludes cards AFTER current card
		// This ensures consistency with user requirements and works for all pages (hospital, admission, etc.)
		const isCardUsedAsFilter = (cardId) => {
			// Always exclude the current card from being treated as a filter (it's shown in preview)
			if (cardId === currentCardTypeValue) {
				return true; // Exclude current card from list
			}

			// Check priorityData/mainPriorityData arrays
			const priorityData = cardFilter.priorityData || [];
			const mainPriorityData = cardFilter.mainPriorityData || [];

			// CRITICAL: For display logic, if current card is in priorityData/mainPriorityData,
			// we only exclude the current card itself (already handled above)
			// All other cards in priorityData/mainPriorityData should remain visible (they're filters, but shown for reference)
			// This is different from analysis logic which excludes cards AFTER current card
			if (currentCardTypeValue) {
				// Check if current card is in priorityData
				if (Array.isArray(priorityData) && priorityData.length > 0) {
					const currentCardIndex = priorityData.findIndex(item => item && item.type === currentCardTypeValue);
					if (currentCardIndex >= 0) {
						// Current card is in priorityData - check if this card is also in priorityData
						const cardIndex = priorityData.findIndex(item => item && item.type === cardId);
						if (cardIndex >= 0) {
							// Card is in priorityData - keep it visible (don't exclude)
							return false;
						}
					}
				}

				// Check if current card is in mainPriorityData (admission page)
				if (Array.isArray(mainPriorityData) && mainPriorityData.length > 0) {
					const currentCardIndex = mainPriorityData.findIndex(item => item && item.type === currentCardTypeValue);
					if (currentCardIndex >= 0) {
						const cardIndex = mainPriorityData.findIndex(item => item && item.type === cardId);
						if (cardIndex >= 0) {
							// Card is in mainPriorityData - keep it visible (don't exclude)
							return false;
						}
					}
				}
			}

			// Check regular cardFilter (if not in priorityData or current card not in priorityData)
			// Only exclude if it's actually being used as a filter (has values)
			// This works for all card types (standard and custom) and all pages
			const filterValue = cardFilter[cardId];
			if (filterValue && Array.isArray(filterValue) && filterValue.length > 0) {
				// Card is a filter but not in priorityData/mainPriorityData - exclude it
				return true;
			}

			// Check if card is in priorityData/mainPriorityData (when current card is not in priorityData)
			// In this case, exclude all cards in priorityData/mainPriorityData as they're filters
			// This ensures consistency across all pages
			if (!currentCardTypeValue ||
				(Array.isArray(priorityData) && priorityData.length > 0 && !priorityData.some(item => item && item.type === currentCardTypeValue)) ||
				(Array.isArray(mainPriorityData) && mainPriorityData.length > 0 && !mainPriorityData.some(item => item && item.type === currentCardTypeValue))) {
				// Current card is NOT in priorityData/mainPriorityData - exclude all cards in priorityData/mainPriorityData
				const inPriorityData = Array.isArray(priorityData) && priorityData.some(item => item && item.type === cardId);
				const inMainPriorityData = Array.isArray(mainPriorityData) && mainPriorityData.some(item => item && item.type === cardId);

				return inPriorityData || inMainPriorityData;
			}

			return false;
		};

		if (filterMode === "keepAll") {
			// Exclude all cards that are in cardFilter and have values (including priorityData/mainPriorityData)
			// BUT always include the current card
			return list.filter((card) => {
				// Always include the current card
				if (card.id === currentCardTypeValue) {
					return true;
				}

				// Also exclude priorityData and mainPriorityData as they're containers
				if (card.id === 'priorityData' || card.id === 'mainPriorityData') {
					return true; // Keep these if they exist, but they shouldn't be in the list anyway
				}
				return !isCardUsedAsFilter(card.id);
			});
		} else if (filterMode === "keepSome") {
			// Exclude only cards that are in selectedFilterCards
			// BUT always include the current card
			const selectedFilterCards = new Set(config.selectedFilterCards || []);
			return list.filter((card) => {
				// Always include the current card
				if (card.id === currentCardTypeValue) {
					return true;
				}
				// Exclude if this card is in the selected filters list
				return !selectedFilterCards.has(card.id);
			});
		} else {
			// "removeAll" - include all cards (no exclusions)
			return list;
		}
	}, [pageType, dialogFilters, cardItemsMap, dynamicCardsFromRedux, patientListFromRedux, config.filterMode, config.selectedFilterCards, currentCardType, compareDashboardData, otherDashboardIdsForCompare, currentDashboardLabel]);

	const fetchCompareDashboardOptions = useCallback(async (dashboardId) => {
		const cards = [];
		const items = [];
		const configForDashboard = COMPARE_PAGE_CONFIG[dashboardId];
		if (!configForDashboard) return { cards, items };

		const filter = dialogFilters?.filter || {};
		const updatedFilter = {
			...filter,
			facilityIds: activeFacilities,
			isSkipCustomCard: true,
			startDate: filter.startDate,
			endDate: filter.endDate,
		};

		const isOverallCompareDashboard =
			dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING ||
			dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING;
		// Use comparison channel + correct transfer type so we don't clobber main Overall Redux
		// and so updateFilterListData sees a real transferType (never [] — arrays are truthy and break logic).
		const res = isOverallCompareDashboard
			? await getAllOverallPatient(updatedFilter, true, dashboardId)
			: await configForDashboard.getCards(updatedFilter);
		const query = { page: selectedTab, ...configForDashboard.query };
		const dynamicCardsData = await getQuestionsData(query);
		const dynamicCardsDataArr = dynamicCardsData?.filter((item) => !item.isDefaultCard) ?? [];
		const dynamicCards = dynamicCardsDataArr?.filter((item) => !item.existingAccessor) ?? [];

		const labelMap = COMPARE_LABEL_MAP[dashboardId] ?? {};
		const cardFilter = { ...(configForDashboard.cardFilter || {}) };

		let resultData;
		if (dashboardId === "ADMISSION") {
			delete cardFilter.averageStayDaysData;
			resultData = await configForDashboard.updateFn(
				{ ...res?.data, dynamicCards },
				cardFilter,
				null,
				cardFilter?.mainPriorityData,
				[],
				false,
			);
			if (resultData?.patientList?.averageStayDaysData) {
				delete resultData.patientList.averageStayDaysData;
			}
		} else if (dashboardId === "HOSPITAL") {
			resultData = await configForDashboard.updateFn(
				cardFilter,
				null,
				{ ...res, dynamicCards },
				cardFilter?.priorityData,
				false,
				res?.defaultCardShiftData ? [res?.defaultCardShiftData] : [],
			);
		} else if (dashboardId === "DECEASED") {
			resultData = await configForDashboard.updateFn(
				cardFilter,
				{ ...res?.data, dynamicCards },
				cardFilter?.priorityData,
				false,
			);
		} else if (
			dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING ||
			dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING
		) {
			const transferForOverallCompare =
				dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING
					? OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING
					: OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING;
			resultData = await configForDashboard.updateFn(
				cardFilter,
				{ ...res, dynamicCards },
				cardFilter?.priorityData,
				transferForOverallCompare,
				true,
			);
		} else {
			resultData = await configForDashboard.updateFn(
				cardFilter,
				{ ...res?.data, dynamicCards },
				cardFilter?.priorityData,
				[],
				false,
			);
		}

		const latestListData = dashboardId === "ADMISSION" ? resultData?.patientList : resultData;
		if (!latestListData) return { cards, items };

		for (const [key, value] of Object.entries(latestListData)) {
			if (key === "pageConverterFilteredPatients") continue;
			if (!Array.isArray(value) || /_?customtab/i.test(key)) continue;

			if (dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING && key === OVERALL_CARDS_TYPE.NINETY_DAYS_DATA) {
				continue;
			}
			if (
				(dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING ||
					dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING) &&
				key === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA
			) {
				continue;
			}

			const dynamicCard = dynamicCards.find((dc) => dc.accessor === key);
			const label = dynamicCard?.label ?? dynamicCard?.tableLabel ?? labelMap[key] ?? key;

			cards.push({
				value: key,
				label,
				questionId: dynamicCard?._id ?? null,
				dataPoint: dashboardId,
			});

			for (const ele of value) {
				if (ele?.isSpacialItem) continue;
				const normalizedItemId =
					ele?._id ??
					ele?.id ??
					ele?.key ??
					ele?.name ??
					ele?.label ??
					ele?.value ??
					ele?.insuranceId ??
					ele?.doctorId ??
					ele?.floorId ??
					(ele?.day != null && ele?.day !== "" ? String(ele.day) : undefined);
				if (!normalizedItemId) continue;
				items.push({
					value: normalizedItemId,
					label: ele?.label ?? ele?.name ?? ele?.title,
					parentId: key,
					questionId: dynamicCard?._id ?? null,
				});
			}
		}

		// Append total/header cards for comparison dashboards
		const mappedPageType = DASHBOARD_ID_TO_PAGE_TYPE[dashboardId];
		const totalCards = mappedPageType ? CARD_CONFIG[mappedPageType] : null;
		if (totalCards) {
			totalCards.forEach((card) => {
				cards.push({
					value: card.value,
					label: card.label,
					questionId: null,
					dataPoint: dashboardId,
				});

				// Each total card acts as a single item itself
				items.push({
					value: card.value,
					label: card.label,
					parentId: card.value,
					questionId: null,
				});
			});
		}

		let compareTransferType = null;
		if (dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING) {
			compareTransferType = OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING;
		} else if (dashboardId === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING) {
			compareTransferType = OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING;
		}

		const patientsOverride =
			res?.data?.list ||
			res?.list ||
			res?.data?.patientList?.list ||
			res?.patientList?.list ||
			[];

		return {
			cards,
			items,
			analysisDialogFilters: {
				filter: {
					startDate: filter.startDate,
					endDate: filter.endDate,
				},
				cardFilter: {},
				filterListData: latestListData || {},
				dynamicCards,
				patientList: dashboardId === "ADMISSION" ? resultData?.patientList : resultData,
				transferType: compareTransferType,
			},
			patientsOverride: Array.isArray(patientsOverride) ? patientsOverride : [],
			analyzerPageType: PAGE_TYPE_TO_ANALYZER[dashboardId] || null,
		};
	}, [activeFacilities, dialogFilters?.filter, selectedTab]);

	useEffect(() => {
		if (!open || otherDashboardIdsForCompare.length === 0) return;

		let isMounted = true;
		const filter = dialogFilters?.filter || {};
		const cacheKeySuffix = `${filter?.startDate || ""}_${filter?.endDate || ""}`;

		const loadCompareDashboards = async () => {
			await Promise.all(
				otherDashboardIdsForCompare.map(async (dashboardId) => {
					if (dashboardId === pageType) return;

					const cacheKey = `${dashboardId}__${cacheKeySuffix}`;
					if (compareDashboardCache?.[cacheKey]) {
						if (!isMounted) return;
						setCompareDashboardData((prev) => ({
							...prev,
							[dashboardId]: compareDashboardCache[cacheKey],
						}));
						// Ensure chip loader is cleared when serving from cache.
						setCompareDashboardLoading((prev) => ({ ...prev, [dashboardId]: false }));
						return;
					}

					setCompareDashboardLoading((prev) => ({ ...prev, [dashboardId]: true }));
					try {
						const fetched = await fetchCompareDashboardOptions(dashboardId);
						if (!isMounted) return;
						setCompareDashboardData((prev) => ({ ...prev, [dashboardId]: fetched }));
						setCompareDashboardCache((prev) => ({ ...prev, [cacheKey]: fetched }));
					} catch (error) {
						if (!isMounted) return;
						setCompareDashboardData((prev) => ({ ...prev, [dashboardId]: { cards: [], items: [] } }));
					} finally {
						// Always clear loading even if this effect run got cleaned up.
						setCompareDashboardLoading((prev) => ({ ...prev, [dashboardId]: false }));
					}
				}),
			);
		};

		loadCompareDashboards();
		return () => {
			isMounted = false;
		};
	}, [open, otherDashboardIdsForCompare, pageType, dialogFilters?.filter, fetchCompareDashboardOptions]); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle results filter if it points at a dashboard that is not in the compare set
	useEffect(() => {
		if (dashboardFilter !== "all" && dashboardFilter !== "CURRENT" && !otherDashboardIdsForCompare.includes(dashboardFilter)) {
			setDashboardFilter("all");
		}
	}, [otherDashboardIdsForCompare, dashboardFilter]);

	// Cards pool tab must be CURRENT or another known dashboard type (not current page)
	useEffect(() => {
		if (compareCardsPoolTab !== "CURRENT" && !otherDashboardIdsForCompare.includes(compareCardsPoolTab)) {
			setCompareCardsPoolTab("CURRENT");
		}
	}, [otherDashboardIdsForCompare, compareCardsPoolTab]);

	// Helper to get item identifier (handles all formats for custom and standard cards)
	const getItemId = useCallback((item) => {
		return item._id || item.id || item.key || item.name || item.label || item.value;
	}, []);

	// Helper to filter series by multiple date ranges
	// Accepts currentFilterType to handle Monthly and 30 Days correctly
	// Works for all card types including ninetyDaysData, custom cards, etc.
	const filterSeriesByMultipleRanges = useCallback((series, ranges, filterType = null) => {
		// Defensive checks: ensure inputs are valid
		if (!Array.isArray(series) || series.length === 0) return series;
		if (!Array.isArray(ranges) || ranges.length === 0) return series; // No filter applied

		return series.filter((point) => {
			// Defensive check: ensure point is an object
			if (!point || typeof point !== 'object') return true; // Keep invalid points

			// Prefer rawDate/x (canonical bucket date) when available so
			// isolation works correctly for daily/weekly/monthly/7/30 views.
			// This works for all card types as buildSeriesForGroupType always includes these fields
			const pointDate = point.rawDate || point.x || point.date || point.z;
			if (!pointDate) return true; // Keep points without dates (defensive: some cards might not have dates)

			// Try to parse the date - handle various formats and types
			let parsedDate = null;
			try {
				if (typeof pointDate === 'string') {
					// Try strict parsing first (handles MM-DD-YYYY, YYYY-MM-DD, etc.)
					parsedDate = moment(pointDate, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD", "MMM DD, YYYY"], true);
					if (!parsedDate.isValid()) {
						// Fallback to loose parsing
						parsedDate = moment(pointDate);
					}
				} else if (pointDate instanceof Date) {
					parsedDate = moment(pointDate);
				} else if (typeof pointDate === 'number') {
					// Handle timestamp
					parsedDate = moment(pointDate);
				}
			} catch (error) {
				// If parsing fails, keep the point (defensive)
				return true;
			}

			if (!parsedDate || !parsedDate.isValid()) {
				return true; // Keep points with unparseable dates (defensive)
			}

			// Check if date is within any of the ranges
			return ranges.some(range => {
				// Defensive check: ensure range is valid
				if (!range || typeof range !== 'object' || !range.start || !range.end) return false;

				try {
					const rangeStart = moment(range.start);
					const rangeEnd = moment(range.end);

					// Defensive check: ensure range dates are valid
					if (!rangeStart.isValid() || !rangeEnd.isValid()) return false;

					// Handle different filter types with appropriate comparison logic
					if (filterType === CHART_FILTER_DAY_OF.MONTH) {
						// For Monthly: rawDate is the start of the month
						// Compare by month/year - point's month should overlap with range's months
						const pointMonth = parsedDate.format('YYYY-MM');
						const rangeStartMonth = rangeStart.format('YYYY-MM');
						const rangeEndMonth = rangeEnd.format('YYYY-MM');
						return pointMonth >= rangeStartMonth && pointMonth <= rangeEndMonth;
					} else if (filterType === CHART_FILTER_DAY_OF.THIRTY) {
						// For 30 Days: rawDate is the end date of the 30-day bucket
						// The bucket spans from (rawDate - 29 days) to rawDate
						// Check if the bucket overlaps with the range
						const bucketStart = parsedDate.clone().subtract(29, 'days');
						const bucketEnd = parsedDate.clone();
						// Bucket overlaps if: bucketStart <= rangeEnd AND bucketEnd >= rangeStart
						return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
					} else if (filterType === CHART_FILTER_DAY_OF.SEVEN) {
						// For 7 Days: rawDate is the end date of the 7-day bucket
						// The bucket spans from (rawDate - 6 days) to rawDate
						// Check if the bucket overlaps with the range
						const bucketStart = parsedDate.clone().subtract(6, 'days');
						const bucketEnd = parsedDate.clone();
						// Bucket overlaps if: bucketStart <= rangeEnd AND bucketEnd >= rangeStart
						return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
					} else if (filterType === CHART_FILTER_DAY_OF.WEEK) {
						// For Weekly: rawDate is the start of the week
						// Compare by week boundaries
						const pointWeekStart = parsedDate.clone().startOf('week');
						const pointWeekEnd = parsedDate.clone().endOf('week');
						// Week overlaps if: pointWeekStart <= rangeEnd AND pointWeekEnd >= rangeStart
						return pointWeekStart.isSameOrBefore(rangeEnd, 'day') && pointWeekEnd.isSameOrAfter(rangeStart, 'day');
					} else {
						// For Daily and others: use day-level comparison
						return parsedDate.isSameOrAfter(rangeStart, 'day') &&
							parsedDate.isSameOrBefore(rangeEnd, 'day');
					}
				} catch (error) {
					// If comparison fails, exclude the point (defensive)
					return false;
				}
			});
		});
	}, []);

	// Filter availableCards based on search term (for cards and items search)
	const filteredAvailableCards = useMemo(() => {
		if (!cardItemSearchTerm.trim()) {
			return availableCards;
		}

		const searchLower = cardItemSearchTerm.toLowerCase().trim();

		return availableCards
			.map((card) => {
				// Filter out isReadOnly items first
				const nonReadOnlyItems = card.items?.filter(item => !item.isReadOnly) || [];

				// Check if card label or dashboard label matches
				const cardLabelMatches =
					card.label?.toLowerCase().includes(searchLower) ||
					card.dashboardLabel?.toLowerCase().includes(searchLower);

				// Check if any item matches
				const matchingItems = nonReadOnlyItems.filter((item) => {
					const itemId = getItemId(item);
					const itemLabel = (item.name || item.label || item.title || itemId || "").toLowerCase();
					return itemLabel.includes(searchLower);
				});

				// If card label matches, show all non-readonly items
				// If only items match, show card with only matching items
				if (cardLabelMatches) {
					return {
						...card,
						items: nonReadOnlyItems, // Show all non-readonly items when card matches
					};
				} else if (matchingItems.length > 0) {
					return {
						...card,
						items: matchingItems, // Show only matching non-readonly items
					};
				}
				return null; // Card doesn't match
			})
			.filter(Boolean); // Remove null entries
	}, [availableCards, cardItemSearchTerm, getItemId]);

	const comparePoolTabs = useMemo(() => {
		const tabs = [{ id: "CURRENT", label: currentDashboardLabel }];
		otherDashboardIdsForCompare.forEach((id) => {
			tabs.push({
				id,
				label: DASHBOARD_OPTIONS.find((d) => d.id === id)?.label || id,
			});
		});
		return tabs;
	}, [currentDashboardLabel, otherDashboardIdsForCompare]);

	const comparePoolScopedFilteredCards = useMemo(
		() => filteredAvailableCards.filter((card) => card.dashboard === compareCardsPoolTab),
		[filteredAvailableCards, compareCardsPoolTab]
	);

	const comparePoolScopedAvailableCards = useMemo(
		() => availableCards.filter((card) => card.dashboard === compareCardsPoolTab),
		[availableCards, compareCardsPoolTab]
	);

	const getComparePoolTabSummary = useCallback(
		(poolId) => {
			const poolCards = availableCards.filter((c) => c.dashboard === poolId);
			const selectedInPool = poolCards.filter((c) => (config.selectedCards || []).includes(c.id));
			const lines = selectedInPool.map((card) => {
				const idSet = new Set((config.selectedItemsByCard?.[card.id] || []).map((x) => String(x)));
				const names = (card.items || [])
					.filter((i) => !i.isReadOnly && idSet.has(String(getItemId(i))))
					.map((i) => i.name || i.label || i.title || getItemId(i));
				const itemStr = names.length
					? ` (${names.slice(0, 12).join(", ")}${names.length > 12 ? "…" : ""})`
					: "";
				return `• ${card.label}${itemStr}`;
			});
			return { selectedCardCount: selectedInPool.length, lines };
		},
		[availableCards, config.selectedCards, config.selectedItemsByCard, getItemId]
	);

	const filterAnalysisResultRows = useCallback(
		(allMatches, dashFilter) => {
			let filteredMatches = Array.isArray(allMatches) ? [...allMatches] : [];
			if (dashFilter !== "all") {
				filteredMatches = filteredMatches.filter((row) => row.dashboardId === dashFilter);
			}
			filteredMatches = filteredMatches.filter((row) => {
				if (row.isGroup) {
					return true;
				}
				if (!row.itemId) {
					return true;
				}
				const card = availableCards.find((c) => String(c.id) === String(row.cardId));
				if (!card || !card.items) {
					return true;
				}
				const item = card.items.find((i) => {
					const itemId = getItemId(i);
					return String(itemId) === String(row.itemId);
				});
				return !item || !item.isReadOnly;
			});
			if (searchTerm.trim()) {
				const searchLower = searchTerm.toLowerCase().trim();
				filteredMatches = filteredMatches.filter((row) => {
					const cardLabel = (row.cardLabel || "").toLowerCase();
					const itemLabel = (row.itemLabel || "").toLowerCase();
					return cardLabel.includes(searchLower) || itemLabel.includes(searchLower);
				});
			}
			const minTotal = minimumTotalFilter === "" ? 0 : Number(minimumTotalFilter || 0);
			if (minTotal > 0) {
				filteredMatches = filteredMatches.filter((row) => {
					const totalCount = Number(row.totalCount || 0);
					return totalCount >= minTotal;
				});
			}
			return filteredMatches;
		},
		[availableCards, getItemId, searchTerm, minimumTotalFilter]
	);

	// Helper to get human readable label for current filter type (Weekly, Monthly, 7 Days, etc.)
	const getFilterTypeLabel = (filterType) => {
		switch (filterType) {
			case CHART_FILTER_DAY_OF.DAY:
				return "Daily";
			case CHART_FILTER_DAY_OF.WEEK:
				return "Weekly";
			case CHART_FILTER_DAY_OF.MONTH:
				return "Monthly";
			case CHART_FILTER_DAY_OF.SEVEN:
				return "7 Days";
			case CHART_FILTER_DAY_OF.THIRTY:
				return "30 Days";
			default:
				return "Weekly";
		}
	};

	// Helper to get week start date (Monday of the week)
	const getWeekStart = (date) => {
		const d = new Date(date);
		const day = d.getDay();
		const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
		return new Date(d.setDate(diff));
	};

	// Helper to format week range as "(MM-DD)-(MM-DD)" to match main chart format
	const formatWeekRange = (weekStart) => {
		const start = new Date(weekStart);
		const end = new Date(weekStart);
		end.setDate(end.getDate() + 6);

		const formatDate = (d) => {
			const month = String(d.getMonth() + 1).padStart(2, '0');
			const day = String(d.getDate()).padStart(2, '0');
			return `${month}-${day}`;
		};

		return `(${formatDate(start)})-(${formatDate(end)})`;
	};

	// Helper to group dates by week, including zero-value weeks between min/max (to match main chart)
	// NOTE: Currently unused - grouping is handled by chartGroupBy from charts-common.js
	// Keeping for potential future use or reference
	// eslint-disable-next-line no-unused-vars
	const groupByWeek = (dateGroups) => {
		const weekGroups = {};

		// First, aggregate counts per week
		Object.keys(dateGroups).forEach((dateStr) => {
			const date = new Date(dateStr);
			if (isNaN(date.getTime())) return;

			const weekStart = getWeekStart(date);
			const weekKey = weekStart.toISOString().split('T')[0];
			const weekLabel = formatWeekRange(weekStart);

			if (!weekGroups[weekKey]) {
				weekGroups[weekKey] = {
					label: weekLabel,
					total: 0,
					weekStart,
				};
			}
			weekGroups[weekKey].total += dateGroups[dateStr];
		});

		const weekKeys = Object.keys(weekGroups).sort();
		if (weekKeys.length === 0) return [];

		// Build a continuous range of weeks from min to max, filling missing weeks with 0
		const firstWeekStart = new Date(weekKeys[0]);
		const lastWeekStart = new Date(weekKeys[weekKeys.length - 1]);

		const result = [];
		for (let d = new Date(firstWeekStart); d <= lastWeekStart; d.setDate(d.getDate() + 7)) {
			const key = d.toISOString().split('T')[0];
			const existing = weekGroups[key];
			const label = existing ? existing.label : formatWeekRange(d);
			const total = existing ? existing.total : 0;

			result.push({
				date: label,
				total,
			});
		}

		return result;
	};

	// Helper to group dates by month
	// NOTE: Currently unused - grouping is handled by chartGroupBy from charts-common.js
	// Keeping for potential future use or reference
	// eslint-disable-next-line no-unused-vars
	const groupByMonth = (dateGroups) => {
		const monthGroups = {};

		Object.keys(dateGroups).forEach((dateStr) => {
			const date = new Date(dateStr);
			if (isNaN(date.getTime())) return;

			const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
			const monthLabel = date.toLocaleString('default', { month: 'long' });

			if (!monthGroups[monthKey]) {
				monthGroups[monthKey] = {
					label: monthLabel,
					total: 0,
					date: new Date(date.getFullYear(), date.getMonth(), 1),
				};
			}
			monthGroups[monthKey].total += dateGroups[dateStr];
		});

		const monthKeys = Object.keys(monthGroups).sort();
		if (monthKeys.length === 0) return [];

		// Build a continuous range of months
		const firstMonth = new Date(monthKeys[0] + '-01');
		const lastMonth = new Date(monthKeys[monthKeys.length - 1] + '-01');

		const result = [];
		for (let d = new Date(firstMonth); d <= lastMonth; d.setMonth(d.getMonth() + 1)) {
			const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
			const existing = monthGroups[key];
			const label = existing ? existing.label : d.toLocaleString('default', { month: 'long' });
			const total = existing ? existing.total : 0;

			result.push({
				date: label,
				total,
			});
		}

		return result;
	};

	// Helper to group dates by day
	// NOTE: Currently unused - grouping is handled by chartGroupBy from charts-common.js
	// Keeping for potential future use or reference
	// eslint-disable-next-line no-unused-vars
	const groupByDay = (dateGroups) => {
		return Object.keys(dateGroups)
			.sort()
			.map((dateStr) => {
				const date = new Date(dateStr);
				const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
				return {
					date: label,
					total: dateGroups[dateStr],
				};
			});
	};

	// Helper to get period end dates (matching get30DaysFromDates logic from charts-common.js)
	const getPeriodEndDates = (startDate, endDate, daysBack) => {
		const dates = [];
		let interimEndDate = new Date(endDate);
		interimEndDate.setHours(0, 0, 0, 0);

		const start = new Date(startDate);
		start.setHours(0, 0, 0, 0);

		// Start from end date and work backwards (matching get30DaysFromDates exactly)
		while (start <= interimEndDate) {
			if (dates.length === 0) {
				// First period ends on endDate
				dates.push(new Date(interimEndDate));
			} else {
				// Subtract 1 day first, then push
				interimEndDate.setDate(interimEndDate.getDate() - 1);
				if (interimEndDate >= start) {
					dates.push(new Date(interimEndDate));
				} else {
					break; // Stop if we've gone before start date
				}
			}
			// Then subtract daysBack days (matching: interimEndDate.subtract(days, "days"))
			interimEndDate.setDate(interimEndDate.getDate() - daysBack);
		}

		return dates.reverse(); // Reverse to get chronological order
	};

	// Helper to group dates by 7-day or 30-day periods (matching main chart logic exactly)
	// NOTE: Currently unused - grouping is handled by chartGroupBy from charts-common.js
	// Keeping for potential future use or reference
	// eslint-disable-next-line no-unused-vars
	const groupByPeriod = (dateGroups, days) => {
		// For 7 days: use 6 days back (7 days total)
		// For 30 days: use 29 days back (30 days total)
		const daysBack = days === 7 ? 6 : 29;

		// Get date range from dialogFilters (required for period calculation)
		let filterStartDate = dialogFilters?.filter?.startDate ? new Date(dialogFilters.filter.startDate) : null;
		let filterEndDate = dialogFilters?.filter?.endDate ? new Date(dialogFilters.filter.endDate) : null;

		if (!filterStartDate || !filterEndDate) {
			// Fallback: use dates from dateGroups
			const allDates = Object.keys(dateGroups)
				.map(d => new Date(d))
				.filter(d => !isNaN(d.getTime()))
				.sort((a, b) => a - b);

			if (allDates.length === 0) return [];

			filterStartDate = allDates[0];
			filterEndDate = allDates[allDates.length - 1];
		}

		// Normalize dates
		filterStartDate.setHours(0, 0, 0, 0);
		filterEndDate.setHours(0, 0, 0, 0);

		// Get period end dates (matching get30DaysFromDates logic)
		const periodEndDates = getPeriodEndDates(filterStartDate, filterEndDate, daysBack);

		if (periodEndDates.length === 0) return [];

		// Format date helper
		const formatDate = (d) => {
			const month = String(d.getMonth() + 1).padStart(2, '0');
			const day = String(d.getDate()).padStart(2, '0');
			return `${month}-${day}`;
		};

		// Create periods matching main chart logic
		const result = periodEndDates.map((endDateItem, index) => {
			// Calculate period start (daysBack days before end date) - matching: moment(x).subtract(days, "days")
			const periodStart = new Date(endDateItem);
			periodStart.setDate(periodStart.getDate() - daysBack);
			periodStart.setHours(0, 0, 0, 0);

			const periodEnd = new Date(endDateItem);
			periodEnd.setHours(0, 0, 0, 0);

			// For aggregation: use the calculated periodStart (matching main chart: startOfMDate = x - days)
			// But ensure we don't go before filter start date
			const actualStartDate = periodStart < filterStartDate ? filterStartDate : periodStart;

			// Format labels (matching main chart: last period uses queryFilters.startDate for label)
			const endDateLabel = formatDate(periodEnd);
			const startDateLabel = index === periodEndDates.length - 1
				? formatDate(filterStartDate)  // Last period: use filter start date for label
				: formatDate(periodStart);      // Other periods: use calculated start date

			// Aggregate all data within this period (matching: obj.x >= startOfMDate && obj.x <= endOfMDate)
			let total = 0;
			Object.keys(dateGroups).forEach((dateStr) => {
				const date = new Date(dateStr);
				if (isNaN(date.getTime())) return;
				date.setHours(0, 0, 0, 0);

				// Match main chart: obj.x >= startOfMDate && obj.x <= endOfMDate
				if (date >= actualStartDate && date <= periodEnd) {
					total += parseInt(dateGroups[dateStr] || 0);
				}
			});

			return {
				date: `(${startDateLabel})-(${endDateLabel})`,
				total,
			};
		});

		return result;
	};


	// Fetch trend-mapping groups once per dialog open (API returns all account groups; no need to refetch when cards list grows).
	useEffect(() => {
		if (!open) {
			setTrendMappingGroupsPayload(null);
			return;
		}
		let cancelled = false;
		const load = async () => {
			setLoadingGroups(true);
			try {
				const response = await getTrendMappingGroups({});
				if (cancelled) return;
				const raw = response?.data ?? response ?? [];
				setTrendMappingGroupsPayload(Array.isArray(raw) ? raw : []);
			} catch (error) {
				if (!cancelled) {
					console.error("Failed to load trend mapping groups:", error);
					setTrendMappingGroupsPayload([]);
				}
			} finally {
				if (!cancelled) {
					setLoadingGroups(false);
				}
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [open]);

	// Map cached groups onto current cards whenever the card list or payload changes (no API).
	useEffect(() => {
		if (!open || availableCards.length === 0 || trendMappingGroupsPayload === null) {
			return;
		}

		const allGroups = trendMappingGroupsPayload;
		const groupsByCardId = {};
		if (Array.isArray(allGroups)) {
			allGroups.forEach((group) => {
				if (!group?.cardId) return;
				if (!groupsByCardId[group.cardId]) {
					groupsByCardId[group.cardId] = [];
				}
				groupsByCardId[group.cardId].push(group);
			});
		}

		const results = availableCards.map((card) => {
			try {
				const cardGroups = groupsByCardId[card.id] || [];
				const cardItems = card.items || [];
				const availableItemIds = new Set(
					cardItems.map((item) => getItemId(item)).filter(Boolean)
				);

				const processedGroups = cardGroups
					.map((group) => {
						const originalItemIds = group.itemIds || [];
						const validItemIds = originalItemIds.filter((itemId) =>
							availableItemIds.has(itemId)
						);
						const missingItemIds = originalItemIds.filter(
							(itemId) => !availableItemIds.has(itemId)
						);

						if (validItemIds.length >= 2) {
							return {
								id: group._id || group.id,
								name: group.name,
								itemIds: validItemIds,
								originalItemIds,
								missingItemIds,
								hasMissingItems: missingItemIds.length > 0,
							};
						}
						return null;
					})
					.filter(Boolean);

				return { cardId: card.id, groups: processedGroups };
			} catch (error) {
				return { cardId: card.id, groups: [] };
			}
		});

		setConfig((prev) => {
			const groupsByCard = {};
			const newSelectedGroupsByCard = {};
			results.forEach(({ cardId, groups }) => {
				if (groups.length > 0) {
					groupsByCard[cardId] = groups;
					if (!prev.isSavedSelectionLoaded || !prev.selectedGroupsByCard?.[cardId]) {
						newSelectedGroupsByCard[cardId] = groups.map((g) => g.id);
					}
				}
			});

			return {
				...prev,
				groupsByCard,
				selectedGroupsByCard: {
					...(prev.selectedGroupsByCard || {}),
					...newSelectedGroupsByCard,
				},
			};
		});
	}, [open, availableCards, trendMappingGroupsPayload, getItemId]);

	// Load saved selection when dialog opens
	useEffect(() => {
		if (!open || !pageType) return;

		const loadSavedSelection = async () => {
			setLoadingSelection(true);
			try {
				const response = await getTrendMappingSelection({ pageType });

				const saved = response?.data || response;
				if (saved && saved._id) {
					setSavedSelection(saved);
					// Get current card type - always include it in selection
					const currentCardTypeValue = currentCardType || dialogFilters?.type;

					// Apply saved selection to config, but ensure current card is always included
					setConfig((prev) => {
						const savedSelectedCards = saved.selectedCards || [];
						// Ensure current card is always included if it exists
						const finalSelectedCards = currentCardTypeValue && !savedSelectedCards.includes(currentCardTypeValue)
							? [...savedSelectedCards, currentCardTypeValue]
							: savedSelectedCards;

						// If current card was added, also add its default items
						const savedSelectedItemsByCard = saved.selectedItemsByCard || {};
						let finalSelectedItemsByCard = { ...savedSelectedItemsByCard };

						// Note: Items for the current card will be added by the default selection effect
						// when availableCards is ready, so we don't need to handle it here

						return {
							...prev,
							selectedCards: finalSelectedCards,
							selectedItemsByCard: finalSelectedItemsByCard,
							selectedGroupsByCard: saved.selectedGroupsByCard || prev.selectedGroupsByCard || {},
							filterMode: saved.filterMode || prev.filterMode || "keepAll",
							selectedFilterCards: saved.selectedFilterCards || prev.selectedFilterCards || [],
							similarityTolerance: saved.similarityTolerance !== undefined ? saved.similarityTolerance : (prev.similarityTolerance || 10),
							isSavedSelectionLoaded: true,
						};
					});

					const validCompareIds = getOtherDashboardIdsForCompare(pageType);

					let nextPoolTab = saved.cardsPoolTab || "CURRENT";
					if (nextPoolTab === "OVERALL") {
						nextPoolTab = CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING;
					}
					if (nextPoolTab !== "CURRENT" && !validCompareIds.includes(nextPoolTab)) {
						nextPoolTab = "CURRENT";
					}
					setCompareCardsPoolTab(nextPoolTab);

					let nextResultFilter = saved.resultDashboardFilter;
					if (nextResultFilter === undefined || nextResultFilter === null || nextResultFilter === "") {
						nextResultFilter = "all";
					}
					if (nextResultFilter === "OVERALL") {
						nextResultFilter = CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING;
					}
					if (
						nextResultFilter !== "all" &&
						nextResultFilter !== "CURRENT" &&
						!validCompareIds.includes(nextResultFilter)
					) {
						nextResultFilter = "all";
					}
					setDashboardFilter(nextResultFilter);
				} else {
					setSavedSelection(null);
					setCompareCardsPoolTab("CURRENT");
					setDashboardFilter("all");
					// Mark as loaded even if no saved selection exists, so defaults can apply
					setConfig(prev => ({ ...prev, isSavedSelectionLoaded: true }));
				}
			} catch (error) {
				setSavedSelection(null);
				setCompareCardsPoolTab("CURRENT");
				setDashboardFilter("all");
			} finally {
				setLoadingSelection(false);
			}
		};

		loadSavedSelection();
	}, [open, pageType, currentCardType, dialogFilters?.type]);

	// Default selection: select all cards and all their items once when dialog opens / cards change
	// Only apply default if no saved selection was loaded
	useEffect(() => {
		if (!open || availableCards.length === 0) return;
		// Don't apply defaults while loading saved selection (prevents race where default overwrites before load completes)
		if (loadingSelection) return;
		// Don't apply defaults if we have a saved selection (it will be loaded separately)
		if (savedSelection) return;

		setConfig((prev) => {
			const allCardIds = availableCards.map((c) => c.id);
			const prevSelectedCards = prev.selectedCards || [];

			// Check if all cards are already selected
			const hasAllCards =
				allCardIds.length === prevSelectedCards.length &&
				allCardIds.every((id) => prevSelectedCards.includes(id));

			// Build desired items-by-card (exclude read-only to match save/display logic)
			const desiredItemsByCard = {};
			availableCards.forEach((card) => {
				const items = (card.items || []).filter((item) => !item.isReadOnly);
				desiredItemsByCard[card.id] = items.map(getItemId).filter(Boolean);
			});

			// Check if selectedItemsByCard already matches desiredItemsByCard (type-safe ID comparison)
			const currentItemsByCard = prev.selectedItemsByCard || {};
			let itemsMatch = true;
			for (const card of availableCards) {
				const cardId = card.id;
				const desired = desiredItemsByCard[cardId] || [];
				const current = currentItemsByCard[cardId] || [];

				if (desired.length !== current.length) {
					itemsMatch = false;
					break;
				}
				const desiredStrSet = new Set(desired.map((id) => String(id)));
				if (!current.every((id) => desiredStrSet.has(String(id)))) {
					itemsMatch = false;
					break;
				}
			}

			// If both cards and items are already in the desired state, avoid state update
			if (hasAllCards && itemsMatch) {
				return prev;
			}

			return {
				...prev,
				selectedCards: allCardIds,
				selectedItemsByCard: {
					...currentItemsByCard,
					...desiredItemsByCard,
				},
			};
		});
	}, [open, availableCards, savedSelection, loadingSelection, getItemId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Ensure current card's items are selected when the card is in selectedCards
	// This handles the case where current card was added from saved selection
	useEffect(() => {
		if (!open || availableCards.length === 0) return;

		const currentCardTypeValue = currentCardType || dialogFilters?.type;
		if (!currentCardTypeValue) return;

		// Check if current card is in selectedCards
		const isCurrentCardSelected = (config.selectedCards || []).includes(currentCardTypeValue);
		if (!isCurrentCardSelected) return;

		// Find the current card in availableCards
		const currentCard = availableCards.find(card => card.id === currentCardTypeValue);
		if (!currentCard) return;

		// Only add items when card was just added (no items yet) - do not overwrite user deselections
		const currentItems = config.selectedItemsByCard?.[currentCardTypeValue] || [];
		const cardItems = (currentCard.items || []).filter((item) => !item.isReadOnly);
		const allItemIds = cardItems.map(getItemId).filter(Boolean);

		if (allItemIds.length > 0 && currentItems.length === 0) {
			setConfig((prev) => ({
				...prev,
				selectedItemsByCard: {
					...(prev.selectedItemsByCard || {}),
					[currentCardTypeValue]: allItemIds,
				},
			}));
		}
	}, [open, availableCards, config.selectedCards, config.selectedItemsByCard, currentCardType, dialogFilters?.type, getItemId]);

	// When saved selection has selectAllByCard[cardId]=true, only ADD items that are NEW (from date range expand)
	// Do NOT add back items the user has deselected - use saved.selectedItemsByCard to know original items.
	// Apply updates via functional setConfig so we never overwrite a recent user deselect (stale closure).
	useEffect(() => {
		if (!open || !savedSelection || availableCards.length === 0) return;

		const selectAllByCard = savedSelection.selectAllByCard || {};
		const cardsToExpand = Object.keys(selectAllByCard).filter((cardId) => selectAllByCard[cardId] === true);
		if (cardsToExpand.length === 0) return;

		// Build only the "new items per card" map here; merge inside setConfig with latest state
		const newItemsByCard = {};
		cardsToExpand.forEach((cardId) => {
			const card = availableCards.find((c) => c.id === cardId);
			if (!card) return;
			const allItemIds = (card.items || [])
				.filter((item) => !item.isReadOnly)
				.map(getItemId)
				.filter(Boolean);
			if (allItemIds.length === 0) return;
			const savedItemIds = savedSelection.selectedItemsByCard?.[cardId] || [];
			const savedSet = new Set(savedItemIds.map((id) => String(id)));
			// Only items that exist NOW but did NOT exist when saved = genuinely new from range expand
			const newItemsFromRangeExpand = allItemIds.filter((id) => !savedSet.has(String(id)));
			if (newItemsFromRangeExpand.length > 0) {
				newItemsByCard[cardId] = newItemsFromRangeExpand;
			}
		});

		if (Object.keys(newItemsByCard).length === 0) return;

		setConfig((prev) => {
			const currentByCard = prev.selectedItemsByCard || {};
			let hasUpdates = false;
			const nextSelectedItemsByCard = { ...currentByCard };
			Object.entries(newItemsByCard).forEach(([cardId, itemsToAdd]) => {
				const currentSelected = nextSelectedItemsByCard[cardId] || [];
				const currentSet = new Set(currentSelected.map((id) => String(id)));
				const actuallyNew = itemsToAdd.filter((id) => !currentSet.has(String(id)));
				if (actuallyNew.length > 0) {
					nextSelectedItemsByCard[cardId] = [...currentSelected, ...actuallyNew];
					hasUpdates = true;
				}
			});
			if (!hasUpdates) return prev;
			return {
				...prev,
				selectedItemsByCard: nextSelectedItemsByCard,
			};
		});
	}, [open, savedSelection, availableCards, getItemId]);

	// Helper function to extract card types from priorityData/mainPriorityData
	const getCardTypesFromPriorityData = useCallback(() => {
		const cardFilter = dialogFilters?.cardFilter || {};
		const cardTypes = new Set();

		// Extract types from priorityData array
		const priorityData = cardFilter.priorityData || [];
		if (Array.isArray(priorityData)) {
			priorityData.forEach((item) => {
				if (item && item.type) {
					cardTypes.add(item.type);
				}
			});
		}

		// Extract types from mainPriorityData array
		const mainPriorityData = cardFilter.mainPriorityData || [];
		if (Array.isArray(mainPriorityData)) {
			mainPriorityData.forEach((item) => {
				if (item && item.type) {
					cardTypes.add(item.type);
				}
			});
		}

		return Array.from(cardTypes);
	}, [dialogFilters?.cardFilter]);

	const handleFilterModeChange = (event) => {
		const mode = event.target.value;
		setConfig((prev) => {
			// For "keepSome" mode, initialize with all filter cards selected
			const initialSelectedFilterCards =
				mode === "keepSome"
					? (() => {
						const cardTypes = new Set();

						// Get card types from regular filters
						Object.keys(dialogFilters?.cardFilter || {}).forEach((key) => {
							// Exclude priorityData/mainPriorityData as containers, not real filters
							if (key === "priorityData" || key === "mainPriorityData") {
								return;
							}
							const value = dialogFilters?.cardFilter[key];
							if (Array.isArray(value) && value.length > 0) {
								cardTypes.add(key);
							}
						});

						// Add card types from priorityData/mainPriorityData
						const priorityCardTypes = getCardTypesFromPriorityData();
						priorityCardTypes.forEach((type) => cardTypes.add(type));

						return Array.from(cardTypes);
					})()
					: prev.selectedFilterCards;

			const nextConfig = {
				...prev,
				filterMode: mode,
				selectedFilterCards: initialSelectedFilterCards,
			};

			return nextConfig;
		});
	};

	const handleToggleFilterCard = (cardType) => {
		setConfig((prev) => {
			const selected = new Set(prev.selectedFilterCards || []);
			if (selected.has(cardType)) {
				selected.delete(cardType);
			} else {
				selected.add(cardType);
			}
			const nextSelected = Array.from(selected);

			return {
				...prev,
				selectedFilterCards: nextSelected,
			};
		});
	};

	const handleToggleCard = (cardId) => {
		setConfig((prev) => {
			const selected = new Set(prev.selectedCards || []);
			const isCurrentlySelected = selected.has(cardId);

			if (isCurrentlySelected) {
				// Unselecting card - also clear its items and groups
				selected.delete(cardId);
				const updatedSelectedItemsByCard = { ...(prev.selectedItemsByCard || {}) };
				const updatedSelectedGroupsByCard = { ...(prev.selectedGroupsByCard || {}) };
				// Remove items and groups for this card
				delete updatedSelectedItemsByCard[cardId];
				delete updatedSelectedGroupsByCard[cardId];

				return {
					...prev,
					selectedCards: Array.from(selected),
					selectedItemsByCard: updatedSelectedItemsByCard,
					selectedGroupsByCard: updatedSelectedGroupsByCard,
				};
			} else {
				// Selecting card - select all items and groups
				selected.add(cardId);
				const card = availableCards.find(c => c.id === cardId);
				// Get all available items (excluding read-only items)
				const allItems = card?.items
					? card.items.filter(item => !item.isReadOnly).map(getItemId).filter(Boolean)
					: [];
				// Get all available groups
				const allGroups = prev.groupsByCard?.[cardId]
					? prev.groupsByCard[cardId].map(g => g.id)
					: [];

				return {
					...prev,
					selectedCards: Array.from(selected),
					selectedItemsByCard: {
						...(prev.selectedItemsByCard || {}),
						[cardId]: allItems,
					},
					selectedGroupsByCard: {
						...(prev.selectedGroupsByCard || {}),
						[cardId]: allGroups,
					},
				};
			}
		});
	};

	const handleToggleItem = (cardId, itemId) => {
		setConfig((prev) => {
			const existing = prev.selectedItemsByCard?.[cardId] || [];
			const itemIdStr = String(itemId);
			// Use string-based comparison so 2 and "2" are treated as the same item.
			// Remove ALL matching ids (handles duplicates or number/string mix from saved selection or selectAllByCard).
			const isCurrentlySelected = existing.some((id) => String(id) === itemIdStr);
			const updatedSelectedItems = isCurrentlySelected
				? existing.filter((id) => String(id) !== itemIdStr)
				: [...existing, itemId];

			// If adding, avoid duplicate by string (shouldn't happen if isCurrentlySelected was false)
			const finalSelectedItems = !isCurrentlySelected && existing.some((id) => String(id) === itemIdStr)
				? existing
				: updatedSelectedItems;
			const selectedGroups = prev.selectedGroupsByCard?.[cardId] || [];

			// Check if all items and groups are unselected
			const card = availableCards.find(c => c.id === cardId);
			const allItems = card?.items
				? card.items.filter(item => !item.isReadOnly).map(getItemId).filter(Boolean)
				: [];
			const allGroups = prev.groupsByCard?.[cardId]
				? prev.groupsByCard[cardId].map(g => g.id)
				: [];

			// Uncheck parent card if all available items and groups are unselected
			// Only uncheck if there are items/groups and they're all unselected
			const hasItems = allItems.length > 0;
			const hasGroups = allGroups.length > 0;
			const allItemsUnselected = !hasItems || finalSelectedItems.length === 0;
			const allGroupsUnselected = !hasGroups || selectedGroups.length === 0;
			// Only uncheck if there are items or groups, and all are unselected
			const shouldUncheckCard = (hasItems || hasGroups) && allItemsUnselected && allGroupsUnselected;

			// If unchecking card, also clean up items and groups entries for consistency
			let updatedSelectedCards = prev.selectedCards || [];
			let updatedSelectedItemsByCard = { ...(prev.selectedItemsByCard || {}) };
			let updatedSelectedGroupsByCard = { ...(prev.selectedGroupsByCard || {}) };

			if (shouldUncheckCard) {
				// Remove card from selectedCards
				updatedSelectedCards = updatedSelectedCards.filter(id => id !== cardId);
				// Clean up items and groups entries (consistent with handleToggleCard behavior)
				delete updatedSelectedItemsByCard[cardId];
				delete updatedSelectedGroupsByCard[cardId];
			} else {
				// Keep items updated if card remains selected
				updatedSelectedItemsByCard[cardId] = finalSelectedItems;
			}

			return {
				...prev,
				selectedCards: updatedSelectedCards,
				selectedItemsByCard: updatedSelectedItemsByCard,
				selectedGroupsByCard: updatedSelectedGroupsByCard,
			};
		});
	};

	const handleToggleGroup = (cardId, groupId) => {
		setConfig((prev) => {
			const existing = prev.selectedGroupsByCard?.[cardId] || [];
			const selected = new Set(existing);
			if (selected.has(groupId)) {
				selected.delete(groupId);
			} else {
				selected.add(groupId);
			}

			const updatedSelectedGroups = Array.from(selected);
			const selectedItems = prev.selectedItemsByCard?.[cardId] || [];

			// Check if all items and groups are unselected
			const card = availableCards.find(c => c.id === cardId);
			const allItems = card?.items
				? card.items.filter(item => !item.isReadOnly).map(getItemId).filter(Boolean)
				: [];
			const allGroups = prev.groupsByCard?.[cardId]
				? prev.groupsByCard[cardId].map(g => g.id)
				: [];

			// Uncheck parent card if all available items and groups are unselected
			// Only uncheck if there are items/groups and they're all unselected
			const hasItems = allItems.length > 0;
			const hasGroups = allGroups.length > 0;
			const allItemsUnselected = !hasItems || selectedItems.length === 0;
			const allGroupsUnselected = !hasGroups || updatedSelectedGroups.length === 0;
			// Only uncheck if there are items or groups, and all are unselected
			const shouldUncheckCard = (hasItems || hasGroups) && allItemsUnselected && allGroupsUnselected;

			// If unchecking card, also clean up items and groups entries for consistency
			let updatedSelectedCards = prev.selectedCards || [];
			let updatedSelectedItemsByCard = { ...(prev.selectedItemsByCard || {}) };
			let updatedSelectedGroupsByCard = { ...(prev.selectedGroupsByCard || {}) };

			if (shouldUncheckCard) {
				// Remove card from selectedCards
				updatedSelectedCards = updatedSelectedCards.filter(id => id !== cardId);
				// Clean up items and groups entries (consistent with handleToggleCard behavior)
				delete updatedSelectedItemsByCard[cardId];
				delete updatedSelectedGroupsByCard[cardId];
			} else {
				// Keep groups updated if card remains selected
				updatedSelectedGroupsByCard[cardId] = updatedSelectedGroups;
			}

			return {
				...prev,
				selectedCards: updatedSelectedCards,
				selectedItemsByCard: updatedSelectedItemsByCard,
				selectedGroupsByCard: updatedSelectedGroupsByCard,
			};
		});
	};

	// Select / clear every card in the active dashboard pool (and all non-readonly items + all groups per card).
	const handleSelectAllInPool = useCallback((cardsInPool) => {
		if (!Array.isArray(cardsInPool) || cardsInPool.length === 0) return;
		setConfig((prev) => {
			const selectedCards = new Set(prev.selectedCards || []);
			const selectedItemsByCard = { ...(prev.selectedItemsByCard || {}) };
			const selectedGroupsByCard = { ...(prev.selectedGroupsByCard || {}) };

			cardsInPool.forEach((card) => {
				const cardId = card.id;
				selectedCards.add(cardId);
				const allItems = card?.items
					? card.items.filter((item) => !item.isReadOnly).map(getItemId).filter(Boolean)
					: [];
				const allGroups = prev.groupsByCard?.[cardId]
					? prev.groupsByCard[cardId].map((g) => g.id)
					: [];
				selectedItemsByCard[cardId] = allItems;
				selectedGroupsByCard[cardId] = allGroups;
			});

			return {
				...prev,
				selectedCards: Array.from(selectedCards),
				selectedItemsByCard,
				selectedGroupsByCard,
			};
		});
	}, [getItemId]);

	const handleDeselectAllInPool = useCallback((cardsInPool) => {
		if (!Array.isArray(cardsInPool) || cardsInPool.length === 0) return;
		setConfig((prev) => {
			const poolIdsStr = new Set(cardsInPool.map((c) => String(c.id)));
			const selectedCards = (prev.selectedCards || []).filter((id) => !poolIdsStr.has(String(id)));
			const selectedItemsByCard = { ...(prev.selectedItemsByCard || {}) };
			const selectedGroupsByCard = { ...(prev.selectedGroupsByCard || {}) };
			cardsInPool.forEach((card) => {
				delete selectedItemsByCard[card.id];
				delete selectedGroupsByCard[card.id];
			});
			return {
				...prev,
				selectedCards,
				selectedItemsByCard,
				selectedGroupsByCard,
			};
		});
	}, []);

	const handleAnalyze = () => {
		// Prevent re-entrancy: don't trigger a new analysis while one is already in progress
		if (analyzing) {
			return;
		}

		const currentCardTypeValue = currentCardType || dialogFilters?.type;
		const cardFilter = dialogFilters?.cardFilter || {};
		const priorityData = cardFilter.priorityData || [];
		const mainPriorityData = cardFilter.mainPriorityData || [];

		// Build the list of cards that should NEVER appear in the "analyze result"
		// compare list:
		// - The current card itself (it's the base card, shown in the preview panel)
		// - ALL cards that are used as priority filters (priorityData / mainPriorityData)
		//   regardless of whether they come before or after the current card.
		// This matches the behaviour of the card & item compare list where
		// priorityData / mainPriorityData cards are not shown as selectable cards.
		let cardsToExcludeFromAnalysis = [];
		if (currentCardTypeValue) {
			// Always exclude current card from compare list
			cardsToExcludeFromAnalysis.push(currentCardTypeValue);
		}

		// Exclude every card type that appears in priorityData
		if (Array.isArray(priorityData) && priorityData.length > 0) {
			priorityData.forEach((item) => {
				if (item && item.type) {
					cardsToExcludeFromAnalysis.push(item.type);
				}
			});
		}

		// Exclude every card type that appears in mainPriorityData (admission page)
		if (Array.isArray(mainPriorityData) && mainPriorityData.length > 0) {
			mainPriorityData.forEach((item) => {
				if (item && item.type) {
					cardsToExcludeFromAnalysis.push(item.type);
				}
			});
		}

		// Filter config to only include selected cards and their selected items/groups
		// EXCLUDE current card and cards after it in priorityData from analysis
		// This works for all card types (standard and custom) and all pages
		let selectedCards = config.selectedCards || [];

		// Remove current card and cards after it in priorityData from selectedCards
		// Use strict string comparison to handle all card types (standard cards, custom cards, etc.)
		if (cardsToExcludeFromAnalysis.length > 0) {
			const excludeSet = new Set(cardsToExcludeFromAnalysis.map(c => String(c)));
			selectedCards = selectedCards.filter(cardId => {
				// String comparison works for all card types (standard and custom)
				return !excludeSet.has(String(cardId));
			});
		}

		if (selectedCards.length === 0) {
			return;
		}

		// Split selected cards: current dashboard vs compare dashboards.
		const compareCardPrefix = "compare__";
		const currentDashboardSelectedCards = selectedCards.filter(
			(cardId) => !String(cardId).startsWith(compareCardPrefix),
		);
		const compareDashboardSelectedCards = selectedCards.filter((cardId) =>
			String(cardId).startsWith(compareCardPrefix),
		);

		// Build filtered groupsByCard - only include selected groups for selected cards
		// EXCLUDE current card and cards after it in priorityData from analysis (works for all card types and pages)
		const filteredGroupsByCard = {};
		const excludeSet = cardsToExcludeFromAnalysis.length > 0
			? new Set(cardsToExcludeFromAnalysis.map(c => String(c)))
			: new Set();

		currentDashboardSelectedCards.forEach((cardId) => {
			// Skip current card and cards after it in priorityData (double-check, though they should already be filtered out)
			// Use strict string comparison for robustness
			if (excludeSet.has(String(cardId))) {
				return;
			}

			const allGroups = config.groupsByCard?.[cardId] || [];
			const selectedGroupIds = new Set(config.selectedGroupsByCard?.[cardId] || []);

			// Only include groups that are selected
			const selectedGroups = allGroups.filter(g => selectedGroupIds.has(g.id));
			if (selectedGroups.length > 0) {
				filteredGroupsByCard[cardId] = selectedGroups;
			}
		});

		// NOTE: config.selectedFilterCards includes card types from priorityData/mainPriorityData
		// (extracted via getCardTypesFromPriorityData). This ensures that when filterMode is "keepSome",
		// the analysis in HospitalCardDetailsDialog correctly filters priorityData/mainPriorityData arrays
		// based on the selected card types (e.g., "ninetyDaysData").
		const crossDashboardSelectionsMap = {};
		compareDashboardSelectedCards.forEach((prefixedCardId) => {
			const parts = String(prefixedCardId).split("__");
			if (parts.length < 3) return;
			const dashboardId = parts[1];
			const originalCardId = parts.slice(2).join("__");
			const dashboardCompareData = compareDashboardData?.[dashboardId];
			if (!dashboardCompareData?.analysisDialogFilters || !dashboardCompareData?.analyzerPageType) {
				return;
			}

			if (!crossDashboardSelectionsMap[dashboardId]) {
				crossDashboardSelectionsMap[dashboardId] = {
					dashboardId,
					dashboardLabel: DASHBOARD_OPTIONS.find((d) => d.id === dashboardId)?.label || dashboardId,
					pageType: dashboardCompareData.analyzerPageType,
					dialogFilters: dashboardCompareData.analysisDialogFilters,
					patientsOverride: dashboardCompareData.patientsOverride || [],
					selectedCards: [],
					selectedItemsByCard: {},
					selectedGroupsByCard: {},
					groupsByCard: {},
				};
			}

			crossDashboardSelectionsMap[dashboardId].selectedCards.push(originalCardId);
			const selectedItems = config.selectedItemsByCard?.[prefixedCardId] || [];
			if (selectedItems.length > 0) {
				crossDashboardSelectionsMap[dashboardId].selectedItemsByCard[originalCardId] = selectedItems;
			}

			// Same as current dashboard: pass only groups that are selected (analyzeTrendsLocally expects
			// groupsByCard + selectedGroupsByCard keyed by real card id, not compare__ prefix).
			const allGroupsForCompareCard = config.groupsByCard?.[prefixedCardId] || [];
			const selectedCompareGroupIds = new Set(config.selectedGroupsByCard?.[prefixedCardId] || []);
			const selectedGroupDefs = allGroupsForCompareCard.filter((g) =>
				selectedCompareGroupIds.has(g.id)
			);
			if (selectedGroupDefs.length > 0) {
				crossDashboardSelectionsMap[dashboardId].groupsByCard[originalCardId] = selectedGroupDefs;
			}
			const selectedGroupIdList = config.selectedGroupsByCard?.[prefixedCardId] || [];
			if (selectedGroupIdList.length > 0) {
				crossDashboardSelectionsMap[dashboardId].selectedGroupsByCard[originalCardId] =
					selectedGroupIdList;
			}
		});

		const crossDashboardSelections = Object.values(crossDashboardSelectionsMap).filter(
			(entry) => Array.isArray(entry.selectedCards) && entry.selectedCards.length > 0,
		);

		const filteredConfig = {
			...config,
			selectedCards: currentDashboardSelectedCards,
			// Only include items for selected cards (current card and cards after it are already excluded from selectedCards)
			// Double-check to ensure they are excluded (works for all card types and pages)
			selectedItemsByCard: currentDashboardSelectedCards.reduce((acc, cardId) => {
				// Skip current card and cards after it in priorityData (use strict string comparison for robustness)
				if (excludeSet.has(String(cardId))) {
					return acc;
				}
				const selectedItems = config.selectedItemsByCard?.[cardId] || [];
				if (selectedItems.length > 0) {
					acc[cardId] = selectedItems;
				}
				return acc;
			}, {}),
			// Only include groups for selected cards (current card and cards after it are already excluded from selectedCards)
			// Double-check to ensure they are excluded (works for all card types and pages)
			selectedGroupsByCard: currentDashboardSelectedCards.reduce((acc, cardId) => {
				// Skip current card and cards after it in priorityData (use strict string comparison for robustness)
				if (excludeSet.has(String(cardId))) {
					return acc;
				}
				const selectedGroups = config.selectedGroupsByCard?.[cardId] || [];
				if (selectedGroups.length > 0) {
					acc[cardId] = selectedGroups;
				}
				return acc;
			}, {}),
			// Filter groupsByCard to only include selected groups
			groupsByCard: filteredGroupsByCard,
			// CRITICAL: Only pass isolated date ranges to analysis if user has explicitly selected ranges
			// When isolated ranges are selected (user clicked "Isolate" and selected ranges), 
			// analysis will only consider data from those date ranges
			// If no ranges are selected, analysis works normally without any isolation filtering
			// Multiple ranges are handled as union (any patient/point within any range is included)
			...(isolatedDateRanges.length > 0 && {
				isolatedDateRanges: isolatedDateRanges,
				currentFilterType: currentFilterType, // Needed for proper date range filtering
			}),
			...(crossDashboardSelections.length > 0 && {
				crossDashboardSelections,
			}),
		};

		if (onAnalyze) {
			onAnalyze(filteredConfig);
		}
	};

	const handleOpenChartPopup = (series, title, displayMode = "number", baselineSeries = [], isolatedRanges = []) => {
		if (series && Array.isArray(series) && series.length > 0) {
			setChartPopupData(series);
			setChartPopupTitle(title || "Trend Chart");
			setChartPopupDisplayMode(displayMode);
			setChartPopupBaselineSeries(baselineSeries);
			setChartPopupOpen(true);
		}
	};

	const handleEditGroup = (event, cardId, group) => {
		// Initialize popover with group's current data
		setGroupPopoverCardId(cardId);
		setGroupName(group.name || "");
		setGroupItemsSelection([...(group.itemIds || [])]);
		setEditingGroupId(group.id);
		setGroupPopoverAnchor(event.currentTarget);
	};

	const handleSaveGroup = async () => {
		if (!groupPopoverCardId || !groupName.trim() || savingGroup) return;

		// Use groupItemsSelection (from popover) or fallback to config selection
		const itemsToGroup = groupItemsSelection.length > 0
			? groupItemsSelection
			: (config.selectedItemsByCard?.[groupPopoverCardId] || []);

		if (itemsToGroup.length < 2) {
			return;
		}

		setSavingGroup(true);
		try {
			// Get current card to validate items exist
			const currentCardForValidation = availableCards.find(c => c.id === groupPopoverCardId);
			const cardItemsForValidation = currentCardForValidation?.items || [];
			const availableItemIdsForValidation = new Set(
				cardItemsForValidation.map((item) => String(getItemId(item))).filter(Boolean)
			);

			// Filter to only include items that currently exist (string-safe for compare vs current dashboards)
			const validItemIds = itemsToGroup
				.filter(Boolean)
				.filter((itemId) => availableItemIdsForValidation.has(String(itemId)));

			if (validItemIds.length < 2) {
				// Not enough valid items - show error or warning
				return;
			}

			// Groups are now account-wise and card-wise (not page-wise)
			let response;
			if (editingGroupId) {
				// Update existing group - only save valid items
				response = await updateTrendMappingGroup(editingGroupId, {
					name: groupName.trim(),
					itemIds: validItemIds,
				});
			} else {
				// Create new group - account and card are determined by API (from headers and cardId)
				response = await createTrendMappingGroup({
					name: groupName.trim(),
					cardId: groupPopoverCardId,
					itemIds: validItemIds,
				});
			}

			const savedGroup = response?.data || response;

			// Get current card to validate items exist (for missing items tracking)
			const currentCardForState = availableCards.find(c => c.id === groupPopoverCardId);
			const cardItemsForState = currentCardForState?.items || [];
			const availableItemIdsForState = new Set(
				cardItemsForState.map((item) => String(getItemId(item))).filter(Boolean)
			);
			const savedItemIds = savedGroup.itemIds || [];
			const missingItemIds = savedItemIds.filter(
				(itemId) => !availableItemIdsForState.has(String(itemId))
			);

			// Update local state with saved group
			setConfig((prev) => {
				const prevGroups = prev.groupsByCard?.[groupPopoverCardId] || [];
				const prevSelectedGroups = prev.selectedGroupsByCard?.[groupPopoverCardId] || [];
				const savedGroupId = savedGroup._id || savedGroup.id || editingGroupId;

				if (editingGroupId) {
					// Update existing group in state
					const updatedGroups = prevGroups.map(gr =>
						gr.id === editingGroupId
							? {
								id: savedGroupId,
								name: savedGroup.name,
								itemIds: savedItemIds,
								originalItemIds: savedItemIds,
								missingItemIds: missingItemIds,
								hasMissingItems: missingItemIds.length > 0,
							}
							: gr
					);
					// Ensure the updated group is selected
					const updatedSelectedGroups = prevSelectedGroups.includes(savedGroupId)
						? prevSelectedGroups
						: [...prevSelectedGroups, savedGroupId];
					return {
						...prev,
						groupsByCard: {
							...(prev.groupsByCard || {}),
							[groupPopoverCardId]: updatedGroups,
						},
						selectedGroupsByCard: {
							...(prev.selectedGroupsByCard || {}),
							[groupPopoverCardId]: updatedSelectedGroups,
						},
					};
				} else {
					// Add new group to state
					const newGroup = {
						id: savedGroupId,
						name: savedGroup.name,
						itemIds: savedItemIds,
						originalItemIds: savedItemIds,
						missingItemIds: missingItemIds,
						hasMissingItems: missingItemIds.length > 0,
					};
					// Automatically select the new group
					const updatedSelectedGroups = [...prevSelectedGroups, savedGroupId];
					return {
						...prev,
						groupsByCard: {
							...(prev.groupsByCard || {}),
							[groupPopoverCardId]: [...prevGroups, newGroup],
						},
						selectedGroupsByCard: {
							...(prev.selectedGroupsByCard || {}),
							[groupPopoverCardId]: updatedSelectedGroups,
						},
					};
				}
			});

			// Close popover and reset state
			setGroupPopoverAnchor(null);
			setGroupPopoverCardId(null);
			setGroupName("");
			setGroupItemsSelection([]);
			setEditingGroupId(null);
		} catch (error) {
			// Error saving group
		} finally {
			setSavingGroup(false);
		}
	};

	const handleSaveSelection = async () => {
		if (savingSelection) return;

		setSavingSelection(true);
		try {
			// Auto-generate name based on page type and timestamp
			const pageTypeLabel = pageType || "Selection";
			const timestamp = new Date().toLocaleDateString();
			const autoName = `${pageTypeLabel} Selection - ${timestamp}`;

			// Compute selectAllByCard: true for each card where user had all items selected (for auto-select when date range expands)
			const selectAllByCard = {};
			(config.selectedCards || []).forEach((cardId) => {
				const card = availableCards.find((c) => c.id === cardId);
				const allItems = card?.items
					? card.items.filter((item) => !item.isReadOnly).map(getItemId).filter(Boolean)
					: [];

				// Use string-safe comparison for robustness (handles string/number ID mixing)
				const allItemIdsStr = allItems.map((id) => String(id));
				const selectedIdsRaw = config.selectedItemsByCard?.[cardId] || [];
				const selectedIdsStrSet = new Set(selectedIdsRaw.map((id) => String(id)));

				const hasAllItemsSelected =
					allItemIdsStr.length > 0 &&
					allItemIdsStr.every((idStr) => selectedIdsStrSet.has(idStr));

				selectAllByCard[cardId] = hasAllItemsSelected;
			});

			const selectionData = {
				name: savedSelection?.name || autoName,
				pageType,
				selectedCards: config.selectedCards || [],
				selectedItemsByCard: config.selectedItemsByCard || {},
				selectedGroupsByCard: config.selectedGroupsByCard || {},
				selectedDashboards: otherDashboardIdsForCompare,
				cardsPoolTab: compareCardsPoolTab,
				resultDashboardFilter: dashboardFilter,
				selectAllByCard,
				filterMode: config.filterMode || "keepAll",
				selectedFilterCards: config.selectedFilterCards || [],
				similarityTolerance: config.similarityTolerance !== undefined ? config.similarityTolerance : 10,
			};

			let response;
			if (savedSelection && savedSelection._id) {
				// Update existing selection
				response = await updateTrendMappingSelection(savedSelection._id, selectionData);
			} else {
				// Create new selection
				response = await createTrendMappingSelection(selectionData);
			}

			// The API returns { status: 200, data: selection }
			// The interceptor returns response.data, so we get { status: 200, data: {...} }
			const saved = response?.data || response;
			setSavedSelection(saved);
			// Show success message
			const message = savedSelection && savedSelection._id
				? "Selection updated successfully!"
				: "Selection saved successfully!";
			setSaveSuccessMessage(message);
			// Auto-hide message after 3 seconds
			setTimeout(() => {
				setSaveSuccessMessage(null);
			}, 3000);
		} catch (error) {
			// Error saving selection
		} finally {
			setSavingSelection(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={isMobile ? false : "lg"}
			fullWidth
			fullScreen={isMobile}
			sx={{
				"& .MuiDialog-paper": {
					height: isMobile ? "100%" : "98%",
					overflow: "hidden",
					...(isMobile ? {
						margin: 0,
						borderRadius: 0,
						maxWidth: "100vw",
						maxHeight: "100vh"
					} : {
						m: 2,
						maxHeight: "90vh",
					})
				}
			}}
		>
			<DialogTitle sx={{ pr: 6, py: isMobile ? 0.5 : 1.5, px: isMobile ? 1 : 3 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 600, fontSize: isMobile ? "0.95rem" : undefined }}>
							Trend Mapping
						</Typography>
						<Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ mt: 0.2, fontSize: isMobile ? "0.7rem" : undefined }}>
							Compare time-based patterns across cards and items using your current
							date range and filters.
						</Typography>
						{savedSelection && (
							<Typography variant="caption" color="primary" sx={{ mt: 0.5, display: "block" }}>
								Saved selection: {savedSelection.name}
							</Typography>
						)}
					</Box>
				</Box>
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 8,
						top: isMobile ? 4 : 8,
						color: (theme) => theme.palette.grey[500],
						cursor: "pointer",
						zIndex: 1,
					}}
				>
					<CloseIcon fontSize={isMobile ? "small" : "medium"} />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ maxHeight: isMobile ? "none" : "80vh", flexGrow: isMobile ? 1 : undefined, overflow: "auto", p: isMobile ? 0.75 : 2 }}>
				<Box sx={{ mb: 2, position: "relative" }}>
					{/* Main Section: Current Card Preview with Large Chart */}
					{(() => {
						// Get current card type from props or dialogFilters
						const cardType = currentCardType || dialogFilters?.type;
						if (!cardType) {
							return (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										minHeight: 400,
										color: "text.secondary",
										borderRadius: 2,
										border: "1px solid",
										borderColor: "divider",
										bgcolor: "background.paper",
									}}
								>
									<Typography variant="body2">
										No current card selected
									</Typography>
								</Box>
							);
						}

						// Find the card in availableCards first
						let currentCard = availableCards.find((card) => card.id === cardType);

						// If not found in availableCards, try to build it from dialogFilters
						if (!currentCard) {
							const filterListData = dialogFilters?.filterListData || {};
							const cardData = filterListData[cardType];

							if (cardData && Array.isArray(cardData) && cardData.length > 0) {
								// Build card info from available data
								let cardLabel;
								if (pageType === "COMMUNITY_TRANSFER") {
									cardLabel = COMMUNITY_CARD_LABELS?.[cardType] || cardType;
								} else if (pageType === "DECEASED") {
									cardLabel = DECEASED_CARDS_LABELS?.[cardType] || cardType;
								} else if (pageType === "ADMISSION") {
									cardLabel = ADMISSION_CARDS_LABELS?.[cardType] || cardType;
								} else if (pageType === "OVERALL") {
									cardLabel = OVERALL_CARDS_LABELS?.[cardType] || cardType;
								} else {
									cardLabel = HOSPITAL_CARDS_LABELS?.[cardType] || cardType;
								}
								currentCard = {
									id: cardType,
									label: cardLabel,
									items: cardData,
								};
							} else {
								// Still try to show it even without items
								let cardLabel;
								if (pageType === "COMMUNITY_TRANSFER") {
									cardLabel = COMMUNITY_CARD_LABELS?.[cardType] || cardType;
								} else if (pageType === "DECEASED") {
									cardLabel = DECEASED_CARDS_LABELS?.[cardType] || cardType;
								} else if (pageType === "ADMISSION") {
									cardLabel = ADMISSION_CARDS_LABELS?.[cardType] || cardType;
								} else if (pageType === "OVERALL") {
									cardLabel = OVERALL_CARDS_LABELS?.[cardType] || cardType;
								} else {
									cardLabel = HOSPITAL_CARDS_LABELS?.[cardType] || cardType;
								}
								currentCard = {
									id: cardType,
									label: cardLabel,
									items: [],
								};
							}
						}

						// Get selected items for current card
						const selectedItemsRaw = currentSelectedItems || dialogFilters?.cardFilter?.[cardType] || [];
						// Normalize selected items to array of IDs
						const selectedItemIds = Array.isArray(selectedItemsRaw)
							? selectedItemsRaw.map(sel => {
								if (typeof sel === 'object' && sel !== null) {
									return sel._id || sel.id || sel.key || sel.name || sel.label || sel.value;
								}
								return sel;
							}).filter(Boolean)
							: [];

						// Use previewSeries state (loaded by useEffect at component level)
						// This uses the SAME processing logic as the main chart: chartsData -> chartGroupBy
						const aggregatedSeries = previewSeries;
						const cardItems = currentCard.items || [];
						const filteredItems = selectedItemIds.length > 0
							? cardItems.filter((item) => {
								const itemId = getItemId(item);
								return selectedItemIds.some((id) => String(id) === String(itemId));
							})
							: cardItems;

						return (
							<Box
								sx={{
									borderRadius: 1,
									border: isMobile ? "1px solid" : "2px solid",
									bgcolor: "background.paper",
									borderColor: "divider",
									p: isMobile ? 0.75 : { xs: 1.5, sm: 2, md: 3 },
									display: "flex",
									flexDirection: "column",
									gap: isMobile ? 0.75 : { xs: 1.5, sm: 2 },
									minHeight: 0,
									overflow: "visible",
								}}
							>
								{/* Card Header */}
								<Box>
									<Typography
										variant={isMobile ? "subtitle2" : "h6"}
										sx={{
											fontWeight: 700,
											mb: isMobile ? 0.25 : 0.5,
											color: "primary.main",
											fontSize: isMobile ? "0.85rem" : { xs: "1rem", sm: "1.25rem" }
										}}
									>
										{currentCard.label}
									</Typography>
								</Box>



								{/* Chart Section - Full Width */}
								<Box>
									{/* Filter By and Selected items */}
									<Box sx={{
										mb: isMobile ? 1 : { xs: 1.5, sm: 2 },
										display: "flex",
										alignItems: "center",
										gap: isMobile ? 0.5 : { xs: 1, sm: 2 },
										flexWrap: "wrap"
									}}>
										<Typography
											variant={isMobile ? "caption" : "body2"}
											color="text.secondary"
											sx={{ whiteSpace: { xs: "normal", sm: "nowrap" }, fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
										>
											Filter By: {getFilterTypeLabel(currentFilterType)}{" "}
											{selectedItemIds.length > 0
												? `· ${selectedItemIds.length} item${selectedItemIds.length > 1 ? "s" : ""} selected`
												: "· All items selected"}
										</Typography>
										{filteredItems.length > 0 && (
											<>
												<Divider
													orientation={isMobile ? "horizontal" : "vertical"}
													flexItem
													sx={{
														height: isMobile ? 1 : 20,
														width: isMobile ? "100%" : "auto",
														borderColor: "divider",
														my: isMobile ? 0.5 : undefined
													}}
												/>
												<Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 0.35 : { xs: 0.5, sm: 1 }, flexWrap: "wrap", flex: 1, width: { xs: "100%", sm: "auto" } }}>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ whiteSpace: { xs: "normal", sm: "nowrap" }, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
													>
														Selected items:
													</Typography>
													<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
														{filteredItems.slice(0, 10).map((item) => {
															const itemId = getItemId(item);
															const itemLabel = item.name || item.label || item.title || itemId;
															return (
																<Chip
																	key={itemId}
																	label={itemLabel}
																	size="small"
																	variant="outlined"
																	sx={{ height: 20, fontSize: "0.65rem" }}
																/>
															);
														})}
														{filteredItems.length > 10 && (
															<Chip
																label={`+${filteredItems.length - 10} more`}
																size="small"
																variant="outlined"
																sx={{ height: 20, fontSize: "0.65rem" }}
															/>
														)}
													</Box>
												</Box>
											</>
										)}
									</Box>

									{/* Main Chart Section */}
									<Box sx={{ mt: isMobile ? 1 : { xs: 1.5, sm: 2, md: 3 } }}>
										<Box sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											mb: isMobile ? 1 : { xs: 1, sm: 1.5, md: 2 },
											flexWrap: "nowrap",
											gap: 1,
											flexDirection: "row",
											width: "100%"
										}}>
											<Typography
												variant={isMobile ? "body2" : isTablet ? "body1" : "subtitle1"}
												sx={{
													fontWeight: 600,
													fontSize: isMobile ? "0.875rem" : { xs: "0.875rem", sm: "1rem", md: "1.25rem" }
												}}
											>
												Trend Chart
											</Typography>
											{aggregatedSeries.length > 0 && (
												<Box sx={{
													display: "flex",
													gap: isMobile ? 0.5 : { xs: 0.75, sm: 1 },
													alignItems: "center",
													flexWrap: "nowrap",
													width: "auto",
													justifyContent: "flex-end"
												}}>
													<Button
														size={isMobile ? "small" : "medium"}
														variant={isIsolationMode ? "contained" : "outlined"}
														{...(isIsolationMode ? { color: "primary" } : {})}
														onClick={() => {
															setIsIsolationMode(!isIsolationMode);
														}}
														sx={{
															minWidth: "auto",
															px: isMobile ? 1 : { xs: 1.25, sm: 1.5, md: 2 },
															py: isMobile ? 0.35 : { xs: 0.5, sm: 0.625 },
															fontSize: isMobile ? "0.65rem" : { xs: "0.7rem", sm: "0.75rem", md: "0.875rem" },
															height: isMobile ? 28 : { xs: 32, sm: 36, md: 40 },
															textTransform: "none",
															fontWeight: 500
														}}
													>
														{isIsolationMode ? "Done" : "Isolate"}
													</Button>
													{isolatedDateRanges.length > 0 && !isIsolationMode && (
														<Button
															size={isMobile ? "small" : "medium"}
															variant="text"
															onClick={() => setIsolatedDateRanges([])}
															sx={{
																minWidth: "auto",
																px: isMobile ? 0.75 : { xs: 1, sm: 1.25, md: 1.5 },
																py: isMobile ? 0.35 : { xs: 0.5, sm: 0.625 },
																fontSize: isMobile ? "0.65rem" : { xs: "0.7rem", sm: "0.75rem", md: "0.875rem" },
																height: isMobile ? 28 : { xs: 32, sm: 36, md: 40 },
																textTransform: "none",
																fontWeight: 500
															}}
														>
															Clear All
														</Button>
													)}
												</Box>
											)}
										</Box>
										{(isIsolationMode || isolatedDateRanges.length > 0) && aggregatedSeries.length > 0 && (
											<Box sx={{ mb: { xs: 1, sm: 1.5 }, p: { xs: 1, sm: 1.5 }, bgcolor: "action.hover", borderRadius: 1 }}>
												<Typography
													variant="caption"
													color="primary"
													sx={{
														fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.875rem" },
														fontWeight: 600,
														display: "block",
														mb: { xs: 0.5, sm: 0.75 }
													}}
												>
													Select periods to isolate:
												</Typography>
												{isolatedDateRanges.length === 0 ? (
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{
															display: "block",
															fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
															lineHeight: 1.4
														}}
													>
														Click on chart points to select periods.
													</Typography>
												) : (
													<Box>
														<Typography
															variant="caption"
															color="text.secondary"
															sx={{
																display: "block",
																mb: { xs: 0.75, sm: 1 },
																fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
																fontWeight: 500
															}}
														>
															Selected ranges ({isolatedDateRanges.length}):
														</Typography>
														<Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 0.5, sm: 0.75 } }}>
															{isolatedDateRanges.map((range, index) => {
																// Format label based on currentFilterType for better readability
																let label = '';
																if (currentFilterType === CHART_FILTER_DAY_OF.MONTH) {
																	// For Monthly: show month names
																	const startMonth = moment(range.start).format("MMM YYYY");
																	const endMonth = moment(range.end).format("MMM YYYY");
																	label = startMonth === endMonth ? startMonth : `${startMonth} - ${endMonth}`;
																} else if (currentFilterType === CHART_FILTER_DAY_OF.THIRTY) {
																	// For 30 Days: show date range
																	label = `${moment(range.start).format("MMM DD")} - ${moment(range.end).format("MMM DD, YYYY")}`;
																} else if (currentFilterType === CHART_FILTER_DAY_OF.SEVEN) {
																	// For 7 Days: show date range
																	label = `${moment(range.start).format("MMM DD")} - ${moment(range.end).format("MMM DD, YYYY")}`;
																} else if (currentFilterType === CHART_FILTER_DAY_OF.WEEK) {
																	// For Weekly: show week range
																	label = `${moment(range.start).format("MMM DD")} - ${moment(range.end).format("MMM DD, YYYY")}`;
																} else {
																	// For Daily: show date range
																	label = `${moment(range.start).format("MMM DD")} - ${moment(range.end).format("MMM DD, YYYY")}`;
																}

																return (
																	<Chip
																		key={index}
																		label={label}
																		size="small"
																		variant="outlined"
																		color="primary"
																		onDelete={() => {
																			setIsolatedDateRanges(prev => prev.filter((_, i) => i !== index));
																		}}
																		sx={{
																			height: { xs: 22, sm: 24, md: 26 },
																			fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
																			maxWidth: { xs: "100%", sm: "none" },
																			"& .MuiChip-label": {
																				padding: { xs: "0 8px", sm: "0 10px", md: "0 12px" },
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																				whiteSpace: "nowrap"
																			},
																			"& .MuiChip-deleteIcon": {
																				fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
																				margin: { xs: "0 4px 0 -4px", sm: "0 6px 0 -4px" }
																			}
																		}}
																	/>
																);
															})}
														</Box>
													</Box>
												)}
											</Box>
										)}
										<Box
											ref={chartContainerRef}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												pt: isMobile ? 1.5 : { xs: 2.5, sm: 3.5, md: 4 },
												pb: isMobile ? 1 : { xs: 1.5, sm: 2.5, md: 3 },
												px: isMobile ? 1 : { xs: 0.75, sm: 1.5, md: 2.5 },
												borderRadius: 1,
												bgcolor: "action.hover",
												height: isMobile
													? "200px"
													: isIsolationMode
														? { xs: "45vh", sm: "38vh", md: "42vh" }
														: { xs: "45vh", sm: "40vh", md: "45vh" },
												...(isMobile ? {} : {
													maxHeight: isIsolationMode
														? { sm: 380, md: 420 }
														: { sm: 400, md: 450 }
												}),
												minHeight: isMobile
													? 180
													: isIsolationMode
														? { xs: 320, sm: 300, md: 340 }
														: { xs: 320, sm: 320, md: 360 },
												width: "100%",
												overflow: "visible",
												flexShrink: 0,
											}}
										>
											{(() => {
												// Apply date range filter if isolation is set (not in isolation mode)
												const displaySeries = (!isIsolationMode && isolatedDateRanges.length > 0)
													? filterSeriesByMultipleRanges(aggregatedSeries, isolatedDateRanges, currentFilterType)
													: aggregatedSeries;

												return displaySeries.length > 0 ? (
													<Box sx={{
														width: "100%",
														height: "100%",
														overflow: "visible",
														display: "flex",
														alignItems: "center",
														justifyContent: "center"
													}}>
														<MiniTrendGraph
															series={displaySeries}
															width={chartWidth}
															height={chartHeight}
															id={`preview-current-${cardType}-aggregated`}
															onClick={!isIsolationMode ? () => handleOpenChartPopup(
																displaySeries,
																`${currentCard.label} - Trend Chart`,
																"number",
																[],
																isolatedDateRanges
															) : undefined}
															isIsolationMode={isIsolationMode}
															currentFilterType={currentFilterType}
															isolatedRanges={isolatedDateRanges}
															onBrushSelect={(range) => {
																// Add new range to the array (avoid duplicates)
																setIsolatedDateRanges(prev => {
																	// Check if this range already exists
																	// Use appropriate comparison based on currentFilterType
																	const exists = prev.some(r => {
																		if (!r.start || !r.end || !range.start || !range.end) return false;

																		if (currentFilterType === CHART_FILTER_DAY_OF.MONTH) {
																			// For Monthly: compare by month/year
																			const rStartMonth = moment(r.start).format('YYYY-MM');
																			const rEndMonth = moment(r.end).format('YYYY-MM');
																			const rangeStartMonth = moment(range.start).format('YYYY-MM');
																			const rangeEndMonth = moment(range.end).format('YYYY-MM');
																			return rStartMonth === rangeStartMonth && rEndMonth === rangeEndMonth;
																		} else {
																			// For Daily, Weekly, 7 Days, 30 Days: compare by day
																			return moment(r.start).isSame(moment(range.start), 'day') &&
																				moment(r.end).isSame(moment(range.end), 'day');
																		}
																	});
																	if (exists) return prev;
																	return [...prev, range];
																});
																// Don't exit isolation mode - allow multiple selections
															}}
														/>
													</Box>
												) : (
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															width: "100%",
															height: chartHeight,
															p: { xs: 2, sm: 3 }
														}}
													>
														<Typography
															variant="body2"
															color="text.secondary"
															sx={{
																fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
																textAlign: "center"
															}}
														>
															No trend data available
														</Typography>
													</Box>
												);
											})()}
										</Box>
									</Box>
								</Box>

								{/* Filter Settings - Compact Horizontal Layout */}
								<Box
									sx={{
										mt: isMobile ? 1 : { xs: 1, sm: 1.5 },
										p: isMobile ? 1 : { xs: 1.5, sm: 2 },
										bgcolor: "action.hover",
										borderRadius: 1,
										border: "1px solid",
										borderColor: "divider",
										width: "100%",
										flexShrink: 0,
										display: "flex",
										flexDirection: "column",
									}}
								>
									<Box sx={{ display: "flex", gap: isMobile ? 1 : { xs: 2, sm: 3 }, flexWrap: "wrap", alignItems: "flex-start" }}>
										{/* Filter Mode */}
										<Box sx={{ flex: isMobile ? "1 1 100%" : { xs: "1 1 100%", sm: "1 1 200px" }, minWidth: isMobile ? 0 : { xs: "100%", sm: 200 } }}>
											<Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: "block", fontSize: "0.75rem" }}>
												Filter Mode
											</Typography>
											<FormControl component="fieldset" size="small">
												<RadioGroup
													value={config.filterMode || "keepAll"}
													onChange={handleFilterModeChange}
													row={true}
													sx={isMobile ? { gap: 0.5, flexWrap: "nowrap" } : undefined}
												>
													<FormControlLabel
														value="keepAll"
														control={<Radio size="small" />}
														label={<Typography variant="caption" sx={{ fontSize: "0.7rem" }}>Keep All</Typography>}
														sx={isMobile ? { mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.65rem" } } : undefined}
													/>
													<FormControlLabel
														value="keepSome"
														control={<Radio size="small" />}
														label={<Typography variant="caption" sx={{ fontSize: "0.7rem" }}>Keep Some</Typography>}
														sx={isMobile ? { mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.65rem" } } : undefined}
													/>
													<FormControlLabel
														value="removeAll"
														control={<Radio size="small" />}
														label={<Typography variant="caption" sx={{ fontSize: "0.7rem" }}>Remove All</Typography>}
														sx={isMobile ? { mr: 0, "& .MuiFormControlLabel-label": { fontSize: "0.65rem" } } : undefined}
													/>
												</RadioGroup>
											</FormControl>
											{/* Show filter selection for "keepSome" mode */}
											<Collapse in={config.filterMode === "keepSome"}>
												<Box sx={{ mt: 1, p: 1, bgcolor: "background.paper", borderRadius: 0.5 }}>
													<Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5, fontSize: "0.65rem" }}>
														Select filters to keep:
													</Typography>
													<FormGroup row={true} sx={isMobile ? { gap: 0.5 } : undefined}>
														{(() => {
															const cardTypes = new Set();

															// Get card types from regular filters
															Object.keys(dialogFilters?.cardFilter || {}).forEach((key) => {
																if (key === 'priorityData' || key === 'mainPriorityData') {
																	return;
																}
																const filterValue = dialogFilters.cardFilter[key];
																if (Array.isArray(filterValue) && filterValue.length > 0) {
																	cardTypes.add(key);
																}
															});

															// Add card types from priorityData/mainPriorityData
															const priorityCardTypes = getCardTypesFromPriorityData();
															priorityCardTypes.forEach((type) => cardTypes.add(type));

															return Array.from(cardTypes).map((cardType) => {
																let cardLabel;
																if (pageType === "COMMUNITY_TRANSFER") {
																	cardLabel = COMMUNITY_CARD_LABELS[cardType] || cardType;
																} else if (pageType === "DECEASED") {
																	cardLabel = DECEASED_CARDS_LABELS[cardType] || cardType;
																} else if (pageType === "ADMISSION") {
																	cardLabel = ADMISSION_CARDS_LABELS[cardType] || cardType;
																} else if (pageType === "OVERALL") {
																	cardLabel = OVERALL_CARDS_LABELS[cardType] || cardType;
																} else {
																	cardLabel = HOSPITAL_CARDS_LABELS[cardType] || cardType;
																}
																const isSelected = (config.selectedFilterCards || []).includes(cardType);
																return (
																	<FormControlLabel
																		key={cardType}
																		control={
																			<Checkbox
																				size="small"
																				checked={isSelected}
																				onChange={() => handleToggleFilterCard(cardType)}
																			/>
																		}
																		label={
																			<Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
																				{cardLabel}
																			</Typography>
																		}
																		sx={isMobile ? { mr: 1, my: 0 } : undefined}
																	/>
																);
															});
														})()}
													</FormGroup>
												</Box>
											</Collapse>
										</Box>

										{/* Minimum Count — no upper cap (users may filter by any floor they need) */}
										<Box sx={{ flex: isMobile ? "1 1 calc(50% - 4px)" : { xs: "1 1 100%", sm: "1 1 150px" }, minWidth: isMobile ? 0 : { xs: "100%", sm: 150 } }}>
											<Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: "block", fontSize: "0.75rem" }}>
												Minimum Count
											</Typography>
											<TextField
												type="number"
												size="small"
												value={minimumTotalFilter === "" ? "" : String(minimumTotalFilter)}
												onChange={(e) => {
													const value = e.target.value;
													if (value === "") {
														setMinimumTotalFilter("");
														return;
													}
													if (value === "-" || value === ".") {
														setMinimumTotalFilter(value);
														return;
													}
													const numValue = parseFloat(value);
													if (!isNaN(numValue) && isFinite(numValue)) {
														if (numValue < 0) return;
														setMinimumTotalFilter(value);
													}
												}}
												onBlur={(e) => {
													const value = e.target.value;
													if (value === "" || value === "-" || value === ".") {
														setMinimumTotalFilter("");
													} else {
														const numValue = parseFloat(value);
														if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
															setMinimumTotalFilter("");
														} else {
															setMinimumTotalFilter(String(Math.floor(numValue)));
														}
													}
												}}
												inputProps={{
													min: 0,
													step: 1,
												}}
												sx={{
													width: "100%",
													"& .MuiOutlinedInput-root": {
														bgcolor: "background.paper",
													},
												}}
												placeholder="0"
											/>
										</Box>

										{/* Similarity Tolerance */}
										<Box sx={{ flex: isMobile ? "1 1 calc(50% - 4px)" : { xs: "1 1 100%", sm: "1 1 200px" }, minWidth: isMobile ? 0 : { xs: "100%", sm: 200 } }}>
											<Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: "block", fontSize: "0.75rem" }}>
												Similarity Tolerance: {typeof config.similarityTolerance === "number" ? config.similarityTolerance : 10}%
											</Typography>
											<Box sx={{ px: 0.5, mb: 1 }}>
												<Slider
													value={
														typeof config.similarityTolerance === "number"
															? config.similarityTolerance
															: 10
													}
													onChange={(_, val) =>
														setConfig((prev) => ({
															...prev,
															similarityTolerance: Array.isArray(val) ? val[0] : val,
														}))
													}
													valueLabelDisplay="auto"
													min={5}
													max={50}
													step={5}
													sx={{
														'& .MuiSlider-thumb': {
															width: 16,
															height: 16,
														},
														'& .MuiSlider-track': {
															height: 3,
														},
														'& .MuiSlider-rail': {
															height: 3,
														},
													}}
												/>
											</Box>
											<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
												{[5, 10, 15, 20, 30].map((preset) => {
													const isActive = (typeof config.similarityTolerance === "number"
														? config.similarityTolerance
														: 10) === preset;
													return (
														<Chip
															key={preset}
															label={`${preset}%`}
															size="small"
															onClick={() =>
																setConfig((prev) => ({
																	...prev,
																	similarityTolerance: preset,
																}))
															}
															sx={{
																cursor: "pointer",
																bgcolor: isActive ? "primary.main" : "background.paper",
																color: isActive ? "primary.contrastText" : "text.primary",
																border: "1px solid",
																borderColor: isActive ? "primary.main" : "divider",
																height: isMobile ? 20 : 24,
																fontSize: isMobile ? "0.6rem" : "0.65rem",
																px: isMobile ? 0.25 : undefined,
																"&:hover": {
																	bgcolor: isActive ? "primary.dark" : "action.hover",
																	borderColor: isActive ? "primary.dark" : "primary.main",
																},
																transition: "all 0.2s",
															}}
														/>
													);
												})}
											</Box>
										</Box>
									</Box>
								</Box>
							</Box>
						);
					})()}
				</Box>

				<Divider sx={{ my: 1.5 }} />

				<Box sx={{ position: "relative", mt: 1 }}>
					<Accordion
						expanded={cardsAccordionOpen}
						onChange={(_, expanded) => setCardsAccordionOpen(expanded)}
						sx={{
							borderRadius: 2,
							border: "1px solid",
							borderColor: "divider",
							bgcolor: "background.default",
							"&:before": { display: "none" },
							opacity: loadingSelection ? 0.6 : 1,
							pointerEvents: loadingSelection ? "none" : "auto",
							transition: "opacity 0.3s ease-in-out",
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								borderBottom: "1px solid",
								borderColor: "divider",
								minHeight: isMobile ? 40 : undefined,
								"& .MuiAccordionSummary-content": { my: isMobile ? 1 : undefined }
							}}
						>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: isMobile ? "0.8rem" : undefined }}>
										Cards &amp; Items to Compare
									</Typography>									
								</Box>
								<Typography variant="caption" color="text.secondary" sx={isMobile ? { fontSize: "0.65rem", lineHeight: 1.2 } : undefined}>
									All cards and items are selected by default. Uncheck any you want to
									exclude from the trend comparison.
								</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails sx={{ p: isMobile ? 0.5 : { xs: 1, sm: 1.5, md: 2 } }}>
							<Box sx={{ position: "relative" }}>
							<Paper
								variant="outlined"
								sx={{
									borderRadius: 2,
									overflow: "hidden",
									borderColor: "divider",
									boxShadow: "none",
									bgcolor: "background.paper",
								}}
							>
								<Box
									sx={(theme) => ({
										px: isMobile ? 1 : { xs: 1.25, sm: 1.5 },
										py: isMobile ? 1 : { xs: 1.25, sm: 1.35 },
										bgcolor:
											theme.palette.mode === "dark"
												? alpha(theme.palette.common.white, 0.04)
												: alpha(theme.palette.primary.main, 0.03),
									})}
								>
								<Box sx={{ mb: isMobile ? 0.5 : { xs: 0.75, sm: 1 } }}>
									<Typography
										variant="body2"
										sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "0.01em", fontSize: isMobile ? "0.75rem" : "0.8125rem" }}
									>
										View cards & items from
									</Typography>									
								</Box>
								<Box
									sx={{
										display: "flex",
										flexWrap: "wrap",
										alignItems: "center",
										gap: isMobile ? 0.5 : 1,
										width: "100%",
									}}
								>
									{comparePoolTabs.map((tab) => {
										const isActive = compareCardsPoolTab === tab.id;
										const summary = getComparePoolTabSummary(tab.id);
										const poolTotalForTab = availableCards.filter((c) => c.dashboard === tab.id).length;
										const cardNoun = poolTotalForTab === 1 ? "card" : "cards";
										const countHeadline = `${summary.selectedCardCount} of ${poolTotalForTab} ${cardNoun} selected`;
										const tooltipBody =
											summary.lines.length === 0 ? (
												<Box sx={{ py: 0.25 }}>
													<Typography
														variant="caption"
														display="block"
														sx={{ fontWeight: 700, mb: 0.5, color: "common.white", fontSize: "0.75rem" }}
													>
														{countHeadline}
													</Typography>
													<Typography variant="caption" component="span" sx={{ opacity: 0.92 }}>
														No cards selected in this dashboard yet.
													</Typography>
												</Box>
											) : (
												<Box sx={{ py: 0.25 }}>
													<Typography
														variant="caption"
														display="block"
														sx={{ fontWeight: 700, mb: 0.75, color: "common.white", fontSize: "0.75rem" }}
													>
														{countHeadline}
													</Typography>
													{/* {summary.lines.slice(0, 40).map((line, idx) => (
														<Typography key={idx} variant="caption" display="block" sx={{ mb: 0.35 }}>
															{line}
														</Typography>
													))}
													{summary.lines.length > 40 ? (
														<Typography variant="caption" display="block" sx={{ fontStyle: "italic", mt: 0.5 }}>
															+{summary.lines.length - 40} more…
														</Typography>
													) : null} */}
												</Box>
											);
										return (
											<Tooltip
												key={tab.id}
												title={tooltipBody}
												placement="top"
												arrow
												enterTouchDelay={400}
												componentsProps={{
													tooltip: {
														sx: {
															maxWidth: 380,
															bgcolor: "grey.900",
															"& .MuiTypography-caption": { color: "common.white", fontSize: "0.72rem" },
														},
													},
												}}
											>
												<Button
													size="medium"
													variant={isActive ? "contained" : "outlined"}
													color={isActive ? "primary" : "inherit"}
													onClick={() => setCompareCardsPoolTab(tab.id)}
													sx={{
														minWidth: 0,
														minHeight: isMobile ? 28 : { xs: 38, sm: 40 },
														px: isMobile ? 1 : { xs: 1.25, sm: 1.5 },
														py: isMobile ? 0.5 : { xs: 0.75, sm: 0.875 },
														textTransform: "none",
														fontSize: isMobile ? "0.7rem" : { xs: "0.75rem", sm: "0.8125rem" },
														fontWeight: 600,
														lineHeight: 1.2,
														borderRadius: 1.25,
														borderWidth: isActive ? 0 : 1,
														borderColor: "divider",
														boxShadow: "none",
														"&:hover": {
															boxShadow: "none",
															borderColor: isActive ? "primary.main" : "primary.light",
														},
													}}
												>
													<Box
														component="span"
														sx={{
															display: "block",
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
															maxWidth: isMobile ? 80 : { xs: 132, sm: 152, md: 138 },
														}}
													>
														{tab.label}
													</Box>
												</Button>
											</Tooltip>
										);
									})}
								</Box>
								</Box>

								<Divider />

								<Box
									sx={{
										px: isMobile ? 1 : { xs: 1.25, sm: 1.5 },
										py: isMobile ? 1 : { xs: 1.25, sm: 1.35 },
										bgcolor: "background.paper",
									}}
								>									
									<TextField
										fullWidth
										size={isMobile ? "small" : "medium"}
										placeholder="Search by card or item name…"
										value={cardItemSearchTerm}
										onChange={(e) => setCardItemSearchTerm(e.target.value)}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon fontSize={isMobile ? "small" : "medium"} sx={{ color: "text.secondary" }} />
												</InputAdornment>
											),
											endAdornment: cardItemSearchTerm && (
												<InputAdornment position="end">
													<IconButton
														size="small"
														onClick={() => setCardItemSearchTerm("")}
														edge="end"
														sx={{
															padding: { xs: "4px", sm: "6px" },
															"&:hover": {
																bgcolor: "action.hover",
															},
														}}
													>
														<ClearIcon fontSize={isMobile ? "small" : "medium"} sx={{ color: "text.secondary" }} />
													</IconButton>
												</InputAdornment>
											),
										}}
										sx={{
											"& .MuiOutlinedInput-root": {
												bgcolor: (theme) =>
													theme.palette.mode === "dark"
														? alpha(theme.palette.common.white, 0.04)
														: alpha(theme.palette.common.black, 0.02),
												fontSize: { xs: "0.875rem", sm: "1rem" },
												"&:hover": {
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: "primary.main",
													},
												},
												"&.Mui-focused": {
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: "primary.main",
														borderWidth: 2,
													},
												},
											},
											"& .MuiOutlinedInput-notchedOutline": {
												borderColor: "divider",
											},
										}}
									/>
									{cardItemSearchTerm.trim() && (() => {
										const currentCardTypeValue = currentCardType || dialogFilters?.type;
										const cardsToDisplayHint = currentCardTypeValue
											? comparePoolScopedFilteredCards.filter(card => String(card.id) !== String(currentCardTypeValue))
											: comparePoolScopedFilteredCards;
										const totalCardsToDisplayHint = currentCardTypeValue
											? comparePoolScopedAvailableCards.filter(card => String(card.id) !== String(currentCardTypeValue)).length
											: comparePoolScopedAvailableCards.length;

										return (
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													mt: { xs: 0.5, sm: 0.75 },
													display: "block",
													fontSize: { xs: "0.7rem", sm: "0.75rem" }
												}}
											>
												Showing {cardsToDisplayHint.length} of {totalCardsToDisplayHint} card{totalCardsToDisplayHint !== 1 ? 's' : ''}
											</Typography>
										);
									})()}
								</Box>

							{(() => {
								// Filter out the current card from the display list
								// The current card is shown in the preview section above, so we hide it here
								// This works for all card types (standard and custom) and all pages
								const currentCardTypeValue = currentCardType || dialogFilters?.type;
								const cardsToDisplay = currentCardTypeValue
									? comparePoolScopedFilteredCards.filter(card => String(card.id) !== String(currentCardTypeValue))
									: comparePoolScopedFilteredCards;

								const poolLabel =
									comparePoolTabs.find((t) => t.id === compareCardsPoolTab)?.label ?? "";
								const poolCardsAll = currentCardTypeValue
									? comparePoolScopedAvailableCards.filter(
											(c) => String(c.id) !== String(currentCardTypeValue)
										)
									: comparePoolScopedAvailableCards;
								const poolTotal = poolCardsAll.length;
								const poolSelected = poolCardsAll.filter((c) =>
									(config.selectedCards || []).includes(c.id)
								).length;
								const isPoolLoading =
									compareCardsPoolTab !== "CURRENT" &&
									Boolean(compareDashboardLoading?.[compareCardsPoolTab]);
								const searchActive = Boolean(cardItemSearchTerm.trim());
								const cardWord = poolTotal === 1 ? "card" : "cards";
								let selectionSummary;
								if (poolTotal === 0) {
									selectionSummary = "No cards in this list";
								} else if (poolSelected === poolTotal) {
									selectionSummary =
										poolTotal === 1
											? "This card is selected"
											: `All ${poolTotal} cards are selected`;
								} else {
									selectionSummary = `${poolSelected} of ${poolTotal} ${cardWord} selected`;
								}
								const countSummaryText = searchActive
									? poolTotal === 0
										? `${cardsToDisplay.length} ${cardsToDisplay.length === 1 ? "card" : "cards"} match your search`
										: `${cardsToDisplay.length} of ${poolTotal} ${cardWord} match your search · ${selectionSummary}`
									: selectionSummary;
								const allSelectedInPool = poolTotal > 0 && poolSelected === poolTotal;

								const summaryBar = (
									<Box
										sx={{
											px: isMobile ? 1 : { xs: 1.25, sm: 1.5 },
											py: isMobile ? 0.5 : { xs: 0.75, sm: 1 },
											display: "flex",
											flexWrap: "wrap",
											alignItems: "center",
											columnGap: isMobile ? 0.5 : { xs: 0.75, sm: 1 },
											rowGap: 0.5,
										}}
									>
										<Typography
											component="span"
											variant="caption"
											color="text.secondary"
											sx={{
												fontWeight: 800,
												letterSpacing: "0.08em",
												textTransform: "uppercase",
												fontSize: isMobile ? "0.58rem" : "0.62rem",
											}}
										>
											{poolLabel}
										</Typography>
										{isPoolLoading ? (
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
												<CircularProgress size={14} thickness={5} />
												<Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
													Loading cards…
												</Typography>
											</Box>
										) : (
											<>
												<Typography component="span" variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: isMobile ? "0.75rem" : undefined }}>
													{countSummaryText}
												</Typography>
												{poolTotal > 0 ? (
													<>
														<Typography
															component="span"
															variant="body2"
															color="text.disabled"
															sx={{ userSelect: "none", fontWeight: 400 }}
														>
															·
														</Typography>
														<Button
															size="small"
															variant="text"
															color="inherit"
															onClick={() =>
																allSelectedInPool
																	? handleDeselectAllInPool(poolCardsAll)
																	: handleSelectAllInPool(poolCardsAll)
															}
															sx={{
																textTransform: "none",
																fontSize: isMobile ? "0.75rem" : "0.8125rem",
																fontWeight: 600,
																minWidth: 0,
																px: 0.5,
																py: 0,
																ml: -0.25,
																verticalAlign: "baseline",
																color: allSelectedInPool ? "text.secondary" : "primary.main",
																"&:hover": {
																	bgcolor: "transparent",
																	color: allSelectedInPool ? "error.main" : "primary.dark",
																},
															}}
														>
															{allSelectedInPool ? "Deselect all" : "Select all"}
														</Button>
													</>
												) : null}
											</>
										)}
									</Box>
								);

								if (cardsToDisplay.length === 0 && searchActive) {
									return (
										<>
											{summaryBar}
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													p: { xs: 2, sm: 3 },
													px: { xs: 1.25, sm: 1.5 },
												}}
											>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ fontSize: { xs: "0.875rem", sm: "1rem" }, textAlign: "center" }}
												>
													No cards or items found matching "{cardItemSearchTerm}"
												</Typography>
											</Box>
										</>
									);
								}

								return (
									<>
										{summaryBar}
										<Box sx={{ px: isMobile ? 1 : { xs: 1.25, sm: 1.5 }, py: isMobile ? 1 : { xs: 1.25, sm: 1.5 } }}>
										{cardsToDisplay.map((card) => {
													const cardSelected = (config.selectedCards || []).includes(card.id);
													const selectedItems = config.selectedItemsByCard?.[card.id] || [];

													return (
														<Box
															key={card.id}
															sx={{
																mb: isMobile ? 0.75 : { xs: 1, sm: 1.25 },
																p: isMobile ? 0.5 : { xs: 0.75, sm: 1, md: 1.25 },
																borderRadius: 1,
																border: "1px solid",
																borderColor: "divider",
																bgcolor: cardSelected ? "action.hover" : "background.paper",
															}}
														>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={cardSelected}
																		onChange={() => handleToggleCard(card.id)}
																		size={isMobile ? "small" : "medium"}
																	/>
																}
																label={
																	<Typography
																		variant="body2"
																		sx={{
																			fontWeight: 500,
																			fontSize: isMobile ? "0.85rem" : { xs: "0.875rem", sm: "1rem" }
																		}}
																	>
																		{card.label}
																	</Typography>
																}
																sx={{ mb: isMobile ? 0.25 : { xs: 0.5, sm: 0.75 } }}
															/>
															{cardSelected && ENABLE_GROUPING && (
																<Box sx={{ ml: isMobile ? 1.5 : { xs: 2, sm: 3, md: 4 }, mb: isMobile ? 0.5 : { xs: 0.75, sm: 1 } }}>
																	<Tooltip
																		title={
																			selectedItems.length >= 2
																				? `Create a group from ${selectedItems.length} selected items. Groups allow you to analyze multiple items together as a single unit.`
																				: "Select at least 2 items to create a group"
																		}
																		arrow
																	>
																		<span>
																			<Button
																				variant="outlined"
																				size={isMobile ? "small" : "medium"}
																				startIcon={<GroupAddIcon sx={{ fontSize: isMobile ? "0.85rem" : { xs: "1rem", sm: "1.25rem" } }} />}
																				onClick={(event) => {
																					const selectedItemsForCard =
																						config.selectedItemsByCard?.[card.id] || [];
																					// Initialize group items selection with currently selected items
																					setGroupItemsSelection([...selectedItemsForCard]);
																					if (!selectedItemsForCard || selectedItemsForCard.length < 2) {
																						// Show error in popover instead of alert
																						setGroupPopoverCardId(card.id);
																						setGroupName("");
																						setGroupPopoverAnchor(event.currentTarget);
																						return;
																					}
																					// Open popover for group name input
																					setGroupPopoverCardId(card.id);
																					setGroupName("");
																					setGroupPopoverAnchor(event.currentTarget);
																				}}
																				disabled={selectedItems.length < 2}
																				sx={{
																					fontSize: isMobile ? "0.65rem" : { xs: "0.7rem", sm: "0.8125rem", md: "0.875rem" },
																					px: isMobile ? 1 : { xs: 1, sm: 1.5 },
																					py: isMobile ? 0.35 : { xs: 0.5, sm: 0.625 },
																					height: isMobile ? 26 : undefined,
																					textTransform: "none"
																				}}
																			>
																				{isMobile
																					? `Group (${selectedItems.length})`
																					: `Create Group ${selectedItems.length >= 2 ? `(${selectedItems.length} items)` : ""}`
																				}
																			</Button>
																		</span>
																	</Tooltip>
																</Box>
															)}
															{cardSelected && card.items && card.items.length > 0 && (
																<FormGroup
																	row
																	sx={{
																		ml: isMobile ? 1.5 : { xs: 2, sm: 3, md: 4 },
																		mt: isMobile ? 0.25 : { xs: 0.5, sm: 0.75 },
																		gap: isMobile ? 0.5 : { xs: 0.75, sm: 1, md: 1.25 },
																		flexWrap: "wrap",
																	}}
																>
																	{card.items.filter(item => !item.isReadOnly).map((item) => {
																		const itemId = getItemId(item);
																		return (
																			<FormControlLabel
																				key={itemId}
																				control={
																					<Checkbox
																						size={isMobile ? "small" : "medium"}
																						checked={selectedItems.some((id) => String(id) === String(itemId))}
																						onChange={() => handleToggleItem(card.id, itemId)}
																					/>
																				}
																				label={
																					<Typography
																						variant="body2"
																						sx={{
																							fontSize: isMobile ? "0.75rem" : { xs: "0.8rem", sm: "0.875rem", md: "1rem" },
																							whiteSpace: "nowrap"
																						}}
																					>
																						{item.name || item.label || item.title || itemId}
																					</Typography>
																				}
																				sx={{
																					mr: 0,
																					mb: isMobile ? 0.15 : { xs: 0.25, sm: 0 }
																				}}
																			/>
																		);
																	})}
																</FormGroup>
															)}
															{/* Only show groups when card is selected and ENABLE_GROUPING is true */}
															{/* Only display the box if there are groups or if we're loading groups */}
															{cardSelected && ENABLE_GROUPING && (
																loadingGroups || (config.groupsByCard?.[card.id] && Array.isArray(config.groupsByCard[card.id]) && config.groupsByCard[card.id].length > 0)
															) && (
																	<Box sx={{
																		ml: isMobile ? 1.5 : { xs: 2, sm: 3, md: 4 },
																		mt: isMobile ? 0.5 : { xs: 1, sm: 1.25, md: 1.5 },
																		p: isMobile ? 0.75 : { xs: 1, sm: 1.25, md: 1.5 },
																		bgcolor: "action.hover",
																		borderRadius: 1
																	}}>
																		{loadingGroups ? (
																			<Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1 } }}>
																				<CircularProgress size={isMobile ? 14 : 16} />
																				<Typography
																					variant="caption"
																					color="text.secondary"
																					sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
																				>
																					Loading groups...
																				</Typography>
																			</Box>
																		) : config.groupsByCard?.[card.id] && Array.isArray(config.groupsByCard[card.id]) &&
																			config.groupsByCard[card.id].length > 0 ? (
																			<>
																				<Typography
																					variant="caption"
																					sx={{
																						fontWeight: 600,
																						display: "block",
																						mb: isMobile ? 0.5 : { xs: 0.75, sm: 1 },
																						fontSize: isMobile ? "0.7rem" : { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" }
																					}}
																				>
																					Saved Groups ({config.groupsByCard[card.id].length}):
																				</Typography>
																				<FormGroup
																					sx={{
																						gap: isMobile ? 0.35 : { xs: 0.5, sm: 0.75 },
																						display: 'flex',
																						visibility: 'visible',
																						opacity: 1,
																					}}
																				>
																					{config.groupsByCard[card.id].map((g) => {
																						const selectedGroups = config.selectedGroupsByCard?.[card.id] || [];
																						const isGroupSelected = selectedGroups.includes(g.id);
																						// Always render checkbox for all groups - checkbox visibility is independent of selection state
																						// Checkbox must ALWAYS be visible whether checked or unchecked
																						return (
																							<FormControlLabel
																								key={g.id}
																								control={
																									<Checkbox
																										size={isMobile ? "small" : "medium"}
																										checked={isGroupSelected}
																										onChange={() => handleToggleGroup(card.id, g.id)}
																										sx={{
																											// Ensure checkbox is ALWAYS visible regardless of checked state
																											visibility: 'visible',
																											opacity: 1,
																											display: 'inline-flex',
																											// Explicit color styling to ensure checkbox border/outline is always visible
																											// For unchecked state, use a darker color that contrasts with light background
																											'&.Mui-checked': {
																												// Checked state - primary color
																												color: 'primary.main',
																											},
																											'&:not(.Mui-checked)': {
																												// Unchecked state - ensure it's visible with dark color
																												color: 'rgba(0, 0, 0, 0.6) !important', // Force dark color for visibility
																												// Target the SVG icon and path directly to ensure border is visible
																												'& .MuiSvgIcon-root': {
																													color: 'rgba(0, 0, 0, 0.6) !important', // Dark color for SVG
																													fontSize: { xs: '1.125rem', sm: '1.25rem' },
																												},
																												'& .MuiSvgIcon-root path': {
																													fill: 'rgba(0, 0, 0, 0.6) !important', // Dark fill for visibility
																												},
																											},
																											'&:hover:not(.Mui-checked)': {
																												// Hover state for unchecked - even darker for better visibility
																												color: 'rgba(0, 0, 0, 0.87) !important',
																												'& .MuiSvgIcon-root': {
																													color: 'rgba(0, 0, 0, 0.87) !important', // Darker color for SVG
																												},
																												'& .MuiSvgIcon-root path': {
																													fill: 'rgba(0, 0, 0, 0.87) !important', // Darker fill on hover
																												},
																											},
																											// Base SVG icon styling
																											'& .MuiSvgIcon-root': {
																												fontSize: { xs: '1.125rem', sm: '1.25rem' },
																											},
																										}}
																									/>
																								}
																								sx={{
																									// Ensure FormControlLabel is ALWAYS visible
																									display: 'flex',
																									visibility: 'visible',
																									opacity: 1,
																									margin: 0,
																									marginBottom: isMobile ? 0.15 : 0,
																									width: '100%',
																								}}
																								label={
																									<Box
																										sx={{
																											display: "inline-flex",
																											alignItems: "center",
																											gap: isMobile ? 0.15 : { xs: 0.25, sm: 0.5 },
																											padding: isMobile ? "2px 4px" : { xs: "3px 6px", sm: "4px 8px" },
																											borderRadius: 1,
																											border: "1px solid",
																											borderColor: g.hasMissingItems ? "warning.main" : isGroupSelected ? "primary.main" : "divider",
																											bgcolor: isGroupSelected ? "action.selected" : "background.paper",
																											height: isMobile ? 22 : { xs: 24, sm: 26 },
																											width: "100%",
																											maxWidth: "100%",
																										}}
																									>
																										<Tooltip
																											title={
																												g.hasMissingItems
																													? `Warning: ${g.missingItemIds?.length || 0} item(s) from this group are no longer available. Only available items will be used.`
																													: `${g.name} - ${g.itemIds?.length || 0} items`
																											}
																											arrow
																										>
																											<Box sx={{
																												display: "flex",
																												alignItems: "center",
																												gap: isMobile ? 0.15 : { xs: 0.25, sm: 0.5 },
																												flex: 1,
																												minWidth: 0,
																												overflow: "hidden"
																											}}>
																												{g.hasMissingItems && (
																													<Typography
																														variant="caption"
																														sx={{
																															fontSize: { xs: "0.65rem", sm: "0.7rem" },
																															color: "warning.main",
																															fontWeight: 600,
																															flexShrink: 0
																														}}
																													>
																														⚠
																													</Typography>
																												)}
																												<Typography
																													variant="caption"
																													sx={{
																														fontSize: isMobile ? "0.65rem" : { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" },
																														color: g.hasMissingItems ? "warning.main" : isGroupSelected ? "primary.main" : "text.primary",
																														fontWeight: isGroupSelected ? 600 : 500,
																														overflow: "hidden",
																														textOverflow: "ellipsis",
																														whiteSpace: "nowrap",
																														flex: 1,
																														minWidth: 0
																													}}
																												>
																													{g.name} ({g.itemIds?.length || 0} items{g.hasMissingItems ? `, ${g.missingItemIds?.length || 0} missing` : ""})
																												</Typography>
																											</Box>
																										</Tooltip>
																										<Tooltip title="Edit group" arrow>
																											<IconButton
																												size={isMobile ? "small" : "medium"}
																												onClick={(event) => {
																													event.stopPropagation();
																													handleEditGroup(event, card.id, g);
																												}}
																												sx={{
																													padding: { xs: "2px", sm: "4px" },
																													flexShrink: 0,
																													"&:hover": {
																														bgcolor: "action.hover",
																													},
																												}}
																											>
																												<EditIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "primary.main" }} />
																											</IconButton>
																										</Tooltip>
																										<Tooltip title="Delete group" arrow>
																											<IconButton
																												size={isMobile ? "small" : "medium"}
																												onClick={async (event) => {
																													event.stopPropagation();
																													try {
																														// Delete from API
																														await deleteTrendMappingGroup(g.id);

																														// Update local state
																														setConfig((prev) => {
																															const prevGroups = prev.groupsByCard?.[card.id] || [];
																															const updatedGroups = prevGroups.filter(gr => gr.id !== g.id);
																															const prevSelectedGroups = prev.selectedGroupsByCard?.[card.id] || [];
																															const updatedSelectedGroups = prevSelectedGroups.filter(groupId => groupId !== g.id);
																															return {
																																...prev,
																																groupsByCard: {
																																	...(prev.groupsByCard || {}),
																																	[card.id]: updatedGroups,
																																},
																																selectedGroupsByCard: {
																																	...(prev.selectedGroupsByCard || {}),
																																	[card.id]: updatedSelectedGroups,
																																},
																															};
																														});
																													} catch (error) {
																														// Error deleting group
																													}
																												}}
																												sx={{
																													padding: { xs: "2px", sm: "4px" },
																													flexShrink: 0,
																													"&:hover": {
																														bgcolor: "error.light",
																														"& .MuiSvgIcon-root": {
																															color: "error.main",
																														},
																													},
																												}}
																											>
																												<DeleteIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "primary.main" }} />
																											</IconButton>
																										</Tooltip>
																									</Box>
																								}
																							/>
																						);
																					})}
																				</FormGroup>
																				<Typography
																					variant="caption"
																					color="text.secondary"
																					sx={{
																						display: "block",
																						mt: { xs: 0.75, sm: 1 },
																						fontSize: { xs: "0.7rem", sm: "0.75rem" },
																						lineHeight: 1.4
																					}}
																				>
																					Select groups to include in analysis. Groups will be analyzed together as a single unit. Click ✏️ to edit or × to delete.
																				</Typography>
																			</>
																		) : null}
																	</Box>
																)}
														</Box>
													);
										})}
									</Box>
									</>
								);
							})()}
							</Paper>
							{isLoadingOtherDashboards ? (
								<Box
									sx={(theme) => ({
										position: "absolute",
										inset: 0,
										zIndex: 4,
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "flex-start",
										gap: 1.5,
										pt: { xs: 3, sm: 4 },
										pb: 2,
										px: 2,
										borderRadius: 2,
										bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.88 : 0.92),
										backdropFilter: "blur(5px)",
									})}
								>
									<CircularProgress size={isMobile ? 40 : 44} thickness={4} />
									<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textAlign: "center" }}>
										Loading other dashboards…
									</Typography>
									<Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", maxWidth: 280 }}>
										Card and item options will be available when data is ready.
									</Typography>
								</Box>
							) : null}
							</Box>
						</AccordionDetails>
					</Accordion>
				</Box>

				<Divider sx={{ my: 1.5 }} />

				{result && !analyzing && (
					<>
						<Divider sx={{ my: isMobile ? 1 : 2 }} />
						<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: isMobile ? 1 : 1.5, fontSize: isMobile ? "0.95rem" : undefined }}>
							Analysis Result
						</Typography>
						<Box sx={{ mb: isMobile ? 1 : 2 }}>
							<Typography variant="body2" color="text.secondary" sx={isMobile ? { fontSize: "0.75rem" } : undefined}>
								Total matching patterns:{" "}
								<Typography
									component="span"
									variant="body2"
									sx={{ fontWeight: 600, color: "primary.main", fontSize: isMobile ? "0.75rem" : undefined }}
								>
									{result.totalMatches || 0}
								</Typography>
							</Typography>
						</Box>

						{/* Search and Dashboard Filter for results */}
						{ENABLE_RESULT_SEARCH && (
							<Box sx={{
								mb: isMobile ? 1 : 2,
								display: "flex",
								flexDirection: isMobile ? "column" : { xs: "column", sm: "row" },
								gap: 1.5,
								alignItems: isMobile ? "stretch" : { xs: "stretch", sm: "center" }
							}}>
								<Box sx={{ flex: { xs: "none", sm: 1 }, maxWidth: { xs: "100%", sm: "50%" } }}>
									<TextField
										fullWidth
										size="small"
										placeholder="Search by card name or target name..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
												</InputAdornment>
											),
											endAdornment: searchTerm && (
												<InputAdornment position="end">
													<IconButton
														size="small"
														onClick={() => setSearchTerm("")}
														edge="end"
														sx={{
															padding: "4px",
															"&:hover": {
																bgcolor: "action.hover",
															},
														}}
													>
														<ClearIcon fontSize="small" sx={{ color: "text.secondary" }} />
													</IconButton>
												</InputAdornment>
											),
										}}
										sx={{
											"& .MuiOutlinedInput-root": {
												bgcolor: "background.paper",
												"&:hover": {
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: "primary.main",
													},
												},
												"&.Mui-focused": {
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: "primary.main",
														borderWidth: 2,
													},
												},
											},
											"& .MuiOutlinedInput-notchedOutline": {
												borderColor: "divider",
											},
										}}
									/>
								</Box>

								{otherDashboardIdsForCompare.length > 0 && (
									<FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
										<InputLabel id="result-dashboard-filter-label" sx={{ fontSize: "0.8125rem" }}>Filter by Dashboard</InputLabel>
										<Select
											labelId="result-dashboard-filter-label"
											value={dashboardFilter}
											label="Filter by Dashboard"
											onChange={(e) => setDashboardFilter(e.target.value)}
											sx={{
												bgcolor: "background.paper",
												fontSize: "0.8125rem",
												"& .MuiOutlinedInput-notchedOutline": {
													borderColor: "divider",
												},
												"&:hover .MuiOutlinedInput-notchedOutline": {
													borderColor: "primary.main",
												},
												"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
													borderColor: "primary.main",
												},
											}}
										>
											<MenuItem value="all">
												<em>All Dashboards</em>
											</MenuItem>
											<MenuItem value="CURRENT">{currentDashboardLabel}</MenuItem>
											{otherDashboardIdsForCompare.map((id) => {
												const label = DASHBOARD_OPTIONS.find((d) => d.id === id)?.label || id;
												return <MenuItem key={id} value={id}>{label}</MenuItem>;
											})}
										</Select>
									</FormControl>
								)}
							</Box>
						)}

						{(() => {
							const allMatches = Array.isArray(result.matchesByCard) ? result.matchesByCard : [];
							const filteredMatches = filterAnalysisResultRows(allMatches, dashboardFilter);
							const filteredCount = filteredMatches.length;

							return (
								<>
									{searchTerm.trim() && (
										<Box sx={{ mb: 1 }}>
											<Typography variant="caption" color="text.secondary">
												Showing {filteredCount} of {allMatches.length} results
											</Typography>
										</Box>
									)}
									{filteredMatches.length > 0 ? (
										<TableContainer
											component={Paper}
											variant="outlined"
											sx={{
												maxHeight: { xs: 300, sm: 400, md: 500 },
												overflow: "auto",
												"& .MuiTable-root": {
													minWidth: { xs: 600, sm: "auto" }
												}
											}}
										>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell sx={{ fontWeight: 600, p: isMobile ? "4px 8px" : undefined, fontSize: isMobile ? "0.7rem" : undefined }}>Card Name</TableCell>
														<TableCell sx={{ fontWeight: 600, p: isMobile ? "4px 8px" : undefined, fontSize: isMobile ? "0.7rem" : undefined }}>Target Name</TableCell>
														<TableCell align="right" sx={{ fontWeight: 600, p: isMobile ? "4px 8px" : undefined, fontSize: isMobile ? "0.7rem" : undefined }}>
															Match %
														</TableCell>
														<TableCell align="right" sx={{ fontWeight: 600, p: isMobile ? "4px 8px" : undefined, fontSize: isMobile ? "0.7rem" : undefined }}>
															Total Count
														</TableCell>
														<TableCell sx={{
															fontWeight: 600,
															minWidth: isMobile ? (trendDisplayMode === "percentage" ? 170 : 130) : (trendDisplayMode === "percentage" ? 260 : 180),
															width: isMobile ? (trendDisplayMode === "percentage" ? 170 : 130) : (trendDisplayMode === "percentage" ? 260 : 180),
															p: isMobile ? "4px 8px" : undefined,
															fontSize: isMobile ? "0.7rem" : undefined
														}}>
															<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																<Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? "0.7rem" : undefined }}>
																	Trend
																</Typography>
																<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
																	<Typography variant="caption" color="text.secondary" sx={isMobile ? { fontSize: "0.6rem" } : undefined}>
																		Number
																	</Typography>
																	<Switch
																		size="small"
																		checked={trendDisplayMode === "percentage"}
																		onChange={(e) => setTrendDisplayMode(e.target.checked ? "percentage" : "number")}
																		sx={{
																			"& .MuiSwitch-switchBase": {
																				padding: "4px",
																			},
																			"& .MuiSwitch-thumb": {
																				width: isMobile ? 12 : 16,
																				height: isMobile ? 12 : 16,
																			},
																			"& .MuiSwitch-track": {
																				height: isMobile ? 8 : 10,
																				borderRadius: 5,
																			},
																		}}
																	/>
																	<Typography variant="caption" color="text.secondary" sx={isMobile ? { fontSize: "0.6rem" } : undefined}>
																		%
																	</Typography>
																</Box>
															</Box>
														</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{filteredMatches.map((row, index) => {
														const matchPercent = Number(row.matchPercentage || 0);
														const isMatch = matchPercent >= (100 - (config.similarityTolerance || 10));

														return (
															<TableRow
																key={`${row.cardId}-${row.itemId}-${index}`}
																sx={{
																	bgcolor: isMatch
																		? "action.selected"
																		: matchPercent >= 50
																			? "action.hover"
																			: index % 2 === 0 ? "rgba(0, 0, 0, 0.02)" : "transparent",
																	minHeight: isMobile ? (trendDisplayMode === "percentage" ? 65 : 50) : (trendDisplayMode === "percentage" ? 95 : 70),
																	height: isMobile ? (trendDisplayMode === "percentage" ? 65 : 50) : (trendDisplayMode === "percentage" ? 95 : 70),
																	transition: "background-color 0.2s",
																	"&:hover": {
																		bgcolor: isMatch ? "action.selected" : "action.hover",
																	}
																}}
															>
																<TableCell sx={isMobile ? { p: "4px 8px" } : undefined}>
																	<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
																		<Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: isMobile ? "0.75rem" : undefined }}>
																			{row.cardLabel}
																		</Typography>
																		<Chip
																			label={row.dashboardLabel || (row.dashboardId === "CURRENT" ? currentDashboardLabel : row.dashboardId)}
																			size="small"
																			variant={row.dashboardId === "CURRENT" ? "filled" : "outlined"}
																			color={row.dashboardId === "CURRENT" ? "primary" : "default"}
																			sx={{
																				height: isMobile ? 15 : 18,
																				fontSize: isMobile ? "0.55rem" : "0.6rem",
																				fontWeight: 700,
																				alignSelf: "flex-start",
																				textTransform: "uppercase",
																				letterSpacing: "0.05em",
																				px: 0.5
																			}}
																		/>
																	</Box>
																</TableCell>
																<TableCell sx={isMobile ? { p: "4px 8px" } : undefined}>
																	<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
																		<Typography variant="body2" sx={isMobile ? { fontSize: "0.75rem" } : undefined}>{row.itemLabel}</Typography>
																		{row.isGroup && (
																			<Chip
																				label="Group"
																				size="small"
																				variant="outlined"
																				sx={{ height: isMobile ? 15 : 18, fontSize: isMobile ? "0.55rem" : "0.65rem" }}
																			/>
																		)}
																	</Box>
																</TableCell>
																<TableCell align="right" sx={isMobile ? { p: "4px 8px" } : undefined}>
																	<Typography
																		variant="body2"
																		sx={{
																			fontWeight: isMatch ? 600 : 400,
																			fontSize: isMobile ? "0.75rem" : undefined,
																			color: isMatch
																				? "success.main"
																				: matchPercent >= 50
																					? "warning.main"
																					: "text.secondary",
																		}}
																	>
																		{matchPercent.toFixed(1)}%
																	</Typography>
																</TableCell>
																<TableCell align="right" sx={isMobile ? { p: "4px 8px" } : undefined}>
																	<Typography variant="body2" sx={isMobile ? { fontSize: "0.75rem" } : undefined}>
																		{row.totalCount || 0}
																	</Typography>
																</TableCell>
																<TableCell sx={{
																	minWidth: isMobile ? (trendDisplayMode === "percentage" ? 170 : 130) : 260,
																	width: isMobile ? (trendDisplayMode === "percentage" ? 170 : 130) : 260,
																	p: isMobile ? "4px 8px" : undefined
																}}>
																	{(() => {
																		// Apply isolated date ranges filter to previewSeries for baseline calculations
																		const filteredBaselineSeries = isolatedDateRanges.length > 0
																			? filterSeriesByMultipleRanges(previewSeries, isolatedDateRanges, currentFilterType)
																			: previewSeries;

																		// Apply isolation filter to the result series for display
																		// When isolation is active, show filtered data; otherwise show full series
																		const filteredResultSeries = isolatedDateRanges.length > 0
																			? filterSeriesByMultipleRanges(row.series || [], isolatedDateRanges, currentFilterType)
																			: (row.series || []);

																		return (
																			<MiniTrendGraph
																				series={filteredResultSeries}
																				width={isMobile ? 150 : 240}
																				height={isMobile ? 45 : 70}
																				id={`${row.cardId}-${row.itemId}-${index}`}
																				displayMode={trendDisplayMode}
																				baselineSeries={filteredBaselineSeries}
																				isolatedRanges={isolatedDateRanges}
																				currentFilterType={currentFilterType}
																				onClick={() => {
																					handleOpenChartPopup(
																						filteredResultSeries,
																						`${row.cardLabel} - ${row.itemLabel}${row.isGroup ? " (Group)" : ""}`,
																						trendDisplayMode,
																						filteredBaselineSeries,
																						isolatedDateRanges
																					);
																				}}
																			/>
																		);
																	})()}
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										</TableContainer>
									) : (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												p: 4,
												bgcolor: "action.hover",
												borderRadius: 1,
											}}
										>
											<Typography variant="body2" color="text.secondary">
												{searchTerm.trim()
													? `No results found for "${searchTerm}"`
													: "No matching patterns found"}
											</Typography>
										</Box>
									)}
								</>
							);
						})()}
					</>
				)}
			</DialogContent>
			<DialogActions sx={{
				position: "relative",
				flexDirection: isMobile ? "row" : { xs: "column", sm: "row" },
				justifyContent: isMobile ? "space-between" : undefined,
				gap: isMobile ? 1 : { xs: 1, sm: 0 },
				p: isMobile ? 1 : { xs: 1.5, sm: 2 }
			}}>
				<Box sx={{
					display: "flex",
					alignItems: "center",
					gap: isMobile ? 1 : { xs: 0.5, sm: 1 },
					flex: isMobile ? "none" : 1,
					flexWrap: "wrap",
					width: isMobile ? "auto" : { xs: "100%", sm: "auto" },
					order: isMobile ? 1 : { xs: 2, sm: 1 }
				}}>
					<Button
						variant="outlined"
						size={isMobile ? "small" : "medium"}
						startIcon={savingSelection ? <CircularProgress size={16} /> : <SaveIcon sx={isMobile ? { fontSize: 16 } : undefined} />}
						onClick={handleSaveSelection}
						disabled={savingSelection || analyzing || loadingSelection || loadingGroups}
						fullWidth={isMobile ? false : isMobile}
						sx={isMobile ? {
							fontSize: "0.75rem",
							py: 0.5,
							px: 1.5,
							height: 32,
							minHeight: 32,
						} : undefined}
					>
						{savingSelection
							? savedSelection
								? "Updating..."
								: "Saving..."
							: savedSelection
								? "Update Selection"
								: "Save Selection"}
					</Button>
					{saveSuccessMessage && (
						<Alert
							severity="success"
							sx={{
								py: 0.5,
								px: isMobile ? 1 : { xs: 1, sm: 1.5 },
								fontSize: { xs: "0.7rem", sm: "0.75rem" },
								width: isMobile ? "auto" : { xs: "100%", sm: "auto" },
								"& .MuiAlert-message": {
									padding: 0,
								},
								...(isMobile ? {
									position: "absolute",
									bottom: 52,
									left: 8,
									right: 8,
									zIndex: 10,
									boxShadow: 2,
								} : {})
							}}
							onClose={() => setSaveSuccessMessage(null)}
						>
							{saveSuccessMessage}
						</Alert>
					)}
				</Box>
				<Box sx={{
					display: "flex",
					gap: 1,
					width: isMobile ? "auto" : { xs: "100%", sm: "auto" },
					order: isMobile ? 2 : { xs: 1, sm: 2 }
				}}>
					<Button
						onClick={onClose}
						disabled={analyzing}
						fullWidth={isMobile ? false : isMobile}
						size={isMobile ? "small" : "medium"}
						sx={isMobile ? {
							fontSize: "0.75rem",
							py: 0.5,
							px: 1.5,
							height: 32,
							minHeight: 32,
						} : undefined}
					>
						Close
					</Button>
					<Button
						variant="contained"
						onClick={handleAnalyze}
						disabled={analyzing || loadingSelection || loadingGroups || isLoadingOtherDashboards}
						fullWidth={isMobile ? false : isMobile}
						size={isMobile ? "small" : "medium"}
						startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : null}
						sx={{
							minWidth: { sm: 120 },
							fontWeight: 700,
							boxShadow: (theme) => theme.shadows[2],
							"&:hover": {
								boxShadow: (theme) => theme.shadows[4],
							},
							...(isMobile ? {
								fontSize: "0.75rem",
								py: 0.5,
								px: 1.5,
								height: 32,
								minHeight: 32,
							} : {})
						}}
					>
						{analyzing ? "Analyzing..." : "Analyze Trends"}
					</Button>
				</Box>
			</DialogActions>

			{/* Popover for "Save Selection as Group" */}
			<Popover
				open={Boolean(groupPopoverAnchor)}
				anchorEl={groupPopoverAnchor}
				onClose={() => {
					setGroupPopoverAnchor(null);
					setGroupPopoverCardId(null);
					setGroupName("");
					setGroupItemsSelection([]);
					setEditingGroupId(null);
					setSavingGroup(false);
				}}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				<Box sx={{ p: 2, minWidth: 300 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
						{editingGroupId ? "Edit Group" : "Save Selection as Group"}
					</Typography>
					{groupPopoverCardId && (() => {
						// Current-dashboard cards use plain card type as id; compare cards use `compare__<dashboard>__<cardType>`.
						// filterListData is only keyed by the current page — never use it alone for compare-card ids.
						const popoverCardDef = availableCards.find((c) => c.id === groupPopoverCardId);
						const filterListData = dialogFilters?.filterListData || {};
						const cardItems =
							Array.isArray(popoverCardDef?.items) && popoverCardDef.items.length > 0
								? popoverCardDef.items
								: filterListData[groupPopoverCardId] || [];

						// Get initially selected items from config
						const selectedItemsForCard =
							config.selectedItemsByCard?.[groupPopoverCardId] || [];

						// Use groupItemsSelection for the current selection in popover
						// If groupItemsSelection is empty, use the initially selected items
						const currentGroupSelection = groupItemsSelection.length > 0
							? groupItemsSelection
							: selectedItemsForCard;
						const hasEnoughItemsForGroup = currentGroupSelection.length >= 2;

						// Handler to toggle item selection in popover
						const handleToggleGroupItem = (itemId) => {
							setGroupItemsSelection((prev) => {
								const base = prev.length > 0 ? prev : selectedItemsForCard;
								const selected = new Set(base.map((id) => String(id)));
								const key = String(itemId);
								if (selected.has(key)) {
									selected.delete(key);
								} else {
									selected.add(key);
								}
								// Preserve original id types from card rows when possible
								const byStr = new Map(
									cardItems.map((it) => {
										const id = getItemId(it);
										return id != null ? [String(id), id] : null;
									}).filter(Boolean)
								);
								return Array.from(selected).map((s) => byStr.get(s) ?? s);
							});
						};

						// Check if the group being edited has missing items
						const groupBeingEdited = editingGroupId
							? config.groupsByCard?.[groupPopoverCardId]?.find(g => g.id === editingGroupId)
							: null;
						const hasMissingItems = groupBeingEdited?.hasMissingItems || false;
						const missingCount = groupBeingEdited?.missingItemIds?.length || 0;

						return (
							<>
								{hasMissingItems && (
									<Box
										sx={{
											mb: 1.5,
											p: 1,
											bgcolor: "warning.light",
											borderRadius: 1,
											border: "1px solid",
											borderColor: "warning.main",
										}}
									>
										<Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
											⚠ Warning: {missingCount} item(s) from this group are no longer available and have been removed.
										</Typography>
									</Box>
								)}
								<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
									Select items for this group (at least 2 required):
								</Typography>

								{/* Interactive item selection with checkboxes */}
								<Box
									sx={{
										mb: 2,
										p: 1.5,
										bgcolor: "action.hover",
										borderRadius: 1,
										maxHeight: 200,
										overflowY: "auto"
									}}
								>
									<FormGroup>
										{cardItems.map((item) => {
											const itemId = getItemId(item);
											const itemLabel = item.name || item.label || item.title || itemId;
											const isSelected = currentGroupSelection.some(
												(sid) => String(sid) === String(itemId)
											);
											return (
												<FormControlLabel
													key={itemId}
													control={
														<Checkbox
															size="small"
															checked={isSelected}
															onChange={() => handleToggleGroupItem(itemId)}
														/>
													}
													label={
														<Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
															{itemLabel}
														</Typography>
													}
													sx={{ mb: 0.5 }}
												/>
											);
										})}
									</FormGroup>
									{cardItems.length === 0 && (
										<Typography variant="caption" color="text.secondary">
											No items available for this card.
										</Typography>
									)}
								</Box>

								{/* Selection count indicator */}
								<Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Typography
										variant="caption"
										color={hasEnoughItemsForGroup ? "text.secondary" : "error"}
									>
										{currentGroupSelection.length} item{currentGroupSelection.length !== 1 ? 's' : ''} selected
										{!hasEnoughItemsForGroup && " (minimum 2 required)"}
									</Typography>
								</Box>

								<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									Enter a name for this group (e.g., Weekend, Top Payers):
								</Typography>
								<TextField
									fullWidth
									size="small"
									placeholder="Group name"
									value={groupName}
									onChange={(e) => setGroupName(e.target.value)}
									onKeyPress={(e) => {
										if (e.key === "Enter" && groupName.trim() && hasEnoughItemsForGroup) {
											handleSaveGroup();
										}
									}}
									autoFocus
									sx={{ mb: 2 }}
								/>
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
									<Button
										size="small"
										onClick={() => {
											setGroupPopoverAnchor(null);
											setGroupPopoverCardId(null);
											setGroupName("");
											setGroupItemsSelection([]);
											setEditingGroupId(null);
											setSavingGroup(false);
										}}
										disabled={savingGroup}
									>
										Cancel
									</Button>
									<Button
										variant="contained"
										size="small"
										onClick={handleSaveGroup}
										disabled={!groupName.trim() || !hasEnoughItemsForGroup || savingGroup}
										startIcon={savingGroup ? <CircularProgress size={16} /> : null}
									>
										{savingGroup ? (editingGroupId ? "Updating..." : "Saving...") : (editingGroupId ? "Update Group" : "Save Group")}
									</Button>
								</Box>
							</>
						);
					})()}
				</Box>
			</Popover>

			{/* Full Chart Popup Dialog */}
			<Dialog
				open={chartPopupOpen}
				onClose={() => setChartPopupOpen(false)}
				maxWidth={isMobile ? false : "lg"}
				fullWidth
				fullScreen={false}
				sx={{
					"& .MuiDialog-paper": {
						height: "auto",
						overflow: "hidden",
						...(isMobile ? {
							margin: 2,
							borderRadius: "8px",
							maxWidth: "calc(100vw - 32px)",
							maxHeight: "85vh"
						} : {
							maxHeight: "90vh",
						})
					}
				}}
			>
				<DialogTitle sx={{ pr: 6, py: isMobile ? 0.75 : 1.5, px: isMobile ? 1.5 : 3, position: "relative" }}>
					<Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 600, fontSize: isMobile ? "0.95rem" : undefined }}>
						{chartPopupTitle}
					</Typography>
					<Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ mt: 0.2, fontSize: isMobile ? "0.7rem" : undefined }}>
						All data points are displayed directly on the chart with values and dates
					</Typography>
					{isMobile && (
						<IconButton
							aria-label="close"
							onClick={() => setChartPopupOpen(false)}
							sx={{
								position: "absolute",
								right: 8,
								top: 4,
								color: (theme) => theme.palette.grey[500],
								cursor: "pointer",
								zIndex: 1,
							}}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					)}
				</DialogTitle>
				<DialogContent
					dividers
					sx={{
						maxHeight: isMobile ? "none" : "80vh",
						flexGrow: isMobile ? 1 : undefined,
						overflowY: "auto",
						p: isMobile ? 0.75 : 2,
						"&::-webkit-scrollbar": {
							width: "8px",
						},
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: "rgba(0,0,0,0.2)",
							borderRadius: "4px",
						},
					}}
				>
					{chartPopupData && (
						<Box>
							{/* Full-size chart with all data points visible - fixed width to prevent horizontal scroll */}
							<Box sx={{
								mb: 0,
								display: "flex",
								justifyContent: "center",
								width: "100%",
								overflow: "hidden",
								maxWidth: "100%"
							}}>
								<Box sx={{
									width: "100%",
									display: "flex",
									justifyContent: "center",
									overflow: "hidden"
								}}>
									<FullTrendChart
										series={chartPopupData}
										displayMode={chartPopupDisplayMode}
										baselineSeries={chartPopupBaselineSeries}
									/>
								</Box>
							</Box>
						</Box>
					)}
				</DialogContent>
				{!isMobile && (
					<DialogActions>
						<Button onClick={() => setChartPopupOpen(false)}>Close</Button>
					</DialogActions>
				)}
			</Dialog>

		</Dialog>
	);
}


